// =============================================================================
// SIREPPAR - Página de Documentos
// =============================================================================

import { useState, useRef, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Modal from '../../components/common/Modal';
import FileUpload from '../../components/common/FileUpload';
import TipoDocumentoSelect from '../../components/common/TipoDocumentoSelect';
import AgrupacionSelect from '../../components/common/AgrupacionSelect';
import CuadroNota from '../../components/common/CuadroNota';
import { archivosNuevosSinRevisar, avisoArchivoNuevo } from '../../utils/archivoNuevo';
import CheckSeccionTipo, { estadoDeSeccion, firmaDeSeccion, type FilaSeccion } from '../../components/common/CheckSeccionTipo';
import EditarDocumentoModal, { AgrupacionesConAccesoContext } from '../../components/common/EditarDocumentoModal';
import { MAX_MOTIVO_DOCUMENTO, PREVIEW_MOTIVO_DOCUMENTO, RESUMEN_MOTIVO_LINEA } from '../../utils/limites';
import TextoColapsable, { resumirEnUnaLinea } from '../../components/common/TextoColapsable';
import PopoverPortal from '../../components/common/PopoverPortal';
import { api, documentoService, partidoService, expedienteService, rendicionAnualService, rendicionCampanaService, seccionesDocumentoService } from '../../services/api';
import { tramiteIniciado, AVISO_TRAMITE_SIN_INICIAR } from '../../utils/tramiteIniciado';
import { useAuth } from '../../contexts/AuthContext';
import { sanitizarTextoLive } from '../../utils/sanitizarTexto';
import {
  TIPOS_PARTIDO,
  TIPOS_AUTORIDADES,
  TIPOS_APODERADOS,
  TIPOS_OTROS,
  TIPOS_RESOLUCIONES,
  MOTIVO_RECHAZO_TEP_SIMBOLO,
  TIPOS_DNI_PERSONA,
  TIPOS_CANDIDATURA,
  tipoDocLabels,
  DOCS_OBLIGATORIOS_BASE,
  DOCS_OBLIGATORIOS_EXTRA,
  obligatoriosPorPartido,
  DOCS_OBLIGATORIOS_RECONOCIMIENTO,
  DOCS_ALIANZA_TODOS,
  DOCS_COMPLEMENTARIOS_ALIANZA,
  GRUPOS_TIPOS_DOCUMENTO,
  TIPOS_CON_SECCION_PROPIA,
  esComplementarioAlianza,
  DOCS_OBLIGATORIOS_RECONOCIMIENTO_AFILIADOS,
  DOCS_OBLIGATORIOS_RECONOCIMIENTO_AUTORIDADES,
  TIPOS_SOLO_EXCEL,
  ACCEPT_EXCEL_ODS,
} from '../../utils/tiposDocumento';
import { tipoTramiteLabel } from '../../utils/tiposExpediente';
import { getFaseActual, alcanzoEtapaAfiliados, alcanzoEtapaAutoridades } from '../../utils/fasesReconocimiento';
import { etapaAlcanzada, pesaEnDimension, type EtapaVerificacion } from '../../utils/etapaVerificacion';
import ResumenDocumentacionTab from './ResumenDocumentacionTab';

// =============================================================================
// Tipos
// =============================================================================

type EstadoRevision = 'PENDIENTE' | 'APROBADO' | 'RECHAZADO';

interface Documento {
  id: string;
  tipo: string;
  titulo: string;
  descripcion?: string;
  nombreOriginal: string;
  mimeType: string;
  tamanioBytes: number;
  version: number;
  creadoEn: string;
  // --- Verificación en dos dimensiones ---
  estadoRevisionAdmisibilidad?: EstadoRevision;
  observacionAdmisibilidad?: string | null;
  admisibilidadRevisadoPor?: string | null;
  admisibilidadRevisadoEn?: string | null;
  estadoRevisionAnalisis?: EstadoRevision;
  observacionAnalisis?: string | null;
  analisisRevisadoPor?: string | null;
  analisisRevisadoEn?: string | null;
  observacionRespuestaId?: string | null;
  intimacionRespuestaId?: string | null;
  // Campos "efectivos" proyectados según la etapa que se está viendo. Permiten
  // que toda la UI existente siga leyendo estadoRevision/observacion/revisado*.
  estadoRevision: EstadoRevision;
  observacion?: string;
  revisadoPorNombre?: string;
  revisadoEn?: string;
  partido?: { id: string; denominacion: string; sigla: string };
  expediente?: { id: string; numero: string; tipo?: string };
  virtual?: boolean;
}

// =============================================================================
// Constantes
// =============================================================================

// DOCS_OBLIGATORIOS_BASE / _EXTRA ahora viven en utils/tiposDocumento.ts
// (fuente de verdad compartida con el Resumen de Documentación).

// =============================================================================
// Helpers
// =============================================================================

type EstadoTipo = 'verificado' | 'rechazado' | 'pendiente' | 'faltante';

function getEstadoTipo(archivos: Documento[], pendientePrioritario = true): EstadoTipo {
  if (archivos.length === 0) return 'faltante';
  // Un PENDIENTE dentro del tipo MANDA: el tipo no debe figurar "verificado" (verde)
  // si todavía hay algún documento sin revisar (p. ej. uno nuevo recién subido al
  // responder una observación). Queda "pendiente" (amarillo) hasta que todos estén
  // aprobados o rechazados. Este es el comportamiento por defecto en todo el sistema.
  if (pendientePrioritario) {
    if (archivos.some(a => a.estadoRevision === 'PENDIENTE')) return 'pendiente';
    if (archivos.some(a => a.estadoRevision === 'APROBADO')) return 'verificado';
    return 'rechazado';
  }
  if (archivos.some(a => a.estadoRevision === 'APROBADO')) return 'verificado';
  if (archivos.some(a => a.estadoRevision === 'PENDIENTE')) return 'pendiente';
  return 'rechazado';
}

function fmtDate(iso: string) {
  return iso ? new Date(iso).toLocaleDateString('es-AR') : '';
}

// =============================================================================
// Componentes utilitarios compartidos
// =============================================================================

const POPOVER_EVENT = 'sireppar:popover-open';

function usePopover() {
  const [open, setOpen] = useState(false);
  const id = useState(() => `pop-${Math.random().toString(36).slice(2)}`)[0];

  const openPopover = () => {
    window.dispatchEvent(new CustomEvent(POPOVER_EVENT, { detail: id }));
    setOpen(true);
  };

  useState(() => {
    const handler = (e: CustomEvent) => {
      if (e.detail !== id) setOpen(false);
    };
    window.addEventListener(POPOVER_EVENT, handler as EventListener);
    return () => window.removeEventListener(POPOVER_EVENT, handler as EventListener);
  });

  return { open, setOpen, openPopover };
}

const ESTADO_CONFIG: Record<EstadoRevision, { icon: React.ReactNode; bg: string; label: string }> = {
  PENDIENTE: {
    icon: <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" /></svg>,
    bg: 'bg-gray-100',
    label: 'Pendiente',
  },
  APROBADO: {
    icon: <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>,
    bg: 'bg-green-100',
    label: 'Aprobado',
  },
  RECHAZADO: {
    icon: <svg className="w-4 h-4 text-red-600" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>,
    bg: 'bg-red-100',
    label: 'Rechazado',
  },
};

const ESTADOS: EstadoRevision[] = ['PENDIENTE', 'APROBADO', 'RECHAZADO'];

// =============================================================================
// Motivo del cambio de estado — los TRES estados lo piden, no sólo el rechazo
// =============================================================================
// Antes el cuadro de texto era exclusivo del rechazo: verificar y volver a
// pendiente se ejecutaban de una y no dejaban rastro de por qué. Ahora cualquiera
// de los tres abre el mismo formulario. Sólo el rechazo exige escribir algo: en
// los otros dos el motivo es opcional (verificar es el caso normal y no hay que
// frenar el trabajo) y, si se deja vacío, el motivo anterior queda limpio.
//
// El hook y el formulario viven acá porque las cinco secciones de la página
// (obligatorios, agrupada por tipo, lista, símbolos y los modales de DNI) tenían
// el mismo formulario copiado: un cambio en la regla se hacía cinco veces.

const MOTIVO_ACCION: Record<EstadoRevision, {
  titulo: string;
  placeholder: string;
  botonConfirmar: string;
  obligatorio: boolean;
  panel: string;
  input: string;
  botonClase: string;
  tituloColor: string;
}> = {
  APROBADO: {
    titulo: 'Verificar — motivo (opcional)',
    placeholder: 'Con qué criterio se aprueba, si hace falta dejarlo dicho…',
    botonConfirmar: 'Confirmar verificación',
    obligatorio: false,
    panel: 'bg-green-50 border-green-200',
    input: 'border-green-200 focus:border-green-400',
    botonClase: 'bg-green-600 hover:bg-green-700',
    tituloColor: 'text-green-700',
  },
  RECHAZADO: {
    titulo: 'Rechazar — motivo (obligatorio)',
    placeholder: 'Motivo del rechazo…',
    botonConfirmar: 'Confirmar rechazo',
    obligatorio: true,
    panel: 'bg-red-50 border-red-200',
    input: 'border-red-200 focus:border-red-400',
    botonClase: 'bg-red-600 hover:bg-red-700',
    tituloColor: 'text-red-700',
  },
  PENDIENTE: {
    titulo: 'Volver a pendiente — motivo (opcional)',
    placeholder: 'Por qué se reabre la revisión de este archivo…',
    botonConfirmar: 'Pasar a pendiente',
    obligatorio: false,
    panel: 'bg-amber-50 border-amber-200',
    input: 'border-amber-200 focus:border-amber-400',
    botonClase: 'bg-amber-600 hover:bg-amber-700',
    tituloColor: 'text-amber-700',
  },
};

interface MotivoForm {
  docId: string | null;
  estado: EstadoRevision;
  motivo: string;
  setMotivo: (v: string) => void;
  abrir: (docId: string, estado: EstadoRevision, motivoActual?: string | null) => void;
  cerrar: () => void;
  confirmar: () => void;
  puedeConfirmar: boolean;
  abiertoEn: (docId: string) => boolean;
}

function useMotivoForm(
  onSetEstado: (docId: string, estado: EstadoRevision, observacion?: string | null) => void,
): MotivoForm {
  const [docId, setDocId] = useState<string | null>(null);
  const [estado, setEstado] = useState<EstadoRevision>('RECHAZADO');
  const [motivo, setMotivo] = useState('');

  const cerrar = () => { setDocId(null); setMotivo(''); };

  const abrir = (id: string, nuevoEstado: EstadoRevision, motivoActual?: string | null) => {
    setDocId(id);
    setEstado(nuevoEstado);
    // Al rechazar se precarga el motivo anterior (suele ser el mismo reproche);
    // al verificar o reabrir se arranca en blanco, que el motivo viejo ya no aplica.
    setMotivo(nuevoEstado === 'RECHAZADO' ? (motivoActual || '') : '');
  };

  const texto = motivo.trim();
  const puedeConfirmar = !MOTIVO_ACCION[estado].obligatorio || texto.length > 0;

  const confirmar = () => {
    if (!docId || !puedeConfirmar) return;
    // Sin texto se manda null y el backend limpia el motivo anterior.
    onSetEstado(docId, estado, texto || null);
    cerrar();
  };

  return {
    docId, estado, motivo, setMotivo, abrir, cerrar, confirmar, puedeConfirmar,
    abiertoEn: (id: string) => docId === id,
  };
}

// El Excel del listado de adherentes NO se decide a mano: handleSetEstado lo
// desvía al control de adherentes, que resuelve APROBADO/RECHAZADO según el
// mínimo legal y DESCARTA cualquier observación. Pedir un motivo ahí sería pedir
// un texto que se tira, así que ese tipo se aplica directo, sin formulario.
function aplicarConMotivo(
  form: MotivoForm,
  onSetEstado: (docId: string, estado: EstadoRevision, observacion?: string | null) => void,
  doc: { id: string; tipo: string; observacion?: string },
  estado: EstadoRevision,
) {
  if (doc.tipo === 'LISTADO_ADHERENTES_EXCEL') {
    onSetEstado(doc.id, estado);
    return;
  }
  form.abrir(doc.id, estado, doc.observacion);
}

// Formulario inline del motivo. Se renderiza debajo de la fila del archivo.
function MotivoFormInline({ form }: { form: MotivoForm }) {
  const cfg = MOTIVO_ACCION[form.estado];
  return (
    <div className={`mt-1.5 p-3 border rounded-lg ${cfg.panel}`}>
      <p className={`text-[11px] font-semibold uppercase tracking-wide mb-1.5 ${cfg.tituloColor}`}>{cfg.titulo}</p>
      <textarea
        value={form.motivo}
        onChange={e => form.setMotivo(e.target.value)}
        placeholder={cfg.placeholder}
        autoFocus
        // El motivo es el texto que se le transcribe al partido: cuatro filas para
        // que se pueda detallar sin escribir a ciegas en un campo de dos.
        rows={4}
        maxLength={MAX_MOTIVO_DOCUMENTO}
        onKeyDown={e => {
          if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) form.confirmar();
          if (e.key === 'Escape') form.cerrar();
        }}
        className={`w-full border rounded-md px-3 py-2 text-xs mb-2 resize-y focus:outline-none bg-white ${cfg.input}`}
      />
      <div className="flex gap-2 justify-end items-center">
        {!cfg.obligatorio && (
          <span className="mr-auto text-[11px] text-gray-500">Se puede confirmar sin escribir nada.</span>
        )}
        <button
          onClick={form.cerrar}
          className="text-xs border border-gray-200 rounded-md px-3 py-1.5 bg-white hover:bg-gray-50"
        >
          Cancelar
        </button>
        <button
          onClick={form.confirmar}
          disabled={!form.puedeConfirmar}
          className={`text-xs text-white rounded-md px-3 py-1.5 disabled:opacity-40 disabled:cursor-not-allowed ${cfg.botonClase}`}
        >
          {cfg.botonConfirmar}
        </button>
      </div>
    </div>
  );
}

// Etiqueta del estado de un DNI en el encabezado del modal.
function estadoDniLabel(estado?: EstadoRevision): string {
  if (estado === 'APROBADO') return 'Verificado';
  if (estado === 'RECHAZADO') return 'Rechazado';
  return 'Pendiente';
}

// Campo de motivo dentro de los modales de DNI. Mismo contrato que
// MotivoFormInline, pero sin botones: el modal ya tiene su barra de acciones.
function MotivoModalCampo({ accion, texto, setTexto, onConfirmar, onCancelar }: {
  accion: EstadoRevision;
  texto: string;
  setTexto: (v: string) => void;
  onConfirmar: () => void;
  onCancelar: () => void;
}) {
  const cfg = MOTIVO_ACCION[accion];
  return (
    <div>
      <p className={`text-[11px] font-semibold uppercase tracking-wide mb-1 ${cfg.tituloColor}`}>{cfg.titulo}</p>
      <textarea
        value={texto}
        onChange={e => setTexto(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) onConfirmar();
          if (e.key === 'Escape') onCancelar();
        }}
        placeholder={cfg.placeholder}
        autoFocus
        rows={4}
        maxLength={MAX_MOTIVO_DOCUMENTO}
        className={`w-full border rounded-lg px-3 py-2 text-sm resize-y focus:outline-none ${cfg.input}`}
      />
    </div>
  );
}

// Qué acción dejó ese revisor y esa fecha en el documento.
function accionRevisionLabel(estado: EstadoRevision): string {
  if (estado === 'APROBADO') return 'Verificado';
  if (estado === 'RECHAZADO') return 'Rechazado';
  return 'Devuelto a pendiente';
}

// Línea "Motivo: …" del detalle de un archivo. Vale para los tres estados: un
// documento verificado o reabierto también puede tener motivo escrito.
function MotivoDoc({ doc }: { doc: { estadoRevision: EstadoRevision; observacion?: string } }) {
  if (!doc.observacion) return null;
  const color = doc.estadoRevision === 'RECHAZADO' ? 'text-red-500'
    : doc.estadoRevision === 'APROBADO' ? 'text-green-600' : 'text-amber-600';
  return (
    // whitespace-pre-wrap: el motivo largo suele venir en párrafos y hasta acá se
    // pintaba todo corrido, en un solo bloque ilegible.
    <p className="text-xs mt-0.5 whitespace-pre-wrap break-words">
      <span className="text-gray-400">Motivo: </span>
      <TextoColapsable texto={doc.observacion} tope={PREVIEW_MOTIVO_DOCUMENTO} className={color} />
    </p>
  );
}

// Badge de estado con popover para cambiar — usado en la tabla de otras secciones
function EstadoBadge({ estado, canEdit, onSelect }: {
  estado: EstadoRevision;
  canEdit: boolean;
  onSelect: (e: EstadoRevision) => void;
}) {
  const { open, setOpen, openPopover } = usePopover();
  const anchorRef = useRef<HTMLSpanElement>(null);
  const safeEstado: EstadoRevision = ESTADOS.includes(estado) ? estado : 'PENDIENTE';
  const config = ESTADO_CONFIG[safeEstado];
  const opciones = ESTADOS.filter(e => e !== safeEstado);

  if (!canEdit) {
    return (
      <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full ${config.bg}`}>
        {config.icon}
      </span>
    );
  }

  return (
    <span ref={anchorRef} className="relative inline-block">
      <button
        onClick={() => open ? setOpen(false) : openPopover()}
        title={config.label}
        className={`inline-flex items-center justify-center w-7 h-7 rounded-full ${config.bg} hover:opacity-75 transition-opacity cursor-pointer`}
      >
        {config.icon}
      </button>
      {/* En portal: las tarjetas de sección tienen overflow-hidden y recortaban
          este menú cuando el archivo era el último de la sección. */}
      <PopoverPortal anchorRef={anchorRef} open={open} onClose={() => setOpen(false)} maxHeight={160} minWidth={150}>
        <div className="py-1">
          {opciones.map(e => (
            <button
              key={e}
              onClick={() => { onSelect(e); setOpen(false); }}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-gray-50 text-left"
            >
              <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full ${ESTADO_CONFIG[e].bg}`}>
                {ESTADO_CONFIG[e].icon}
              </span>
              {ESTADO_CONFIG[e].label}
            </button>
          ))}
        </div>
      </PopoverPortal>
    </span>
  );
}

// Etiqueta de un partido en el filtro. El desplegable NATIVO se dimensiona según
// la opción más larga, así que un nombre como "PARTIDO UNIÓN VECINAL PARA LA
// VICTORIA DE EL MAITÉN" estiraba la lista hasta salirse de la pantalla. La sigla
// es lo que identifica al partido de un vistazo, así que se conserva entera y se
// recorta la denominación.
const LARGO_MAX_OPCION_PARTIDO = 42;

function etiquetaPartidoFiltro(p: { sigla?: string | null; denominacion: string }): string {
  const completa = p.sigla ? `${p.sigla} - ${p.denominacion}` : p.denominacion;
  return completa.length <= LARGO_MAX_OPCION_PARTIDO
    ? completa
    : `${completa.slice(0, LARGO_MAX_OPCION_PARTIDO - 1)}…`;
}

function TituloCell({ titulo }: { titulo: string }) {
  const MAX = 40;
  if (titulo.length <= MAX) return <span className="text-sm font-medium text-gray-900">{titulo}</span>;
  return <span className="text-sm font-medium text-gray-900 cursor-default" title={titulo}>{titulo.slice(0, MAX)}…</span>;
}

function DescripcionCell({ descripcion }: { descripcion?: string }) {
  const { open: pinned, setOpen: setPinned, openPopover } = usePopover();
  const texto = descripcion || 'Sin descripción';
  return (
    <span className="relative inline-block whitespace-nowrap">
      <span className="text-xs text-primary-600 cursor-pointer select-none" title={texto}
        onClick={() => pinned ? setPinned(false) : openPopover()}>
        Ver descripción
      </span>
      {pinned && (
        <span className="absolute z-10 left-0 top-5 w-56 bg-gray-800 text-white text-xs rounded shadow-lg px-3 py-2 whitespace-pre-wrap break-words">
          {texto}
          <button onClick={() => setPinned(false)} className="ml-2 text-gray-400 hover:text-white font-bold" title="Cerrar">✕</button>
        </span>
      )}
    </span>
  );
}

function SeccionBadges({ docs }: { docs: Documento[] }) {
  const aprobados = docs.filter(d => d.estadoRevision === 'APROBADO').length;
  const rechazados = docs.filter(d => d.estadoRevision === 'RECHAZADO').length;
  const pendientes = docs.filter(d => d.estadoRevision === 'PENDIENTE').length;
  return (
    <div className="flex items-center gap-1.5">
      {aprobados > 0 && (
        <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-50 px-1.5 py-0.5 rounded-full font-medium">
          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
          {aprobados}
        </span>
      )}
      {rechazados > 0 && (
        <span className="inline-flex items-center gap-1 text-xs text-red-700 bg-red-50 px-1.5 py-0.5 rounded-full font-medium">
          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
          {rechazados}
        </span>
      )}
      {pendientes > 0 && (
        <span className="inline-flex items-center gap-1 text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded-full font-medium">
          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" /></svg>
          {pendientes}
        </span>
      )}
      {docs.length === 0 && <span className="text-xs text-gray-400">Sin documentos</span>}
    </div>
  );
}

// =============================================================================
// SeccionLista — secciones genéricas con estilo checklist
// =============================================================================

function estadoToTipo(estado: EstadoRevision, isVirtual?: boolean): EstadoTipo {
  if (isVirtual) return 'faltante';
  if (estado === 'APROBADO') return 'verificado';
  if (estado === 'RECHAZADO') return 'rechazado';
  return 'pendiente';
}

interface SeccionListaProps {
  // Dimensión que se está mostrando (para corregir el motivo correcto).
  etapa?: 'ADMISIBILIDAD' | 'ANALISIS';
  titulo: string;
  docs: Documento[];
  canEdit: boolean;
  isInterno: boolean;
  onSetEstado: (docId: string, estado: EstadoRevision, observacion?: string | null) => void;
  markedDocIds?: Set<string>;
  // Si es true, cada fila muestra el título real del documento (que incluye la
  // etapa y el N°), en vez de la etiqueta genérica del tipo. Útil para
  // "Resoluciones", donde conviven varias etapas/versiones del mismo tipo.
  mostrarTitulo?: boolean;
}

function SeccionLista({ titulo, docs, canEdit, onSetEstado, markedDocIds, mostrarTitulo, etapa }: SeccionListaProps) {
  const hasMarkedDocInside = !!markedDocIds && docs.some(d => markedDocIds.has(d.id));
  const [open, setOpen] = useState(hasMarkedDocInside);
  // Si llega un marked doc nuevo después del primer render, también abrir
  useEffect(() => {
    if (hasMarkedDocInside) setOpen(true);
  }, [hasMarkedDocInside]);
  const [expandedDocId, setExpandedDocId] = useState<string | null>(null);
  const motivoForm = useMotivoForm(onSetEstado);

  // Numeración para tipos repetidos
  const tipoCounts: Record<string, number> = {};
  docs.forEach(d => { tipoCounts[d.tipo] = (tipoCounts[d.tipo] || 0) + 1; });
  const tipoIdx: Record<string, number> = {};

  const realDocs = docs.filter(d => !d.virtual);
  const aprobadosCount = realDocs.filter(d => d.estadoRevision !== 'PENDIENTE').length;
  const totalCount = realDocs.length;

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      {/* Header colapsable */}
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 text-left"
      >
        <div className="flex items-center gap-3">
          <span className="text-sm font-semibold text-gray-700 uppercase tracking-wide">{titulo}</span>
        </div>
        <div className="flex items-center gap-3">
          {totalCount > 0 && (
            <span className="text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap bg-gray-100 text-gray-600 border border-gray-200">
              {aprobadosCount} / {totalCount} revisados
            </span>
          )}
          <svg className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
            fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {open && (
        <div className="border-t border-gray-200">
          {docs.length === 0 ? (
            <p className="px-6 py-4 text-sm text-gray-400">No hay documentos en esta sección.</p>
          ) : (
            docs.map(doc => {
              tipoIdx[doc.tipo] = (tipoIdx[doc.tipo] || 0) + 1;
              const idx = tipoIdx[doc.tipo];
              const label = tipoDocLabels[doc.tipo] || doc.tipo;
              const displayLabel = mostrarTitulo && doc.titulo
                ? doc.titulo
                : (tipoCounts[doc.tipo] > 1 ? `${label} #${idx}` : label);
              const status = estadoToTipo(doc.estadoRevision, doc.virtual);
              const isMarked = !!markedDocIds?.has(doc.id);
              const isDocOpen = expandedDocId === doc.id || isMarked;

              let sub: React.ReactNode;
              if (doc.virtual) {
                sub = <span className="text-gray-300">No presentado</span>;
              } else if (status === 'verificado') {
                sub = (
                  <span className="text-gray-400">
                    Verificado por {doc.revisadoPorNombre || '—'}
                    {doc.revisadoEn && <> · {fmtDate(doc.revisadoEn)}</>}
                  </span>
                );
              } else if (status === 'rechazado') {
                sub = (
                  <span>
                    <span className="text-red-600">Rechazado</span>
                    {doc.revisadoPorNombre && (
                      <span className="text-gray-400"> por {doc.revisadoPorNombre}{doc.revisadoEn && ` · ${fmtDate(doc.revisadoEn)}`}</span>
                    )}
                    {doc.observacion && (
                      <span className="text-gray-400"> · <span className="text-red-500">Motivo: {resumirEnUnaLinea(doc.observacion, RESUMEN_MOTIVO_LINEA)}</span></span>
                    )}
                  </span>
                );
              } else {
                sub = <span className="text-amber-600">Cargado · pendiente de revisión</span>;
              }

              return (
                <div key={doc.id} data-doc-id={doc.id}>
                  {/* Fila */}
                  <div
                    className={`flex items-center px-6 py-3 gap-3 border-b border-gray-100 transition-colors
                      ${doc.virtual ? 'opacity-60' : 'cursor-pointer'}
                      ${isMarked ? 'bg-yellow-50 hover:bg-yellow-100 border-l-4 border-l-yellow-400'
                        : status === 'rechazado' ? 'bg-red-50 hover:bg-red-100'
                        : (!doc.virtual ? 'hover:bg-gray-50' : '')}`}
                    onClick={() => !doc.virtual && setExpandedDocId(isDocOpen ? null : doc.id)}
                  >
                    <TipoIcon status={status} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800">
                        {displayLabel}
                        {isMarked && (
                          <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded bg-yellow-200 text-yellow-900 text-[10px] font-semibold uppercase tracking-wide">
                            Adjuntó a respuesta
                          </span>
                        )}
                      </p>
                      <p className="text-xs mt-0.5 leading-relaxed">{sub}</p>
                    </div>
                    {doc.creadoEn && (
                      <span className="text-xs text-gray-400 whitespace-nowrap">{fmtDate(doc.creadoEn)}</span>
                    )}
                    {!doc.virtual && (
                      <svg
                        className={`w-3 h-3 text-gray-400 flex-shrink-0 transition-transform ${isDocOpen ? 'rotate-90' : ''}`}
                        fill="currentColor" viewBox="0 0 20 20"
                      >
                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>

                  {/* Detalle expandido */}
                  {isDocOpen && !doc.virtual && (
                    <div className="bg-gray-50 border-b border-gray-200 px-6 py-3 pl-[72px]">
                      <div>
                        <div className="flex items-center gap-3 px-3 py-2.5 bg-white border border-gray-200 rounded-lg">
                          <span className="text-lg flex-shrink-0">📄</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-gray-800 truncate">{doc.titulo || doc.nombreOriginal}</p>
                            <p className="text-xs text-gray-400 mt-0.5">
                              Cargado {fmtDate(doc.creadoEn)}
                              {doc.nombreOriginal && doc.nombreOriginal !== doc.titulo && <> · Archivo: {doc.nombreOriginal}</>}
                              {doc.expediente && (
                                <> · <Link to={`/expedientes/${doc.expediente.id}`} className="text-primary-600 hover:underline" onClick={e => e.stopPropagation()}>{doc.expediente.numero}{doc.expediente.tipo ? ` — ${tipoTramiteLabel(doc.expediente.tipo)}` : ''}</Link></>
                              )}
                              {doc.descripcion && <> · {doc.descripcion}</>}
                            </p>
                            <MotivoDoc doc={doc} />
                            {doc.revisadoPorNombre && (
                              <p className="text-xs text-gray-400 mt-0.5">
                                {accionRevisionLabel(doc.estadoRevision)} por {doc.revisadoPorNombre}
                                {doc.revisadoEn && <> · {fmtDate(doc.revisadoEn)}</>}
                              </p>
                            )}
                          </div>

                          {/* Badge desplegable: también en PENDIENTE. Antes se ocultaba
                              en ese estado porque lo cubrían los botones de abajo. */}
                          {canEdit && (
                            <ChecklistEstadoBadge
                              estado={doc.estadoRevision}
                              canEdit={canEdit}
                              onSelect={(newState) => {
                                aplicarConMotivo(motivoForm, onSetEstado, doc, newState);
                              }}
                            />
                          )}

                          <EditarDocumentoModal doc={{ id: doc.id, tipo: doc.tipo, titulo: doc.titulo, partidoId: doc.partido?.id, virtual: doc.virtual, estadoRevision: doc.estadoRevision, observacion: doc.observacion }} etapa={etapa} />

                          {/* Ver archivo — siempre */}
                          <button
                            onClick={(e) => { e.stopPropagation(); documentoService.download(doc.id); }}
                            className="text-xs text-blue-600 hover:underline flex-shrink-0"
                          >
                            Ver archivo
                          </button>

                          {/* Sin botones Verificar/Rechazar: el estado se cambia pulsando
                              el badge, que abre el desplegable con las tres opciones. */}
                        </div>

                        {/* Formulario del motivo (verificar / rechazar / volver a pendiente) */}
                        {motivoForm.abiertoEn(doc.id) && <MotivoFormInline form={motivoForm} />}
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// ZoomableImage — previsualización de imagen con zoom y paneo
// =============================================================================

function ZoomableImage({ src, alt }: { src: string; alt: string }) {
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null);

  useEffect(() => {
    if (zoom <= 1) setOffset({ x: 0, y: 0 });
  }, [zoom]);

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    setZoom(z => Math.min(4, Math.max(1, z - e.deltaY * 0.0015)));
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (zoom <= 1) return;
    setDragging(true);
    dragStart.current = { x: e.clientX, y: e.clientY, ox: offset.x, oy: offset.y };
    e.preventDefault();
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!dragging || !dragStart.current) return;
    setOffset({
      x: dragStart.current.ox + e.clientX - dragStart.current.x,
      y: dragStart.current.oy + e.clientY - dragStart.current.y,
    });
  };

  const handleMouseUp = () => {
    setDragging(false);
    dragStart.current = null;
  };

  const changeZoom = (delta: number) => {
    setZoom(z => Math.min(4, Math.max(1, z + delta)));
  };

  return (
    <div
      className="relative w-full h-full overflow-hidden select-none"
      onWheel={handleWheel}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      style={{ cursor: zoom > 1 ? (dragging ? 'grabbing' : 'grab') : 'default' }}
    >
      <img
        src={src}
        alt={alt}
        className="absolute rounded"
        style={{
          top: '50%', left: '50%',
          transform: `translate(calc(-50% + ${offset.x}px), calc(-50% + ${offset.y}px)) scale(${zoom})`,
          transformOrigin: 'center',
          transition: dragging ? 'none' : 'transform 0.1s ease',
          maxWidth: zoom <= 1 ? '100%' : 'none',
          maxHeight: zoom <= 1 ? '100%' : 'none',
        }}
        draggable={false}
      />
      {/* Controles de zoom */}
      <div className="absolute bottom-2 right-2 flex items-center gap-1 bg-white bg-opacity-90 rounded-lg shadow-md px-2 py-1 border border-gray-200">
        <button
          type="button"
          onClick={() => changeZoom(-0.25)}
          disabled={zoom <= 1}
          className="w-6 h-6 flex items-center justify-center text-gray-700 hover:text-gray-900 font-bold text-base disabled:opacity-30"
        >−</button>
        <span className="text-xs text-gray-500 w-9 text-center tabular-nums">{Math.round(zoom * 100)}%</span>
        <button
          type="button"
          onClick={() => changeZoom(0.25)}
          disabled={zoom >= 4}
          className="w-6 h-6 flex items-center justify-center text-gray-700 hover:text-gray-900 font-bold text-base disabled:opacity-30"
        >+</button>
        {zoom > 1 && (
          <button
            type="button"
            onClick={() => { setZoom(1); setOffset({ x: 0, y: 0 }); }}
            className="ml-1 text-xs text-blue-600 hover:underline"
          >Reset</button>
        )}
      </div>
      {zoom <= 1 && (
        <div className="absolute bottom-2 left-2 text-xs text-gray-400 bg-white bg-opacity-80 rounded px-1.5 py-0.5">
          Scroll para zoom
        </div>
      )}
    </div>
  );
}

// =============================================================================
// SeccionAtributosWizard — visores de los archivos del wizard de RECONOCIMIENTO
// Símbolos/colores/descripción: solo lectura (competencia TEP).
// DNI del iniciador: Documento revisable en Admisibilidad (competencia SEP).
// =============================================================================
function BotonVerArchivo({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
    >
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
        <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
        <circle cx="12" cy="12" r="3" />
      </svg>
      {label}
    </button>
  );
}

// SeccionSimbolos — "Emblemas y símbolos": archivos SIMBOLO evaluables + colores
// declarados (solo lectura). Colapsable con estado del grupo. Reutilizable en
// reconocimiento y reempadronamiento.
function SeccionSimbolos({ expedienteId, simbolosDocs, colores, descripcion, simbolosPaths, onSetEstado, canRevisar, defaultOpen, etapa, seccion, onAprobarTipo, onGuardarNota }: {
  expedienteId?: string;
  simbolosDocs?: Documento[];
  colores?: string[];
  descripcion?: string;
  // Fallback: paths en datosFormulario cuando aún no hay Documento (visores).
  simbolosPaths?: string[];
  onSetEstado: (docId: string, estado: EstadoRevision, observacion?: string | null) => void;
  canRevisar: boolean;
  defaultOpen?: boolean;
  etapa?: 'ADMISIBILIDAD' | 'ANALISIS';
  // Aprobación POR TIPO, igual que en los documentos obligatorios: cuando el
  // expediente tiene sección para SIMBOLO, el check manda sobre la cuenta de
  // archivos. Sin sección, se comporta como siempre.
  seccion?: SeccionTipo;
  onAprobarTipo?: (tipo: string, estado: EstadoRevision, nota?: string) => void;
  onGuardarNota?: (tipo: string, nota: string) => void;
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen ?? true);
  const motivoForm = useMotivoForm(onSetEstado);

  const ver = async (ruta: string) => {
    try {
      const r = await api.get(ruta, { responseType: 'blob' });
      window.open(URL.createObjectURL(r.data as Blob), '_blank');
    } catch {
      toast.error('No se pudo abrir el archivo');
    }
  };

  const simbolos = simbolosPaths ?? [];
  const coloresArr = colores ?? [];
  // Único tipo de documento de esta sección: SIMBOLO. Se muestra con el MISMO
  // diseño que un documento obligatorio: una fila de tipo colapsable que adentro
  // lista cada archivo con su estado. La aclaración del símbolo va ARRIBA del
  // tipo y los colores declarados van DEBAJO.
  const files = [...(simbolosDocs ?? [])].sort((a, b) => new Date(a.creadoEn).getTime() - new Date(b.creadoEn).getTime());
  const hayAlgo = files.length > 0 || simbolos.length > 0 || coloresArr.length > 0 || !!descripcion;
  if (!hayAlgo) return null;

  const dimVista: 'ADMISIBILIDAD' | 'ANALISIS' = etapa === 'ADMISIBILIDAD' ? 'ADMISIBILIDAD' : 'ANALISIS';
  const dimSec = seccion ? (dimVista === 'ADMISIBILIDAD' ? seccion.admisibilidad : seccion.analisis) : null;
  const archivosSinRevisar = files.filter((d) => d.estadoRevision === 'PENDIENTE').length;
  const statusTipo: EstadoTipo = dimSec
    ? (dimSec.estado === 'APROBADO' ? 'verificado'
      : dimSec.estado === 'RECHAZADO' ? 'rechazado'
      : files.length > 0 ? 'pendiente' : 'faltante')
    : getEstadoTipo(files);
  // Contador por TIPO (no por archivo): hay un solo tipo de documento (SÍMBOLO).
  // Está "verificado" si al menos un archivo del tipo fue aprobado → 1/1; si no,
  // 0/1. Igual criterio que la sección de documentos obligatorios.
  const total = 1;
  const verificados = (statusTipo === 'verificado' || statusTipo === 'rechazado') ? 1 : 0;
  const avisoNuevosSimbolos = avisoArchivoNuevo(archivosNuevosSinRevisar(
    dimSec?.revisadoEn,
    files.map((d) => ({ creadoEn: d.creadoEn, estado: d.estadoRevision })),
  ));
  const lastRejected = [...files].filter(f => f.estadoRevision === 'RECHAZADO').slice(-1)[0];
  let subTipo: React.ReactNode;
  if (dimSec) {
    const cuenta = `${files.length} ${files.length === 1 ? 'archivo' : 'archivos'}`;
    if (dimSec.estado === 'APROBADO') {
      subTipo = (
        <span className="text-gray-400">
          Sección aprobada por {dimSec.revisadoPorNombre || '—'}
          {dimSec.revisadoEn && <> · {fmtDate(dimSec.revisadoEn)}</>} · {cuenta}
        </span>
      );
    } else if (dimSec.estado === 'RECHAZADO') {
      subTipo = (
        <span>
          <span className="text-red-600">Sección rechazada</span>
          {seccion?.nota && <span className="text-gray-400"> · <span className="text-red-500">Nota: {seccion.nota}</span></span>}
          <span className="text-gray-400"> · {cuenta}</span>
        </span>
      );
    } else {
      subTipo = (
        <span className={files.length === 0 ? 'text-gray-300' : 'text-amber-600'}>
          {files.length === 0 ? 'Sin archivos · sección sin aprobar' : `${cuenta}${archivosSinRevisar ? ` · ${archivosSinRevisar} sin revisar` : ''} · sección sin aprobar`}
        </span>
      );
    }
  } else if (statusTipo === 'verificado') {
    const approvedFile = files.find(f => f.estadoRevision === 'APROBADO');
    subTipo = <span className="text-gray-400">Verificado por {approvedFile?.revisadoPorNombre || '—'}{approvedFile?.revisadoEn && <> · {fmtDate(approvedFile.revisadoEn)}</>}</span>;
  } else if (statusTipo === 'rechazado') {
    subTipo = <span><span className="text-red-600">Rechazado</span>{lastRejected?.observacion && <span className="text-gray-400"> · <span className="text-red-500">Motivo: {resumirEnUnaLinea(lastRejected.observacion, RESUMEN_MOTIVO_LINEA)}</span></span>}</span>;
  } else if (statusTipo === 'pendiente') {
    subTipo = <span className="text-amber-600">Cargado · pendiente de revisión</span>;
  } else {
    subTipo = <span className="text-gray-300">No cargado</span>;
  }

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden" data-tipo-doc="SIMBOLO">
      {/* Encabezado = LA SECCIÓN. Antes había dos niveles: la tarjeta "Emblemas y
          símbolos" y, adentro, una fila "Símbolo Partidario" que llevaba el check.
          Con un solo tipo de documento los dos niveles son la misma cosa, así que el
          check quedaba un escalón más adentro que en las demás secciones. Es un div
          y no un button porque adentro va la casilla (interactivo anidado). */}
      <div
        onClick={() => setIsOpen(o => !o)}
        className={`w-full px-6 py-4 flex items-center gap-3 text-left cursor-pointer transition-colors ${statusTipo === 'rechazado' ? 'bg-red-50 hover:bg-red-100' : 'hover:bg-gray-50'}`}
      >
        {/* Mismo check que los demás tipos: es el validado definitivo de la
            sección, no lo decide la cuenta de archivos. */}
        {dimSec && canRevisar && onAprobarTipo ? (
          <input
            type="checkbox"
            checked={dimSec.estado === 'APROBADO'}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => {
              const aprobar = e.target.checked;
              // No se aprueba con archivos sin revisar: el check declara un
              // juicio y no se puede juzgar lo que nadie abrió.
              if (aprobar && archivosSinRevisar > 0) {
                toast.error(
                  `No es posible aprobar: quedan ${archivosSinRevisar} ` +
                  `${archivosSinRevisar === 1 ? 'documento pendiente' : 'documentos pendientes'} dentro.`,
                );
                e.target.checked = false;
                return;
              }
              onAprobarTipo('SIMBOLO', aprobar ? 'APROBADO' : 'PENDIENTE');
            }}
            title={dimSec.estado === 'APROBADO'
              ? 'Desmarcar: la sección vuelve a pendiente'
              : 'Aprobar la sección completa'}
            className="w-5 h-5 flex-shrink-0 rounded border-gray-300 accent-green-600 text-green-600 focus:ring-green-500 cursor-pointer"
          />
        ) : (
          <TipoIcon status={statusTipo} />
        )}
        <div className="flex-1 min-w-0">
          <span className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Emblemas y símbolos</span>
          <p className="text-xs mt-0.5 leading-relaxed">{subTipo}</p>
          {avisoNuevosSimbolos && (
            <p className="mt-1 inline-flex items-center gap-1.5 rounded-md bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800">
              <span aria-hidden>●</span> {avisoNuevosSimbolos}
            </p>
          )}
        </div>
        {total > 0 && (
          <span className="text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap bg-gray-100 text-gray-600 border border-gray-200">
            {verificados} / {total} revisados
          </span>
        )}
        <svg className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {isOpen && (
        <div className="border-t border-gray-100">
          {/* Aclaración del símbolo, arriba de los archivos */}
          {descripcion && (
            <p className="px-6 py-3 text-sm text-gray-600 border-b border-gray-100">{descripcion}</p>
          )}

          <div className="bg-gray-50 border-b border-gray-200 px-6 py-3">
            {seccion && canRevisar && onGuardarNota && (
              <div className="mb-3 rounded-lg border border-gray-200 bg-white p-3">
                <p className="text-[11px] font-medium text-gray-600 mb-1">Nota de la sección</p>
                <CuadroNota
                  valor={seccion.nota ?? ''}
                  puedeEditar={canRevisar}
                  placeholder="Nota…"
                  onGuardar={async (nota) => { onGuardarNota('SIMBOLO', nota); }}
                />
              </div>
            )}
                {files.length > 0 ? (
                  <div className="space-y-2">
                    {files.map(doc => (
                      <div key={doc.id} data-doc-id={doc.id}>
                        <div className="flex items-center gap-3 px-3 py-2.5 border rounded-lg bg-white border-gray-200">
                          <span className="text-lg flex-shrink-0">🖼️</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-gray-800 truncate">{doc.titulo || doc.nombreOriginal}</p>
                            <p className="text-xs text-gray-400 mt-0.5">Cargado {fmtDate(doc.creadoEn)}{doc.nombreOriginal && doc.nombreOriginal !== doc.titulo && <> · Archivo: {doc.nombreOriginal}</>}</p>
                            <MotivoDoc doc={doc} />
                            {doc.revisadoPorNombre && (
                              <p className="text-xs text-gray-400 mt-0.5">{accionRevisionLabel(doc.estadoRevision)} por {doc.revisadoPorNombre}{doc.revisadoEn && <> · {fmtDate(doc.revisadoEn)}</>}</p>
                            )}
                          </div>
                          {canRevisar ? (
                            <ChecklistEstadoBadge
                              estado={doc.estadoRevision}
                              canEdit={canRevisar}
                              onSelect={(newState) => {
                                aplicarConMotivo(motivoForm, onSetEstado, doc, newState);
                              }}
                            />
                          ) : (
                            <span className={`text-xs font-medium px-2.5 py-1 rounded-full whitespace-nowrap ${doc.estadoRevision === 'APROBADO' ? 'bg-green-100 text-green-700' : doc.estadoRevision === 'RECHAZADO' ? 'bg-red-100 text-red-700' : 'bg-amber-50 text-amber-700'}`}>
                              {doc.estadoRevision === 'APROBADO' ? 'Verificado' : doc.estadoRevision === 'RECHAZADO' ? 'Rechazado' : 'Pendiente'}
                            </span>
                          )}
                          <EditarDocumentoModal doc={{ id: doc.id, tipo: doc.tipo, titulo: doc.titulo, partidoId: doc.partido?.id, virtual: doc.virtual, estadoRevision: doc.estadoRevision, observacion: doc.observacion }} etapa={etapa} />
                          <button onClick={(e) => { e.stopPropagation(); documentoService.download(doc.id); }} className="text-xs text-blue-600 hover:underline flex-shrink-0">Ver archivo</button>
                          {/* Sin botones Verificar/Rechazar: se cambia el estado
                              pulsando el badge (abre el desplegable). */}
                        </div>
                        {motivoForm.abiertoEn(doc.id) && <MotivoFormInline form={motivoForm} />}
                      </div>
                    ))}
                  </div>
                ) : simbolos.length > 0 ? (
                  <div className="flex flex-wrap gap-2 items-center">
                    {simbolos.map((_, i) => <BotonVerArchivo key={i} label={`Símbolo ${i + 1}`} onClick={() => ver(`/expedientes/${expedienteId}/simbolos/${i}`)} />)}
                  </div>
            ) : (
              <p className="text-sm text-gray-400 text-center py-3">No se cargó ningún archivo de este tipo</p>
            )}
          </div>

          {/* Colores declarados, debajo de los archivos */}
          {coloresArr.length > 0 && (
            <div className="px-6 py-3">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Colores declarados</p>
              <div className="flex flex-wrap gap-4 items-center">
                {coloresArr.map((c, i) => (
                  <span key={i} className="inline-flex items-center gap-2 text-sm font-mono text-gray-700">
                    <span className="w-8 h-8 rounded-md border border-gray-300 flex-shrink-0" style={{ backgroundColor: c }} />
                    {c}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// SeccionObligatorios — checklist de documentos obligatorios
// =============================================================================

function TipoIcon({ status }: { status: EstadoTipo }) {
  if (status === 'verificado') return (
    <div className="w-6 h-6 rounded-full bg-green-600 flex items-center justify-center flex-shrink-0">
      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 12 12" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.5 6L5 8.5L9.5 3.5" />
      </svg>
    </div>
  );
  if (status === 'rechazado') return (
    <div className="w-6 h-6 rounded-full bg-red-600 flex items-center justify-center flex-shrink-0">
      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 12 12" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 3L9 9M9 3L3 9" />
      </svg>
    </div>
  );
  if (status === 'pendiente') return (
    <div className="w-6 h-6 rounded-full bg-amber-500 flex items-center justify-center flex-shrink-0">
      <div className="w-2 h-2 rounded-full bg-white opacity-80" />
    </div>
  );
  return <div className="w-6 h-6 rounded-full border-2 border-dashed border-gray-300 flex-shrink-0" />;
}

// Badge de estado en el checklist — muestra label y permite cambiar si canEdit y no es PENDIENTE
function ChecklistEstadoBadge({ estado, canEdit, onSelect }: {
  estado: EstadoRevision;
  canEdit: boolean;
  onSelect: (e: EstadoRevision) => void;
}) {
  const { open, setOpen, openPopover } = usePopover();
  const anchorRef = useRef<HTMLSpanElement>(null);
  const safeEstado: EstadoRevision = ESTADOS.includes(estado) ? estado : 'PENDIENTE';

  const cfg: Record<EstadoRevision, { label: string; cls: string }> = {
    APROBADO: { label: 'Verificado', cls: 'bg-green-100 text-green-700' },
    RECHAZADO: { label: 'Rechazado', cls: 'bg-red-100 text-red-700' },
    PENDIENTE: { label: 'Pendiente', cls: 'bg-amber-50 text-amber-700' },
  };
  const { label, cls } = cfg[safeEstado];

  // PENDIENTE también abre el desplegable. Antes no lo hacía porque esa transición
  // la cubrían los botones Verificar/Rechazar de la fila; al sacarlos, el estado
  // pendiente quedaba sin ninguna forma de cambiarse.
  if (!canEdit) {
    return <span className={`text-xs font-medium px-2.5 py-1 rounded-full whitespace-nowrap ${cls}`}>{label}</span>;
  }

  const opciones = (Object.keys(cfg) as EstadoRevision[]).filter(e => e !== safeEstado);
  return (
    <span ref={anchorRef} className="relative">
      <button
        onClick={() => open ? setOpen(false) : openPopover()}
        className={`text-xs font-medium px-2.5 py-1 rounded-full whitespace-nowrap cursor-pointer hover:opacity-80 transition-opacity ${cls}`}
      >
        {label} ▾
      </button>
      {/* En portal: la tarjeta de la sección tiene overflow-hidden y recortaba
          este menú cuando el archivo era el último de la sección. */}
      <PopoverPortal anchorRef={anchorRef} open={open} onClose={() => setOpen(false)} align="right" maxHeight={140} minWidth={140}>
        <div className="py-1">
          {opciones.map(e => (
            <button
              key={e}
              onClick={() => { onSelect(e); setOpen(false); }}
              className={`w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 ${cfg[e].cls}`}
            >
              {cfg[e].label}
            </button>
          ))}
        </div>
      </PopoverPortal>
    </span>
  );
}

interface SeccionObligatoriosProps {
  // Dimensión que se está mostrando (para corregir el motivo correcto).
  etapa?: 'ADMISIBILIDAD' | 'ANALISIS';
  tiposObligatorios: string[];
  archivos: Documento[];
  canEdit: boolean;
  isInterno: boolean;
  onSetEstado: (docId: string, estado: EstadoRevision, observacion?: string | null) => void;
  markedDocIds?: Set<string>;
  // Si se provee, muestra un botón "Subir/Re-subir" por fila con el tipo fijo.
  onSubirTipo?: (tipo: string) => void;
  // Partido activo (para el link "Ver motivo" → control de adherentes).
  partidoId?: string;
  // Tipo que llega abierto desde el tablero del expediente (?abrirTipo=…).
  abrirTipo?: string;
  // --- APROBACIÓN POR TIPO (alianzas) ---------------------------------------
  // Cuando el expediente tiene secciones (`expediente_tipos_documento`), el estado
  // de la fila lo DECIDE el check del tipo, no la cuenta de archivos: en una alianza
  // hay tipos con N documentos (una constancia por integrante, un acta por órgano) y
  // contar no puede saber cuántos hacen falta. Sin secciones el componente se
  // comporta igual que siempre.
  secciones?: SeccionTipo[];
  onAprobarTipo?: (tipo: string, estado: EstadoRevision, nota?: string) => void;
  /** Guarda sólo la nota de la sección, sin tocar las aprobaciones. */
  onGuardarNota?: (tipo: string, nota: string) => void;
  onEximirTipo?: (tipo: string, obligatorio: boolean) => void;
  puedeEximir?: boolean;
}

/** Estado de UNA dimensión de la sección. La nota no entra: es de la sección. */
export interface EstadoDimensionTipo {
  estado: EstadoRevision;
  revisadoPorNombre: string | null;
  revisadoEn: string | null;
}

/** Fila de `expediente_tipos_documento` proyectada para esta pantalla. */
export interface SeccionTipo {
  tipo: string;
  label: string;
  obligatorio: boolean;
  /** Comentario interno de la Secretaría sobre la sección. Uno solo, compartido por
   *  las dos etapas: describe la sección, no la etapa en la que se la miró. NO es
   *  una observación —esa es un acto formal con plazo, PDF y correo al partido. */
  nota: string | null;
  // Las MISMAS dos dimensiones que tiene cada documento. El componente muestra la
  // de la etapa que se está viendo, para que la casilla de Admisibilidad y la de
  // Análisis sean decisiones distintas y no la misma repetida.
  admisibilidad: EstadoDimensionTipo;
  analisis: EstadoDimensionTipo;
}

function SeccionObligatorios({ tiposObligatorios, archivos, canEdit, isInterno, onSetEstado, markedDocIds, onSubirTipo, partidoId, abrirTipo, etapa, secciones, onAprobarTipo, onGuardarNota, onEximirTipo, puedeEximir }: SeccionObligatoriosProps) {
  const navigate = useNavigate();
  const [expandedTipo, setExpandedTipo] = useState<string | null>(abrirTipo ?? null);
  // Tipos cuyos docs incluyen alguno marcado para revisión → se mantienen abiertos
  // siempre (no se cierran al clickear, pero se pueden cerrar manualmente)
  const tiposConDocMarcado = new Set<string>();
  if (markedDocIds && markedDocIds.size > 0) {
    archivos.forEach(d => {
      if (markedDocIds.has(d.id)) tiposConDocMarcado.add(d.tipo);
    });
  }
  // docId → muestra formulario de rechazo inline (para PENDIENTE o cambio a RECHAZADO)
  const motivoForm = useMotivoForm(onSetEstado);

  // Agrupar archivos por tipo, ordenados por fecha ascendente
  const byTipo: Record<string, Documento[]> = {};
  tiposObligatorios.forEach(tipo => {
    byTipo[tipo] = archivos
      .filter(a => a.tipo === tipo)
      .sort((a, b) => new Date(a.creadoEn).getTime() - new Date(b.creadoEn).getTime());
  });

  // Índice de secciones. Presente = manda el check del tipo; ausente = se deriva de
  // los archivos, como en todos los demás trámites.
  const porTipo = new Map((secciones ?? []).map((x) => [x.tipo, x]));
  const conSecciones = porTipo.size > 0;
  // La dimensión que se está viendo. Es la misma que ya proyecta los estados de los
  // archivos (`etapa`), así que la casilla y los archivos de abajo hablan siempre de
  // lo mismo.
  const dimVista: 'ADMISIBILIDAD' | 'ANALISIS' = etapa === 'ADMISIBILIDAD' ? 'ADMISIBILIDAD' : 'ANALISIS';
  const dimDe = (sec: SeccionTipo) => (dimVista === 'ADMISIBILIDAD' ? sec.admisibilidad : sec.analisis);
  const estadoDeFila = (tipo: string): EstadoTipo => {
    const sec = porTipo.get(tipo);
    if (!sec) return getEstadoTipo(byTipo[tipo] || []);
    const d = dimDe(sec);
    if (d.estado === 'APROBADO') return 'verificado';
    if (d.estado === 'RECHAZADO') return 'rechazado';
    return (byTipo[tipo] || []).length > 0 ? 'pendiente' : 'faltante';
  };
  // Con secciones, los complementarios no cuentan para el total: no los pide el
  // Art. 27 y no bloquean nada, así que inflarían el denominador.
  const tiposContables = conSecciones
    ? tiposObligatorios.filter((t) => porTipo.get(t)?.obligatorio !== false)
    : tiposObligatorios;
  const verified = tiposContables.filter((t) => {
    const e = estadoDeFila(t);
    return e === 'verificado' || e === 'rechazado';
  }).length;
  const total = tiposContables.length;

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      {/* Header del checklist */}
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between gap-4">
        <div>
          <span className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Documentos Obligatorios</span>
          <p className="text-xs text-gray-400 mt-0.5">
            {conSecciones
              ? 'Tildá el check de cada sección para darla por completa'
              : 'Requeridos según tipo de partido'}
          </p>
        </div>
        <span className="text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap bg-gray-100 text-gray-600 border border-gray-200">
          {verified} / {total} revisados
        </span>
      </div>

      {/* Filas por tipo */}
      {tiposObligatorios.map(tipo => {
        const files = byTipo[tipo] || [];
        const seccion = porTipo.get(tipo);
        // Estado de la sección EN LA ETAPA QUE SE ESTÁ VIENDO.
        const dim = seccion ? dimDe(seccion) : null;
        const status = estadoDeFila(tipo);
        // `estadoRevision` ya viene proyectado a la etapa que se está viendo, así
        // que esto cuenta los pendientes de ESA dimensión.
        const archivosSinRevisar = files.filter((d) => d.estadoRevision === 'PENDIENTE').length;
        // Archivos cargados DESPUÉS de revisar la sección y todavía sin mirar. Es el
        // caso que se escapa: el partido responde una observación sobre una sección
        // ya aprobada y la fila sigue en verde.
        const avisoNuevos = avisoArchivoNuevo(archivosNuevosSinRevisar(
          dim?.revisadoEn,
          files.map((d) => ({ creadoEn: d.creadoEn, estado: d.estadoRevision })),
        ));
        const isOpen = expandedTipo === tipo || tiposConDocMarcado.has(tipo);
        const lastFile = files[files.length - 1];
        const lastRejected = [...files].filter(f => f.estadoRevision === 'RECHAZADO').slice(-1)[0];

        let sub: React.ReactNode;
        if (seccion && dim) {
          // Con sección, lo que se informa es el juicio sobre la SECCIÓN. El estado
          // de cada archivo queda en el detalle expandido.
          const cuenta = `${files.length} ${files.length === 1 ? 'archivo' : 'archivos'}`;
          if (dim.estado === 'APROBADO') {
            sub = (
              <span className="text-gray-400">
                Sección aprobada por {dim.revisadoPorNombre || '—'}
                {dim.revisadoEn && <> · {fmtDate(dim.revisadoEn)}</>} · {cuenta}
              </span>
            );
          } else if (dim.estado === 'RECHAZADO') {
            sub = (
              <span>
                <span className="text-red-600">Sección rechazada</span>
                {dim.revisadoPorNombre && (
                  <span className="text-gray-400"> por {dim.revisadoPorNombre}{dim.revisadoEn && ` · ${fmtDate(dim.revisadoEn)}`}</span>
                )}
                {/* La nota es interna: en la vista de la agrupación no se muestra
                    aunque el estado sea RECHAZADO. Lo que le corresponde leer es la
                    observación, que es el acto formal con plazo y correo. */}
                {isInterno && seccion.nota && <span className="text-gray-400"> · <span className="text-red-500">Nota: {seccion.nota}</span></span>}
                <span className="text-gray-400"> · {cuenta}</span>
              </span>
            );
          } else if (archivosSinRevisar > 0) {
            sub = (
              <span className="text-amber-600">
                {cuenta} · {archivosSinRevisar} sin revisar
              </span>
            );
          } else {
            sub = (
              <span className={files.length === 0 ? 'text-gray-300' : 'text-amber-600'}>
                {files.length === 0 ? 'Sin archivos · sección sin aprobar' : `${cuenta} · sección sin aprobar`}
              </span>
            );
          }
        } else if (status === 'verificado') {
          const approvedFile = files.find(f => f.estadoRevision === 'APROBADO');
          const rejCount = files.filter(f => f.estadoRevision === 'RECHAZADO').length;
          sub = (
            <span className="text-gray-400">
              Verificado por {approvedFile?.revisadoPorNombre || '—'}
              {approvedFile?.revisadoEn && <> · {fmtDate(approvedFile.revisadoEn)}</>}
              {rejCount > 0 && <span className="text-amber-500"> · {rejCount} versión anterior rechazada</span>}
            </span>
          );
        } else if (status === 'rechazado') {
          sub = (
            <span>
              <span className="text-red-600">Rechazado</span>
              {lastRejected?.revisadoPorNombre && (
                <span className="text-gray-400"> por {lastRejected.revisadoPorNombre}{lastRejected.revisadoEn && ` · ${fmtDate(lastRejected.revisadoEn)}`}</span>
              )}
              {lastRejected?.observacion && (
                <span className="text-gray-400"> · <span className="text-red-500">Motivo: {resumirEnUnaLinea(lastRejected.observacion, RESUMEN_MOTIVO_LINEA)}</span></span>
              )}
            </span>
          );
        } else if (status === 'pendiente') {
          sub = <span className="text-amber-600">Cargado · pendiente de revisión</span>;
        } else {
          sub = <span className="text-gray-300">No cargado</span>;
        }

        return (
          <div key={tipo} data-tipo-doc={tipo}>
            {/* Fila del tipo */}
            <div
              className={`flex items-center px-6 py-3 gap-3 cursor-pointer border-b border-gray-100 transition-colors
                ${status === 'rechazado' ? 'bg-red-50 hover:bg-red-100' : 'hover:bg-gray-50'}`}
              onClick={() => setExpandedTipo(isOpen ? null : tipo)}
            >
              {/* EL CHECK DE LA SECCIÓN. Es el validado definitivo del tipo: tildarlo
                  la aprueba sin importar el estado de los archivos de adentro.
                  Sólo aparece con secciones cargadas (hoy, alianzas). */}
              {seccion && dim && canEdit && onAprobarTipo ? (
                <input
                  type="checkbox"
                  checked={dim.estado === 'APROBADO'}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => {
                    const aprobar = e.target.checked;
                    // NO se aprueba con archivos sin revisar. Un RECHAZADO no cuenta
                    // (se rechazó una versión y se subió otra); PENDIENTE significa
                    // que nadie lo abrió, y el check declara un juicio.
                    if (aprobar && archivosSinRevisar > 0) {
                      toast.error(
                        `No es posible aprobar: quedan ${archivosSinRevisar} ` +
                        `${archivosSinRevisar === 1 ? 'documento pendiente' : 'documentos pendientes'} dentro.`,
                      );
                      e.target.checked = false;
                      return;
                    }
                    onAprobarTipo(tipo, aprobar ? 'APROBADO' : 'PENDIENTE');
                  }}
                  title={
                    dim.estado === 'APROBADO'
                      ? 'Desmarcar: la sección vuelve a pendiente'
                      : archivosSinRevisar > 0
                        ? `Aprobar la sección (quedan ${archivosSinRevisar} archivo(s) sin revisar en esta etapa)`
                        : 'Aprobar la sección completa'
                  }
                  className="w-5 h-5 flex-shrink-0 rounded border-gray-300 accent-green-600 text-green-600 focus:ring-green-500 cursor-pointer"
                />
              ) : seccion && dim ? (
                /* MISMA casilla, deshabilitada: la vista del partido perdía el
                   cuadrado y quedaba el círculo de estado, así que la misma fila se
                   veía distinta según quién mirara y no había cómo saber de un
                   vistazo si la Secretaría ya la había dado por completa.
                   Sin tildar, cuadrado blanco. */
                <input
                  type="checkbox"
                  checked={dim.estado === 'APROBADO'}
                  readOnly
                  title={dim.estado === 'APROBADO'
                    ? 'La Secretaría Electoral dio esta sección por completa'
                    : dim.estado === 'RECHAZADO'
                      ? 'La Secretaría Electoral rechazó esta sección'
                      : 'La Secretaría Electoral todavía no dio esta sección por completa'}
                  /* Sin `disabled`: el navegador lo pintaría gris y le ganaría al
                     accent-color. `pointer-events-none` deja que el clic abra la fila. */
                  className="w-5 h-5 flex-shrink-0 rounded border-gray-300 bg-white accent-green-600
                    text-green-600 pointer-events-none"
                />
              ) : (
                /* Sin sección sembrada el estado se deriva de los archivos y no hay
                   una casilla que represente una decisión: va el ícono de estado. */
                <TipoIcon status={status} />
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-800">
                  {seccion?.label || tipoDocLabels[tipo] || tipo}
                  {seccion && !seccion.obligatorio && (
                    <span className="ml-2 px-1.5 py-0.5 text-[10px] rounded bg-slate-100 text-slate-600 align-middle">
                      no exigido
                    </span>
                  )}
                </p>
                <p className="text-xs mt-0.5 leading-relaxed">{sub}</p>
                {avisoNuevos && (
                  <p className="mt-1 inline-flex items-center gap-1.5 rounded-md bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800">
                    <span aria-hidden>●</span> {avisoNuevos}
                  </p>
                )}
              </div>
              {lastFile && (
                <span className="text-xs text-gray-400 whitespace-nowrap">{fmtDate(lastFile.creadoEn)}</span>
              )}
              {/* La vista del partido (externo) NO puede re-subir un documento ya
                  cargado: solo se muestra "Subir" cuando aún no hay archivo. El lado
                  interno conserva la posibilidad de re-subir. */}
              {onSubirTipo && (isInterno || files.length === 0) && (
                <button
                  onClick={(e) => { e.stopPropagation(); onSubirTipo(tipo); }}
                  className="text-xs font-medium px-2.5 py-1 rounded-md bg-blue-600 hover:bg-blue-700 text-white whitespace-nowrap flex-shrink-0"
                >
                  {files.length > 0 ? 'Re-subir' : 'Subir'}
                </button>
              )}
              <svg
                className={`w-3 h-3 text-gray-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-90' : ''}`}
                fill="currentColor" viewBox="0 0 20 20"
              >
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
            </div>

            {/* Detalle expandido */}
            {isOpen && (
              <div className="bg-gray-50 border-b border-gray-200 px-6 py-3 pl-[72px]">
                {/* NOTA de la sección. Es sólo una nota: el estado lo decide el
                    check de la fila, no este cuadro. Se muestra siempre —también con
                    la sección aprobada o rechazada—, porque anotar algo no depende
                    de en qué estado esté. */}
                {seccion && canEdit && (
                  <div className="mb-3 rounded-lg border border-gray-200 bg-white p-3">
                    <p className="text-[11px] font-medium text-gray-600 mb-1">Nota de la sección</p>
                    {/* El MISMO cuadro del tablero de documentación: textarea amarillo
                        con el botón "Anotar" adentro. */}
                    <CuadroNota
                      valor={seccion.nota ?? ''}
                      puedeEditar={canEdit}
                      placeholder="Nota..."
                      onGuardar={async (nota) => { onGuardarNota?.(tipo, nota); }}
                    />
                    {puedeEximir && onEximirTipo && (
                      <button
                        onClick={() => onEximirTipo(tipo, !seccion.obligatorio)}
                        className="mt-2 text-[11px] text-gray-500 hover:text-gray-700 underline"
                      >
                        {seccion.obligatorio
                          ? 'Eximir este requisito en este expediente'
                          : 'Volver a exigir este requisito'}
                      </button>
                    )}
                  </div>
                )}
                {files.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-3">No se cargó ningún archivo de este tipo</p>
                ) : (
                  <div className="space-y-2">
                    {files.map(doc => {
                      const isMarked = !!markedDocIds?.has(doc.id);
                      return (
                      <div key={doc.id} data-doc-id={doc.id}>
                        <div className={`flex items-center gap-3 px-3 py-2.5 border rounded-lg ${
                          isMarked ? 'bg-yellow-50 border-yellow-400 border-l-4' : 'bg-white border-gray-200'
                        }`}>
                          <span className="text-lg flex-shrink-0">📄</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-gray-800 truncate">
                              {doc.titulo || doc.nombreOriginal}
                              {isMarked && (
                                <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded bg-yellow-200 text-yellow-900 text-[10px] font-semibold uppercase tracking-wide">
                                  Adjuntó a respuesta
                                </span>
                              )}
                            </p>
                            <p className="text-xs text-gray-400 mt-0.5">
                              Cargado {fmtDate(doc.creadoEn)}
                              {doc.nombreOriginal && doc.nombreOriginal !== doc.titulo && <> · Archivo: {doc.nombreOriginal}</>}
                              {isInterno && doc.expediente && (
                                <> · <Link to={`/expedientes/${doc.expediente.id}`} className="text-primary-600 hover:underline" onClick={e => e.stopPropagation()}>{doc.expediente.numero}{doc.expediente.tipo ? ` — ${tipoTramiteLabel(doc.expediente.tipo)}` : ''}</Link></>
                              )}
                            </p>
                            <MotivoDoc doc={doc} />
                            {doc.revisadoPorNombre && (
                              <p className="text-xs text-gray-400 mt-0.5">
                                {accionRevisionLabel(doc.estadoRevision)} por {doc.revisadoPorNombre}
                                {doc.revisadoEn && <> · {fmtDate(doc.revisadoEn)}</>}
                              </p>
                            )}
                          </div>

                          {/* Badge de estado */}
                          <ChecklistEstadoBadge
                            estado={doc.estadoRevision}
                            canEdit={canEdit}
                            onSelect={(newState) => {
                              aplicarConMotivo(motivoForm, onSetEstado, doc, newState);
                            }}
                          />

                          <EditarDocumentoModal doc={{ id: doc.id, tipo: doc.tipo, titulo: doc.titulo, partidoId: doc.partido?.id, virtual: doc.virtual, estadoRevision: doc.estadoRevision, observacion: doc.observacion }} etapa={etapa} />

                          {/* Ver archivo — siempre visible */}
                          <button
                            onClick={(e) => { e.stopPropagation(); documentoService.download(doc.id); }}
                            className="text-xs text-blue-600 hover:underline flex-shrink-0"
                          >
                            Ver archivo
                          </button>

                          {/* El listado de adherentes (Excel) tiene "Ver motivo" cuando fue
                              rechazado por el control: lleva a /adherentes con todo cargado.
                              Solo del lado interno: el partido no accede al control. */}
                          {isInterno && doc.tipo === 'LISTADO_ADHERENTES_EXCEL' && doc.estadoRevision === 'RECHAZADO' && (
                            <button
                              onClick={(e) => { e.stopPropagation(); navigate(`/adherentes?partido=${partidoId || ''}&obs=1`); }}
                              className="text-xs text-amber-700 hover:underline flex-shrink-0 font-medium"
                            >
                              Ver motivo
                            </button>
                          )}

                          {/* Sin botones Verificar/Rechazar: el estado se cambia
                              pulsando el badge, que abre el desplegable con las tres
                              opciones. Los botones duplicaban esa acción y ocupaban
                              el ancho de la fila. */}
                        </div>

                        {/* Formulario del motivo (verificar / rechazar / volver a pendiente) */}
                        {motivoForm.abiertoEn(doc.id) && <MotivoFormInline form={motivoForm} />}
                      </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

    </div>
  );
}

// =============================================================================
// SeccionAgrupadaPorTipo — agrupa documentos por tipo en desplegables.
// Usada para "Otros Documentos": cada tipo presente (símbolo partidario, etc.)
// aparece como una fila desplegable que lista sus archivos. Sólo se muestran
// los tipos que tienen al menos un archivo cargado (todos son opcionales).
// =============================================================================

interface SeccionAgrupadaPorTipoProps {
  // Dimensión que se está mostrando (para corregir el motivo correcto).
  etapa?: 'ADMISIBILIDAD' | 'ANALISIS';
  titulo: string;
  subtitulo?: string;
  // Tipo que llega abierto desde el tablero del expediente (?abrirTipo=…).
  abrirTipo?: string;
  docs: Documento[];
  canEdit: boolean;
  isInterno: boolean;
  onSetEstado: (docId: string, estado: EstadoRevision, observacion?: string | null) => void;
  markedDocIds?: Set<string>;
  // Si se provee, muestra un botón "Subir" en el header de la sección.
  onSubir?: () => void;
}

function SeccionAgrupadaPorTipo({ titulo, subtitulo, docs, canEdit, isInterno, onSetEstado, markedDocIds, onSubir, abrirTipo, etapa }: SeccionAgrupadaPorTipoProps) {
  const [expandedTipo, setExpandedTipo] = useState<string | null>(abrirTipo ?? null);
  const motivoForm = useMotivoForm(onSetEstado);

  // Sólo documentos reales (no virtuales). Los tipos se derivan de lo que se subió.
  const realDocs = docs.filter(d => !d.virtual);

  // Tipos presentes, ordenados alfabéticamente por su etiqueta legible.
  const tiposPresentes = Array.from(new Set(realDocs.map(d => d.tipo)))
    .sort((a, b) => (tipoDocLabels[a] || a).localeCompare(tipoDocLabels[b] || b, 'es'));

  // Agrupar archivos por tipo, ordenados por fecha ascendente.
  const byTipo: Record<string, Documento[]> = {};
  tiposPresentes.forEach(tipo => {
    byTipo[tipo] = realDocs
      .filter(d => d.tipo === tipo)
      .sort((a, b) => new Date(a.creadoEn).getTime() - new Date(b.creadoEn).getTime());
  });

  // Tipos cuyos docs incluyen alguno marcado para revisión → se abren solos.
  const tiposConDocMarcado = new Set<string>();
  if (markedDocIds && markedDocIds.size > 0) {
    realDocs.forEach(d => { if (markedDocIds.has(d.id)) tiposConDocMarcado.add(d.tipo); });
  }

  // Se abre sola si trae un adjunto de respuesta o si el tablero pidió este tipo.
  const traeTipoPedido = !!abrirTipo && tiposPresentes.includes(abrirTipo);
  const hasMarkedDocInside = tiposConDocMarcado.size > 0;
  const [open, setOpen] = useState(hasMarkedDocInside || traeTipoPedido);
  useEffect(() => { if (hasMarkedDocInside || traeTipoPedido) setOpen(true); }, [hasMarkedDocInside, traeTipoPedido]);

  const verified = tiposPresentes.filter(t => {
    const e = getEstadoTipo(byTipo[t] || [], true);
    return e === 'verificado' || e === 'rechazado';
  }).length;
  const total = tiposPresentes.length;

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      {/* Header colapsable de la sección */}
      <div className="w-full px-6 py-4 flex items-center justify-between gap-3">
        <button
          onClick={() => setOpen(o => !o)}
          className="flex items-center justify-between gap-3 flex-1 min-w-0 hover:opacity-80 text-left"
        >
          <div className="min-w-0">
            <span className="text-sm font-semibold text-gray-700 uppercase tracking-wide">{titulo}</span>
            {subtitulo && <p className="text-xs text-gray-400 mt-0.5">{subtitulo}</p>}
          </div>
          <div className="flex items-center gap-3">
            {total > 0 && (
              <span className="text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap bg-gray-100 text-gray-600 border border-gray-200">
                {verified} / {total} revisados
              </span>
            )}
            <svg className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
              fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </button>
        {onSubir && (
          <button
            onClick={onSubir}
            className="text-xs font-medium px-2.5 py-1 rounded-md bg-blue-600 hover:bg-blue-700 text-white whitespace-nowrap flex-shrink-0"
          >
            Subir
          </button>
        )}
      </div>

      {open && (
        <div className="border-t border-gray-200">
          {tiposPresentes.length === 0 ? (
            <p className="px-6 py-4 text-sm text-gray-400">No hay documentos en esta sección.</p>
          ) : (
            tiposPresentes.map(tipo => {
              const files = byTipo[tipo] || [];
              const status = getEstadoTipo(files, true);
              const isOpen = expandedTipo === tipo || tiposConDocMarcado.has(tipo);
              const lastFile = files[files.length - 1];
              const lastRejected = [...files].filter(f => f.estadoRevision === 'RECHAZADO').slice(-1)[0];

              let sub: React.ReactNode;
              if (status === 'verificado') {
                const approvedFile = files.find(f => f.estadoRevision === 'APROBADO');
                const rejCount = files.filter(f => f.estadoRevision === 'RECHAZADO').length;
                sub = (
                  <span className="text-gray-400">
                    Verificado por {approvedFile?.revisadoPorNombre || '—'}
                    {approvedFile?.revisadoEn && <> · {fmtDate(approvedFile.revisadoEn)}</>}
                    {rejCount > 0 && <span className="text-amber-500"> · {rejCount} versión anterior rechazada</span>}
                  </span>
                );
              } else if (status === 'rechazado') {
                sub = (
                  <span>
                    <span className="text-red-600">Rechazado</span>
                    {lastRejected?.revisadoPorNombre && (
                      <span className="text-gray-400"> por {lastRejected.revisadoPorNombre}{lastRejected.revisadoEn && ` · ${fmtDate(lastRejected.revisadoEn)}`}</span>
                    )}
                    {lastRejected?.observacion && (
                      <span className="text-gray-400"> · <span className="text-red-500">Motivo: {resumirEnUnaLinea(lastRejected.observacion, RESUMEN_MOTIVO_LINEA)}</span></span>
                    )}
                  </span>
                );
              } else {
                sub = <span className="text-amber-600">Cargado · pendiente de revisión</span>;
              }

              const countLabel = files.length > 1 ? ` (${files.length})` : '';

              return (
                <div key={tipo} data-tipo-doc={tipo}>
                  {/* Fila del tipo */}
                  <div
                    className={`flex items-center px-6 py-3 gap-3 cursor-pointer border-b border-gray-100 transition-colors
                      ${status === 'rechazado' ? 'bg-red-50 hover:bg-red-100' : 'hover:bg-gray-50'}`}
                    onClick={() => setExpandedTipo(isOpen ? null : tipo)}
                  >
                    <TipoIcon status={status} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800">{tipoDocLabels[tipo] || tipo}{countLabel}</p>
                      <p className="text-xs mt-0.5 leading-relaxed">{sub}</p>
                    </div>
                    {lastFile && (
                      <span className="text-xs text-gray-400 whitespace-nowrap">{fmtDate(lastFile.creadoEn)}</span>
                    )}
                    <svg
                      className={`w-3 h-3 text-gray-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-90' : ''}`}
                      fill="currentColor" viewBox="0 0 20 20"
                    >
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>

                  {/* Detalle expandido: lista de archivos de este tipo */}
                  {isOpen && (
                    <div className="bg-gray-50 border-b border-gray-200 px-6 py-3 pl-[72px]">
                      <div className="space-y-2">
                        {files.map(doc => {
                          const isMarked = !!markedDocIds?.has(doc.id);
                          return (
                          <div key={doc.id} data-doc-id={doc.id}>
                            <div className={`flex items-center gap-3 px-3 py-2.5 border rounded-lg ${
                              isMarked ? 'bg-yellow-50 border-yellow-400 border-l-4' : 'bg-white border-gray-200'
                            }`}>
                              <span className="text-lg flex-shrink-0">📄</span>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium text-gray-800 truncate">
                                  {doc.titulo || doc.nombreOriginal}
                                  {isMarked && (
                                    <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded bg-yellow-200 text-yellow-900 text-[10px] font-semibold uppercase tracking-wide">
                                      Adjuntó a respuesta
                                    </span>
                                  )}
                                </p>
                                <p className="text-xs text-gray-400 mt-0.5">
                                  Cargado {fmtDate(doc.creadoEn)}
                                  {doc.nombreOriginal && doc.nombreOriginal !== doc.titulo && <> · Archivo: {doc.nombreOriginal}</>}
                              {doc.nombreOriginal && doc.nombreOriginal !== doc.titulo && <> · Archivo: {doc.nombreOriginal}</>}
                                  {isInterno && doc.expediente && (
                                    <> · <Link to={`/expedientes/${doc.expediente.id}`} className="text-primary-600 hover:underline" onClick={e => e.stopPropagation()}>{doc.expediente.numero}{doc.expediente.tipo ? ` — ${tipoTramiteLabel(doc.expediente.tipo)}` : ''}</Link></>
                                  )}
                                  {doc.descripcion && <> · {doc.descripcion}</>}
                                </p>
                                <MotivoDoc doc={doc} />
                                {doc.revisadoPorNombre && (
                                  <p className="text-xs text-gray-400 mt-0.5">
                                    {accionRevisionLabel(doc.estadoRevision)} por {doc.revisadoPorNombre}
                                    {doc.revisadoEn && <> · {fmtDate(doc.revisadoEn)}</>}
                                  </p>
                                )}
                              </div>

                              {/* Badge de estado */}
                              <ChecklistEstadoBadge
                                estado={doc.estadoRevision}
                                canEdit={canEdit}
                                onSelect={(newState) => {
                                  aplicarConMotivo(motivoForm, onSetEstado, doc, newState);
                                }}
                              />

                              <EditarDocumentoModal doc={{ id: doc.id, tipo: doc.tipo, titulo: doc.titulo, partidoId: doc.partido?.id, virtual: doc.virtual, estadoRevision: doc.estadoRevision, observacion: doc.observacion }} etapa={etapa} />

                              {/* Ver archivo — siempre visible */}
                              <button
                                onClick={(e) => { e.stopPropagation(); documentoService.download(doc.id); }}
                                className="text-xs text-blue-600 hover:underline flex-shrink-0"
                              >
                                Ver archivo
                              </button>

                              {/* Sin botones Verificar/Rechazar: el estado se cambia pulsando
                                  el badge, que abre el desplegable con las tres opciones. */}
                            </div>

                            {/* Formulario del motivo (verificar / rechazar / volver a pendiente) */}
                            {motivoForm.abiertoEn(doc.id) && <MotivoFormInline form={motivoForm} />}
                          </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Tipos para SeccionDNIAutoridades
// =============================================================================

interface DocDNI {
  id: string;
  tipo: string;
  mimeType?: string;
  /** Cuándo se cargó. Se compara con la fecha de revisión de la sección. */
  creadoEn?: string;
  // Dimensiones crudas que devuelve el backend.
  estadoRevisionAdmisibilidad?: EstadoRevision;
  observacionAdmisibilidad?: string | null;
  admisibilidadRevisadoPor?: string | null;
  admisibilidadRevisadoEn?: string | null;
  estadoRevisionAnalisis?: EstadoRevision;
  observacionAnalisis?: string | null;
  analisisRevisadoPor?: string | null;
  analisisRevisadoEn?: string | null;
  // Proyección efectiva según la etapa activa.
  estadoRevision: EstadoRevision;
  observacion?: string;
  revisadoPorNombre?: string;
  revisadoEn?: string;
  descripcion?: string;
}

// Proyecta el estado/motivo efectivo de un DocDNI según la etapa activa.
function proyectarDniEtapa(d: DocDNI, enAnalisis: boolean): DocDNI {
  return {
    ...d,
    estadoRevision: (enAnalisis ? d.estadoRevisionAnalisis : d.estadoRevisionAdmisibilidad) ?? d.estadoRevision ?? 'PENDIENTE',
    observacion: enAnalisis ? (d.observacionAnalisis ?? undefined) : (d.observacionAdmisibilidad ?? undefined),
    revisadoPorNombre: enAnalisis ? (d.analisisRevisadoPor ?? undefined) : (d.admisibilidadRevisadoPor ?? undefined),
    revisadoEn: enAnalisis ? (d.analisisRevisadoEn ?? undefined) : (d.admisibilidadRevisadoEn ?? undefined),
  };
}

interface AutoridadDNI {
  id: string;
  dni: string;
  nombre: string;
  apellido: string;
  genero: string;
  fechaNacimiento?: string;
  mandato: { cargo: string; esTitular: boolean; mandatoDesde: string; mandatoHasta?: string } | null;
  dniFrente: DocDNI[];
  dniDorso: DocDNI[];
}

interface OrganoConDNIs {
  id: string;
  tipoOrgano: string;
  denominacion: string;
  denominacionPersonalizada?: string;
  esObligatorio: boolean;
  fechaMandatoDesde?: string;
  fechaMandatoHasta?: string;
  autoridades: AutoridadDNI[];
}

// =============================================================================
// SeccionDNIAutoridades — verificación de DNIs agrupada por órgano
// =============================================================================

// Documento que REPRESENTA al lado (frente/dorso) de una persona en el badge.
//
// Manda el PENDIENTE: si hay un archivo sin revisar, el badge tiene que avisarlo
// aunque al lado haya otro ya aprobado. Antes ganaba el APROBADO y el pendiente
// quedaba escondido detrás de un badge verde — nadie podía encontrarlo para
// verificarlo, pero seguía contando como documentación sin verificar y trababa la
// elevación al Secretario (caso real: un dorso duplicado del Partido De La
// Victoria). Mismo criterio que `getEstadoTipo`: un pendiente manda.
//
// Después del pendiente gana el aprobado sobre el rechazado: si el partido
// repuso un archivo que había sido rechazado, el lado está resuelto.
function bestDoc(docs: DocDNI[]): DocDNI | null {
  return docs.find(d => d.estadoRevision === 'PENDIENTE')
    ?? docs.find(d => d.estadoRevision === 'APROBADO')
    ?? docs.find(d => d.estadoRevision === 'RECHAZADO')
    ?? docs[0]
    ?? null;
}

// Índice del documento que muestra el badge, para abrir el visor directamente en
// él. Sin esto el visor abría siempre en el #1 y había que adivinar en cuál de
// las versiones estaba el pendiente que hay que verificar.
function bestDocIdx(docs: DocDNI[]): number {
  const mejor = bestDoc(docs);
  const idx = mejor ? docs.indexOf(mejor) : -1;
  return idx >= 0 ? idx : 0;
}

function DniBadge({ docs, lado, onClick, isMarked }: {
  docs: DocDNI[];
  lado: 'Frente' | 'Dorso';
  onClick: () => void;
  isMarked?: boolean;
}) {
  const doc = bestDoc(docs);
  const estado = doc?.estadoRevision;
  const base = 'inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border text-xs font-medium cursor-pointer transition-colors';
  // Cuando el doc está adjuntado a una respuesta que el supervisor está revisando,
  // sobre-imponemos un ring amarillo grueso y saturado para destacarlo sin perder
  // el color de estado del badge (verde/rojo/ámbar).
  const markedRing = isMarked ? ' ring-4 ring-yellow-500 ring-offset-2 shadow-lg shadow-yellow-300/50' : '';
  const countBadge = docs.length > 1
    ? <span className="ml-0.5 bg-white/60 rounded px-1 text-[10px] font-bold">{docs.length}</span>
    : null;
  if (!doc) {
    return (
      <button onClick={onClick} data-doc-id={undefined} className={`${base} border-gray-200 text-gray-400 bg-white hover:bg-gray-50${markedRing}`}>
        <span className="w-2 h-2 rounded-full bg-gray-300 inline-block" />
        {lado}
      </button>
    );
  }
  if (estado === 'APROBADO') {
    return (
      <button onClick={onClick} data-doc-id={doc.id} className={`${base} border-green-400 text-green-700 bg-green-50 hover:bg-green-100${markedRing}`}>
        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
        {lado}{countBadge}
      </button>
    );
  }
  if (estado === 'RECHAZADO') {
    return (
      <button onClick={onClick} data-doc-id={doc.id} className={`${base} border-red-400 text-red-700 bg-red-50 hover:bg-red-100${markedRing}`}>
        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
        {lado}{countBadge}
      </button>
    );
  }
  return (
    <button onClick={onClick} data-doc-id={doc.id} className={`${base} border-amber-400 text-amber-700 bg-amber-50 hover:bg-amber-100${markedRing}`}>
      <span className="w-2 h-2 rounded-full bg-amber-400 inline-block" />
      {lado}{countBadge}
    </button>
  );
}

function SeccionDNIAutoridades({
  expedienteId,
  partidoId,
  canEdit,
  onSetEstado,
  markedDocIds,
  etapaVista,
  titulo,
  defaultOpen,
  filasSeccion,
  notaSeccion,
  onAprobarSeccion,
  onGuardarNotaSeccion,
}: {
  expedienteId?: string;
  partidoId?: string;
  canEdit: boolean;
  onSetEstado: (docId: string, estado: EstadoRevision, observacion?: string | null) => void;
  markedDocIds?: Set<string>;
  etapaVista: 'ADMISIBILIDAD' | 'ANALISIS';
  titulo?: string;
  defaultOpen?: boolean;
  // Aprobación POR SECCIÓN: un check para los DOS tipos (frente y dorso). Sin filas
  // sembradas estas props van vacías y la sección se comporta como siempre.
  filasSeccion?: FilaSeccion[];
  notaSeccion?: string | null;
  onAprobarSeccion?: (estado: EstadoRevision) => void;
  onGuardarNotaSeccion?: (nota: string) => void;
}) {
  const queryClient = useQueryClient();
  const [isOuterOpen, setIsOuterOpen] = useState(defaultOpen ?? false);
  const [expandedOrganos, setExpandedOrganos] = useState<Record<string, boolean>>({});
  const [modal, setModal] = useState<{
    isOpen: boolean;
    autoridad: AutoridadDNI | null;
    side: 'frente' | 'dorso';
    docIdx: number;
    previewUrl: string | null;
    loadingUrl: boolean;
  }>({ isOpen: false, autoridad: null, side: 'frente', docIdx: 0, previewUrl: null, loadingUrl: false });
  // Acción a la espera de motivo (null = ninguna). Los tres estados lo piden;
  // sólo el rechazo exige texto.
  const [motivoAccion, setMotivoAccion] = useState<EstadoRevision | null>(null);
  const [motivoTexto, setMotivoTexto] = useState('');

  // Usa el endpoint de expediente si hay expedienteId, sino el de partido
  const queryKey = expedienteId ? ['autoridades-dni', 'exp', expedienteId] : ['autoridades-dni', 'partido', partidoId];
  const { data: organosData = [], isLoading } = useQuery<OrganoConDNIs[]>({
    queryKey,
    queryFn: async () => {
      if (expedienteId) {
        const response = await expedienteService.getAutoridadesDni(expedienteId);
        return response.data.data as OrganoConDNIs[];
      }
      const response = await partidoService.getAutoridadesDni(partidoId!);
      return response.data.data as OrganoConDNIs[];
    },
    enabled: !!(expedienteId || partidoId),
  });

  // Proyecta el estado de cada DNI según la etapa activa (admisibilidad/análisis).
  const organos = useMemo(() => {
    const enAnalisis = etapaVista === 'ANALISIS';
    return organosData.map(o => ({
      ...o,
      autoridades: o.autoridades.map(a => ({
        ...a,
        dniFrente: a.dniFrente.map(d => proyectarDniEtapa(d, enAnalisis)),
        dniDorso: a.dniDorso.map(d => proyectarDniEtapa(d, enAnalisis)),
      })),
    }));
  }, [organosData, etapaVista]);

  // Stats globales
  const allAuts = organos.flatMap(o => o.autoridades);
  const totalAuts = allAuts.length;
  let dniOk = 0, dniPending = 0, dniRejected = 0;
  // Se junta la lista de archivos mientras se cuenta, para poder avisar si alguno
  // entró DESPUÉS de que se revisó la sección y todavía nadie lo miró.
  const archivosDni: Array<{ creadoEn: string; estado: string }> = [];
  allAuts.forEach(aut => {
    (['dniFrente', 'dniDorso'] as const).forEach(k => {
      const doc = bestDoc(aut[k]);
      if (!doc) { dniPending++; return; }
      if (doc.creadoEn) archivosDni.push({ creadoEn: doc.creadoEn, estado: doc.estadoRevision });
      // `dniOk` es el contador de REVISADOS: un rechazo también es trabajo hecho.
      if (doc.estadoRevision === 'APROBADO') dniOk++;
      else if (doc.estadoRevision === 'RECHAZADO') { dniOk++; dniRejected++; }
      else dniPending++;
    });
  });
  const avisoNuevosDni = avisoArchivoNuevo(archivosNuevosSinRevisar(
    firmaDeSeccion(filasSeccion ?? [], etapaVista).en,
    archivosDni,
  ));

  // Calcular qué órganos tienen un DNI adjuntado a la respuesta que el supervisor
  // está revisando. Si hay alguno, forzamos la apertura del panel completo y de
  // los órganos correspondientes.
  const markedOrganoIds = new Set<string>();
  const hasAnyMarkedDni = !!markedDocIds && markedDocIds.size > 0 && organos.some(o => {
    const tieneMarcado = o.autoridades.some(a =>
      a.dniFrente.some(d => markedDocIds.has(d.id)) ||
      a.dniDorso.some(d => markedDocIds.has(d.id))
    );
    if (tieneMarcado) markedOrganoIds.add(o.id);
    return tieneMarcado;
  });

  useEffect(() => {
    if (hasAnyMarkedDni) setIsOuterOpen(true);
  }, [hasAnyMarkedDni]);

  useEffect(() => {
    if (markedOrganoIds.size === 0) return;
    setExpandedOrganos(prev => {
      const next = { ...prev };
      markedOrganoIds.forEach(id => { next[id] = true; });
      return next;
    });
  }, [markedOrganoIds.size]);

  const isOrganoOpen = (id: string) => expandedOrganos[id] === true || markedOrganoIds.has(id);
  const toggleOrgano = (id: string) => setExpandedOrganos(prev => ({ ...prev, [id]: !isOrganoOpen(id) }));

  const loadPreviewUrl = async (docId: string) => {
    try {
      const response = await documentoService.getUrl(docId);
      const url = response.data.data?.url || response.data.url;
      return url as string;
    } catch {
      return null;
    }
  };

  const openModal = async (autoridad: AutoridadDNI, side: 'frente' | 'dorso') => {
    const docs = side === 'frente' ? autoridad.dniFrente : autoridad.dniDorso;
    const idx = bestDocIdx(docs);
    const doc = docs[idx] ?? null;
    setModal({ isOpen: true, autoridad, side, docIdx: idx, previewUrl: null, loadingUrl: !!doc });
    setMotivoAccion(null);
    setMotivoTexto('');
    if (doc) {
      const url = await loadPreviewUrl(doc.id);
      setModal(prev => ({ ...prev, previewUrl: url, loadingUrl: false }));
    }
  };

  const switchSide = async (side: 'frente' | 'dorso') => {
    if (!modal.autoridad) return;
    const docs = side === 'frente' ? modal.autoridad.dniFrente : modal.autoridad.dniDorso;
    const idx = bestDocIdx(docs);
    const doc = docs[idx] ?? null;
    setModal(prev => ({ ...prev, side, docIdx: idx, previewUrl: null, loadingUrl: !!doc }));
    setMotivoAccion(null);
    setMotivoTexto('');
    if (doc) {
      const url = await loadPreviewUrl(doc.id);
      setModal(prev => ({ ...prev, previewUrl: url, loadingUrl: false }));
    }
  };

  const switchDocIdx = async (idx: number) => {
    if (!modal.autoridad) return;
    const docs = modal.side === 'frente' ? modal.autoridad.dniFrente : modal.autoridad.dniDorso;
    const doc = docs[idx] ?? null;
    setModal(prev => ({ ...prev, docIdx: idx, previewUrl: null, loadingUrl: !!doc }));
    setMotivoAccion(null);
    setMotivoTexto('');
    if (doc) {
      const url = await loadPreviewUrl(doc.id);
      setModal(prev => ({ ...prev, previewUrl: url, loadingUrl: false }));
    }
  };

  const closeModal = () => {
    setModal({ isOpen: false, autoridad: null, side: 'frente', docIdx: 0, previewUrl: null, loadingUrl: false });
    setMotivoAccion(null);
    setMotivoTexto('');
  };

  const getCurrentDoc = (): DocDNI | null => {
    if (!modal.autoridad) return null;
    const docs = modal.side === 'frente' ? modal.autoridad.dniFrente : modal.autoridad.dniDorso;
    return docs[modal.docIdx] ?? null;
  };

  // Pide el motivo antes de aplicar el cambio; con `motivoAccion` ya abierto,
  // confirma. El motivo vacío se manda null y limpia el anterior.
  const aplicarEstado = (estado: EstadoRevision) => {
    if (motivoAccion !== estado) { setMotivoAccion(estado); setMotivoTexto(''); return; }
    const doc = getCurrentDoc();
    if (!doc) return;
    const texto = motivoTexto.trim();
    if (MOTIVO_ACCION[estado].obligatorio && !texto) return;
    onSetEstado(doc.id, estado, texto || null);
    queryClient.invalidateQueries({ queryKey });
    closeModal();
  };

  const handleVerificar = () => aplicarEstado('APROBADO');

  const handleRechazar = () => aplicarEstado('RECHAZADO');

  const handleMarcarPendiente = () => aplicarEstado('PENDIENTE');

  const handleDescargar = () => {
    const doc = getCurrentDoc();
    if (doc) documentoService.download(doc.id);
  };

  const currentDoc = getCurrentDoc();
  const currentEstado = currentDoc?.estadoRevision;
  const isImagePreview = !currentDoc?.mimeType || currentDoc.mimeType.startsWith('image/');

  const totalDni = totalAuts * 2;

  return (
    <>
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <button
          onClick={() => setIsOuterOpen(o => !o)}
          className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 text-left"
        >
          <span className="flex items-center gap-3 min-w-0">
            <CheckSeccionTipo
              filas={filasSeccion ?? []}
              dimension={etapaVista}
              sinRevisar={totalDni - dniOk}
              puedeEditar={canEdit && !!onAprobarSeccion}
              onCambiar={(e) => onAprobarSeccion?.(e)}
            />
            <span className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
              {titulo ?? 'Documentación de Autoridades'} ({totalAuts} autoridades)
            </span>
            {avisoNuevosDni && (
              <span className="inline-flex items-center gap-1.5 rounded-md bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800 whitespace-nowrap">
                <span aria-hidden>●</span> {avisoNuevosDni}
              </span>
            )}
          </span>
          <div className="flex items-center gap-3">
            <span className="text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap bg-gray-100 text-gray-600 border border-gray-200">
              {dniOk} / {totalDni} revisados
            </span>
            <svg className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isOuterOpen ? 'rotate-180' : ''}`}
              fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </button>

        {isOuterOpen && (filasSeccion && filasSeccion.length > 0 && onGuardarNotaSeccion && canEdit) && (
          <div className="px-6 py-3 border-t border-gray-100">
            <p className="text-[11px] font-medium text-gray-600 mb-1">Nota de la sección</p>
            <CuadroNota
              valor={notaSeccion ?? ''}
              puedeEditar={canEdit}
              placeholder="Nota…"
              onGuardar={async (nota) => { onGuardarNotaSeccion(nota); }}
            />
            {(() => {
              const firma = firmaDeSeccion(filasSeccion, etapaVista);
              const est = estadoDeSeccion(filasSeccion, etapaVista);
              if (!firma.por || est === 'PENDIENTE') return null;
              return (
                <p className="text-[11px] text-gray-400 mt-1">
                  {est === 'APROBADO' ? 'Sección aprobada' : 'Sección rechazada'} por {firma.por}
                  {firma.en && <> · {fmtDate(firma.en)}</>}
                </p>
              );
            })()}
          </div>
        )}

        {isOuterOpen && (isLoading ? (
          <div className="flex justify-center py-8 border-t border-gray-100">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
          </div>
        ) : organos.length === 0 ? (
          <p className="px-6 py-4 text-sm text-gray-400 border-t border-gray-100">Sin órganos configurados.</p>
        ) : (
          <div className="border-t border-gray-100">
            {organos.map(organo => {
              const isOpen = isOrganoOpen(organo.id);
              const orgCount = organo.autoridades.reduce((acc, aut) => {
                if (bestDoc(aut.dniFrente)?.estadoRevision === 'APROBADO') acc.ok++;
                else acc.pending++;
                if (bestDoc(aut.dniDorso)?.estadoRevision === 'APROBADO') acc.ok++;
                else acc.pending++;
                return acc;
              }, { ok: 0, pending: 0 });
              const totalDniOrgano = organo.autoridades.length * 2;

              return (
                <div key={organo.id} className="border-b border-gray-100 last:border-0">
                  {/* Header del órgano */}
                  <button
                    onClick={() => toggleOrgano(organo.id)}
                    className={`w-full px-5 py-3 flex items-center justify-between hover:bg-gray-50 text-left border-l-4 ${organo.esObligatorio ? 'border-blue-500' : 'border-gray-300'}`}
                  >
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-bold text-gray-800 uppercase tracking-wide">
                          {organo.denominacionPersonalizada || organo.denominacion}
                        </span>
                        <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full ${
                          organo.esObligatorio ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-500'
                        }`}>
                          {organo.esObligatorio ? 'OBLIGATORIO' : 'OPCIONAL'}
                        </span>
                      </div>
                      {(organo.fechaMandatoDesde || organo.fechaMandatoHasta) && (
                        <p className="text-xs text-gray-400 mt-0.5">
                          {organo.fechaMandatoDesde ? new Date(organo.fechaMandatoDesde).toLocaleDateString('es-AR') : '—'}
                          {organo.fechaMandatoHasta ? ` → ${new Date(organo.fechaMandatoHasta).toLocaleDateString('es-AR')}` : ''}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-gray-500">{orgCount.ok}/{totalDniOrgano} DNIs</span>
                      <span className="text-xs text-gray-400">{organo.autoridades.length} aut.</span>
                      <svg className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                        fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </button>

                  {/* Autoridades del órgano */}
                  {isOpen && (
                    <div>
                      {organo.autoridades.length === 0 ? (
                        <p className="px-6 py-3 text-xs text-gray-400">Sin autoridades.</p>
                      ) : (() => {
                        const titulares = organo.autoridades.filter(a => a.mandato?.esTitular !== false);
                        const suplentes = organo.autoridades.filter(a => a.mandato?.esTitular === false);
                        const renderAut = (aut: AutoridadDNI, i: number) => {
                          const frenteMarcado = !!markedDocIds && aut.dniFrente.some(d => markedDocIds.has(d.id));
                          const dorsoMarcado = !!markedDocIds && aut.dniDorso.some(d => markedDocIds.has(d.id));
                          const filaMarcada = frenteMarcado || dorsoMarcado;
                          return (
                          <div
                            key={aut.id}
                            data-doc-id={
                              frenteMarcado ? aut.dniFrente.find(d => markedDocIds!.has(d.id))?.id :
                              dorsoMarcado ? aut.dniDorso.find(d => markedDocIds!.has(d.id))?.id :
                              undefined
                            }
                            className={`flex items-center gap-3 px-6 py-2.5 border-b border-gray-50 ${
                              filaMarcada ? 'bg-yellow-50 hover:bg-yellow-100 border-l-4 border-l-yellow-400' : 'hover:bg-gray-50'
                            }`}
                          >
                            <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                              <span className="text-xs font-bold text-gray-500">{i + 1}</span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-semibold text-gray-800">
                                {aut.mandato?.cargo}
                                {filaMarcada && (
                                  <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded bg-yellow-200 text-yellow-900 text-[10px] font-semibold uppercase tracking-wide">
                                    Adjuntó a respuesta
                                  </span>
                                )}
                              </p>
                              <p className="text-xs text-gray-600">
                                {aut.nombre} {aut.apellido}
                                <span className="text-gray-400 ml-1">· DNI {aut.dni}</span>
                              </p>
                            </div>
                            <div className="flex gap-1.5 flex-shrink-0">
                              <DniBadge
                                docs={aut.dniFrente}
                                lado="Frente"
                                onClick={() => openModal(aut, 'frente')}
                                isMarked={frenteMarcado}
                              />
                              <DniBadge
                                docs={aut.dniDorso}
                                lado="Dorso"
                                onClick={() => openModal(aut, 'dorso')}
                                isMarked={dorsoMarcado}
                              />
                            </div>
                          </div>
                          );
                        };
                        return (
                          <>
                            {titulares.length > 0 && (
                              <>
                                <div className="px-6 py-1.5 bg-gray-50 border-b border-gray-100 border-t border-gray-100">
                                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Titulares</span>
                                </div>
                                {titulares.map((a, i) => renderAut(a, i))}
                              </>
                            )}
                            {suplentes.length > 0 && (
                              <>
                                <div className="px-6 py-1.5 bg-gray-50 border-b border-gray-100 border-t border-gray-200">
                                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Suplentes</span>
                                </div>
                                {suplentes.map((a, i) => renderAut(a, i))}
                              </>
                            )}
                            {titulares.length === 0 && suplentes.length === 0 && (
                              organo.autoridades.map((a, i) => renderAut(a, i))
                            )}
                          </>
                        );
                      })()}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Modal previsualizador de DNI */}
      <Modal
        isOpen={modal.isOpen}
        onClose={closeModal}
        title={`DNI ${modal.side === 'frente' ? 'Frente' : 'Dorso'} — ${modal.autoridad?.nombre} ${modal.autoridad?.apellido}`}
        size="xl"
      >
        <div className="space-y-4">
          {/* Info de la autoridad */}
          <div className="bg-gray-50 rounded-lg px-4 py-3">
            <div className="flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900">
                  {modal.autoridad?.mandato?.cargo || 'Sin cargo'}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  DNI {modal.autoridad?.dni}
                  {modal.autoridad?.fechaNacimiento && ` · ${new Date(modal.autoridad.fechaNacimiento).toLocaleDateString('es-AR')}`}
                </p>
              </div>
              {currentDoc && (
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full flex-shrink-0 ${
                  currentEstado === 'APROBADO' ? 'bg-green-100 text-green-700' :
                  currentEstado === 'RECHAZADO' ? 'bg-red-100 text-red-700' :
                  'bg-amber-100 text-amber-700'
                }`}>
                  {estadoDniLabel(currentEstado)}
                </span>
              )}
            </div>
            {currentDoc?.descripcion && (
              <p className="text-xs text-gray-500 mt-2 border-t border-gray-200 pt-2">{currentDoc.descripcion}</p>
            )}
            {/* El motivo se muestra en cualquiera de los tres estados: un DNI
                verificado o reabierto también puede tener uno escrito. Va en su
                propio renglón y no dentro de la pastilla de estado: ahí entran
                hasta 200 000 caracteres y deformaban el encabezado del modal. */}
            {currentDoc?.observacion && (
              <div className="mt-2 border-t border-gray-200 pt-2">
                <MotivoDoc doc={{ estadoRevision: currentEstado ?? 'PENDIENTE', observacion: currentDoc.observacion }} />
              </div>
            )}
          </div>

          {/* Tabs frente/dorso */}
          <div className="flex border-b border-gray-200">
            {(['frente', 'dorso'] as const).map(s => (
              <button
                key={s}
                type="button"
                onClick={() => switchSide(s)}
                className={`px-5 py-2 text-sm font-medium border-b-2 transition-colors ${
                  modal.side === s
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {s === 'frente' ? 'Frente' : 'Dorso'}
              </button>
            ))}
          </div>

          {/* Selector de versión si hay múltiples docs para este lado */}
          {(() => {
            const docs = modal.side === 'frente' ? modal.autoridad?.dniFrente : modal.autoridad?.dniDorso;
            if (!docs || docs.length <= 1) return null;
            const labels: Record<string, string> = { APROBADO: 'Aprobado', RECHAZADO: 'Rechazado', PENDIENTE: 'Pendiente' };
            return (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-gray-500 font-medium">Versiones:</span>
                {docs.map((doc, idx) => (
                  <button
                    key={doc.id}
                    type="button"
                    onClick={() => switchDocIdx(idx)}
                    className={`px-2.5 py-1 text-xs rounded-lg border font-medium transition-colors ${
                      modal.docIdx === idx
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    #{idx + 1} · {labels[doc.estadoRevision] ?? doc.estadoRevision}
                  </button>
                ))}
              </div>
            );
          })()}

          {/* Área de previsualización */}
          <div className="bg-gray-100 rounded-lg overflow-hidden" style={{ height: '420px' }}>
            {modal.loadingUrl ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
              </div>
            ) : !currentDoc ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-400">
                <svg className="w-12 h-12 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="text-sm font-medium">Documento no cargado</p>
                <p className="text-xs mt-1">El {modal.side === 'frente' ? 'frente' : 'dorso'} del DNI aún no fue subido</p>
              </div>
            ) : modal.previewUrl ? (
              isImagePreview ? (
                <ZoomableImage src={modal.previewUrl} alt={`DNI ${modal.side}`} />
              ) : (
                <iframe src={modal.previewUrl} className="w-full h-full border-0" title={`DNI ${modal.side}`} />
              )
            ) : (
              <div className="flex items-center justify-center h-full text-gray-400">
                <p className="text-sm">No se pudo cargar la vista previa</p>
              </div>
            )}
          </div>

          {/* Motivo del cambio de estado (verificar / rechazar / volver a pendiente) */}
          {motivoAccion && (
            <MotivoModalCampo
              accion={motivoAccion}
              texto={motivoTexto}
              setTexto={setMotivoTexto}
              onConfirmar={() => aplicarEstado(motivoAccion)}
              onCancelar={() => { setMotivoAccion(null); setMotivoTexto(''); }}
            />
          )}

          {/* Acciones */}
          <div className="flex items-center justify-between pt-2 border-t border-gray-100">
            <div className="flex gap-2">
              {currentDoc && (
                <button
                  type="button"
                  onClick={handleDescargar}
                  className="px-3 py-1.5 text-xs border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-600"
                >
                  Descargar
                </button>
              )}
            </div>
            {canEdit && currentDoc && (
              motivoAccion ? (
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => { setMotivoAccion(null); setMotivoTexto(''); }}
                    className="px-4 py-1.5 text-xs border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-600"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={() => aplicarEstado(motivoAccion)}
                    disabled={MOTIVO_ACCION[motivoAccion].obligatorio && !motivoTexto.trim()}
                    className={`px-4 py-1.5 text-xs text-white rounded-lg font-medium disabled:opacity-40 disabled:cursor-not-allowed ${MOTIVO_ACCION[motivoAccion].botonClase}`}
                  >
                    {MOTIVO_ACCION[motivoAccion].botonConfirmar}
                  </button>
                </div>
              ) : currentEstado === 'PENDIENTE' ? (
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleRechazar}
                    className="px-4 py-1.5 text-xs bg-red-50 text-red-600 border border-red-300 rounded-lg font-medium hover:bg-red-100"
                  >
                    Rechazar
                  </button>
                  <button
                    type="button"
                    onClick={handleVerificar}
                    className="px-4 py-1.5 text-xs bg-green-600 text-white rounded-lg font-medium hover:bg-green-700"
                  >
                    Verificar
                  </button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleMarcarPendiente}
                    className="px-4 py-1.5 text-xs border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-600"
                  >
                    Marcar pendiente
                  </button>
                  {currentEstado === 'APROBADO' ? (
                    <button
                      type="button"
                      onClick={handleRechazar}
                      className="px-4 py-1.5 text-xs bg-red-50 text-red-600 border border-red-300 rounded-lg font-medium hover:bg-red-100"
                    >
                      Rechazar
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={handleVerificar}
                      className="px-4 py-1.5 text-xs bg-green-600 text-white rounded-lg font-medium hover:bg-green-700"
                    >
                      Verificar
                    </button>
                  )}
                </div>
              )
            )}
          </div>
        </div>
      </Modal>
    </>
  );
}

// =============================================================================
// SeccionDNIApoderados — igual vista que autoridades pero lista plana (sin órganos)
// =============================================================================

interface ApoderadoDNI {
  id: string;
  dni: string;
  nombre: string;
  apellido: string;
  tipo: string;
  esIniciador: boolean;
  activo: boolean;
  dniFrente: DocDNI[];
  dniDorso: DocDNI[];
}

function SeccionDNIApoderados({
  partidoId,
  expedienteId,
  canEdit,
  onSetEstado,
  markedDocIds,
  etapaVista,
  titulo,
  labelPersona,
  defaultOpen,
  fuente = 'apoderados',
  filasSeccion,
  notaSeccion,
  onAprobarSeccion,
  onGuardarNotaSeccion,
}: {
  partidoId: string;
  /** Expediente en pantalla. Suma los archivos cargados contra el expediente y no
   *  contra el partido; sin él faltaban los que el lado interno sí veía. */
  expedienteId?: string;
  canEdit: boolean;
  onSetEstado: (docId: string, estado: EstadoRevision, observacion?: string | null) => void;
  markedDocIds?: Set<string>;
  etapaVista: 'ADMISIBILIDAD' | 'ANALISIS';
  titulo?: string;
  labelPersona?: string;
  defaultOpen?: boolean;
  // Fuente de datos: apoderados del partido (default) o el iniciador (Usuario).
  fuente?: 'apoderados' | 'iniciador';
  // Aprobación POR SECCIÓN: un check para frente y dorso. Sirve igual para la
  // sección de iniciadores (reconocimiento), donde quien presenta todavía no es
  // apoderado. Sin filas sembradas, la sección se comporta como siempre.
  filasSeccion?: FilaSeccion[];
  notaSeccion?: string | null;
  onAprobarSeccion?: (estado: EstadoRevision) => void;
  onGuardarNotaSeccion?: (nota: string) => void;
}) {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(defaultOpen ?? false);
  const [modal, setModal] = useState<{
    isOpen: boolean;
    apoderado: ApoderadoDNI | null;
    side: 'frente' | 'dorso';
    docIdx: number;
    previewUrl: string | null;
    loadingUrl: boolean;
  }>({ isOpen: false, apoderado: null, side: 'frente', docIdx: 0, previewUrl: null, loadingUrl: false });
  // Acción a la espera de motivo (null = ninguna). Los tres estados lo piden;
  // sólo el rechazo exige texto.
  const [motivoAccion, setMotivoAccion] = useState<EstadoRevision | null>(null);
  const [motivoTexto, setMotivoTexto] = useState('');

  const esIniciadorFuente = fuente === 'iniciador';
  // `expedienteId` entra en la clave: los archivos que devuelve el endpoint dependen
  // de él, así que dos expedientes distintos no pueden compartir la misma entrada.
  const queryKey = [esIniciadorFuente ? 'iniciador-dni' : 'apoderados-dni', partidoId, expedienteId ?? null];
  const { data: apoderadosData = [], isLoading } = useQuery<ApoderadoDNI[]>({
    queryKey,
    queryFn: async () => {
      const response = esIniciadorFuente
        ? await partidoService.getIniciadorDni(partidoId)
        : await partidoService.getApoderadosDni(partidoId, expedienteId);
      return response.data.data as ApoderadoDNI[];
    },
    enabled: !!partidoId,
  });

  // Proyecta el estado de cada DNI según la etapa activa (admisibilidad/análisis).
  const apoderados = useMemo(() => {
    const enAnalisis = etapaVista === 'ANALISIS';
    return apoderadosData.map(ap => ({
      ...ap,
      dniFrente: ap.dniFrente.map(d => proyectarDniEtapa(d, enAnalisis)),
      dniDorso: ap.dniDorso.map(d => proyectarDniEtapa(d, enAnalisis)),
    }));
  }, [apoderadosData, etapaVista]);

  let dniOk = 0, dniPending = 0, dniRejected = 0;
  // Se junta la lista de archivos mientras se cuenta, para poder avisar si alguno
  // entró DESPUÉS de que se revisó la sección y todavía nadie lo miró.
  const archivosDni: Array<{ creadoEn: string; estado: string }> = [];
  apoderados.forEach(ap => {
    (['dniFrente', 'dniDorso'] as const).forEach(k => {
      const doc = bestDoc(ap[k]);
      if (!doc) { dniPending++; return; }
      if (doc.creadoEn) archivosDni.push({ creadoEn: doc.creadoEn, estado: doc.estadoRevision });
      // `dniOk` es el contador de REVISADOS: un rechazo también es trabajo hecho.
      if (doc.estadoRevision === 'APROBADO') dniOk++;
      else if (doc.estadoRevision === 'RECHAZADO') { dniOk++; dniRejected++; }
      else dniPending++;
    });
  });
  const avisoNuevosDni = avisoArchivoNuevo(archivosNuevosSinRevisar(
    firmaDeSeccion(filasSeccion ?? [], etapaVista).en,
    archivosDni,
  ));

  // Si hay DNIs de apoderado adjuntados a una respuesta que el supervisor revisa,
  // forzamos la apertura del panel.
  const hasAnyMarkedDni = !!markedDocIds && markedDocIds.size > 0 && apoderados.some(ap =>
    ap.dniFrente.some(d => markedDocIds.has(d.id)) ||
    ap.dniDorso.some(d => markedDocIds.has(d.id))
  );
  useEffect(() => {
    if (hasAnyMarkedDni) setIsOpen(true);
  }, [hasAnyMarkedDni]);

  const loadPreviewUrl = async (docId: string) => {
    try {
      const response = await documentoService.getUrl(docId);
      return (response.data.data?.url || response.data.url) as string;
    } catch { return null; }
  };

  const openModal = async (apoderado: ApoderadoDNI, side: 'frente' | 'dorso') => {
    const docs = side === 'frente' ? apoderado.dniFrente : apoderado.dniDorso;
    const idx = bestDocIdx(docs);
    const doc = docs[idx] ?? null;
    setModal({ isOpen: true, apoderado, side, docIdx: idx, previewUrl: null, loadingUrl: !!doc });
    setMotivoAccion(null); setMotivoTexto('');
    if (doc) {
      const url = await loadPreviewUrl(doc.id);
      setModal(prev => ({ ...prev, previewUrl: url, loadingUrl: false }));
    }
  };

  const switchSide = async (side: 'frente' | 'dorso') => {
    if (!modal.apoderado) return;
    const docs = side === 'frente' ? modal.apoderado.dniFrente : modal.apoderado.dniDorso;
    const idx = bestDocIdx(docs);
    const doc = docs[idx] ?? null;
    setModal(prev => ({ ...prev, side, docIdx: idx, previewUrl: null, loadingUrl: !!doc }));
    setMotivoAccion(null); setMotivoTexto('');
    if (doc) {
      const url = await loadPreviewUrl(doc.id);
      setModal(prev => ({ ...prev, previewUrl: url, loadingUrl: false }));
    }
  };

  const switchDocIdx = async (idx: number) => {
    if (!modal.apoderado) return;
    const docs = modal.side === 'frente' ? modal.apoderado.dniFrente : modal.apoderado.dniDorso;
    const doc = docs[idx] ?? null;
    setModal(prev => ({ ...prev, docIdx: idx, previewUrl: null, loadingUrl: !!doc }));
    setMotivoAccion(null); setMotivoTexto('');
    if (doc) {
      const url = await loadPreviewUrl(doc.id);
      setModal(prev => ({ ...prev, previewUrl: url, loadingUrl: false }));
    }
  };

  const closeModal = () => {
    setModal({ isOpen: false, apoderado: null, side: 'frente', docIdx: 0, previewUrl: null, loadingUrl: false });
    setMotivoAccion(null); setMotivoTexto('');
  };

  const getCurrentDoc = (): DocDNI | null => {
    if (!modal.apoderado) return null;
    const docs = modal.side === 'frente' ? modal.apoderado.dniFrente : modal.apoderado.dniDorso;
    return docs[modal.docIdx] ?? null;
  };

  // Pide el motivo antes de aplicar el cambio; con `motivoAccion` ya abierto,
  // confirma. El motivo vacío se manda null y limpia el anterior.
  const aplicarEstado = (estado: EstadoRevision) => {
    if (motivoAccion !== estado) { setMotivoAccion(estado); setMotivoTexto(''); return; }
    const doc = getCurrentDoc();
    if (!doc) return;
    const texto = motivoTexto.trim();
    if (MOTIVO_ACCION[estado].obligatorio && !texto) return;
    onSetEstado(doc.id, estado, texto || null);
    queryClient.invalidateQueries({ queryKey });
    closeModal();
  };

  const handleVerificar = () => aplicarEstado('APROBADO');

  const handleMarcarPendienteAp = () => aplicarEstado('PENDIENTE');

  const handleRechazar = () => aplicarEstado('RECHAZADO');

  const currentDoc = getCurrentDoc();
  const currentEstado = currentDoc?.estadoRevision;
  const isImagePreview = !currentDoc?.mimeType || currentDoc.mimeType.startsWith('image/');
  const allDocs = apoderados.flatMap(a => [...a.dniFrente, ...a.dniDorso]);

  const totalApDni = apoderados.length * 2;

  return (
    <>
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <button
          onClick={() => setIsOpen(o => !o)}
          className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 text-left"
        >
          <span className="flex items-center gap-3 min-w-0">
            <CheckSeccionTipo
              filas={filasSeccion ?? []}
              dimension={etapaVista}
              sinRevisar={totalApDni - dniOk}
              puedeEditar={canEdit && !!onAprobarSeccion}
              onCambiar={(e) => onAprobarSeccion?.(e)}
            />
            <span className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
              {titulo ?? 'Documentación de Apoderados'}{esIniciadorFuente ? '' : ` (${apoderados.length} apoderados)`}
            </span>
            {avisoNuevosDni && (
              <span className="inline-flex items-center gap-1.5 rounded-md bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800 whitespace-nowrap">
                <span aria-hidden>●</span> {avisoNuevosDni}
              </span>
            )}
          </span>
          <div className="flex items-center gap-3">
            <span className="text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap bg-gray-100 text-gray-600 border border-gray-200">
              {dniOk} / {totalApDni} revisados
            </span>
            <svg className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
              fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </button>

        {isOpen && filasSeccion && filasSeccion.length > 0 && onGuardarNotaSeccion && canEdit && (
          <div className="px-6 py-3 border-t border-gray-100">
            <p className="text-[11px] font-medium text-gray-600 mb-1">Nota de la sección</p>
            <CuadroNota
              valor={notaSeccion ?? ''}
              puedeEditar={canEdit}
              placeholder="Nota…"
              onGuardar={async (nota) => { onGuardarNotaSeccion(nota); }}
            />
            {(() => {
              const firma = firmaDeSeccion(filasSeccion, etapaVista);
              const est = estadoDeSeccion(filasSeccion, etapaVista);
              if (!firma.por || est === 'PENDIENTE') return null;
              return (
                <p className="text-[11px] text-gray-400 mt-1">
                  {est === 'APROBADO' ? 'Sección aprobada' : 'Sección rechazada'} por {firma.por}
                  {firma.en && <> · {fmtDate(firma.en)}</>}
                </p>
              );
            })()}
          </div>
        )}

        {isOpen && (
          <div className="border-t border-gray-100">
            {isLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
              </div>
            ) : apoderados.length === 0 ? (
              <p className="px-6 py-4 text-sm text-gray-400">{esIniciadorFuente ? 'Sin DNI del iniciador cargado.' : 'Sin apoderados registrados.'}</p>
            ) : (
              <div className="divide-y divide-gray-50">
                {apoderados.map((ap, i) => {
                  const frenteMarcado = !!markedDocIds && ap.dniFrente.some(d => markedDocIds.has(d.id));
                  const dorsoMarcado = !!markedDocIds && ap.dniDorso.some(d => markedDocIds.has(d.id));
                  const filaMarcada = frenteMarcado || dorsoMarcado;
                  return (
                  <div
                    key={ap.id}
                    data-doc-id={
                      frenteMarcado ? ap.dniFrente.find(d => markedDocIds!.has(d.id))?.id :
                      dorsoMarcado ? ap.dniDorso.find(d => markedDocIds!.has(d.id))?.id :
                      undefined
                    }
                    className={`flex items-center gap-3 px-6 py-2.5 ${
                      filaMarcada ? 'bg-yellow-50 hover:bg-yellow-100 border-l-4 border-l-yellow-400' : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-bold text-gray-500">{i + 1}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-gray-800">
                        {labelPersona ?? 'APODERADO FIRMANTE'}
                        {ap.esIniciador && <span className="ml-1.5 text-blue-500">(Iniciador)</span>}
                        {!ap.activo && <span className="ml-1.5 text-gray-400">(Inactivo)</span>}
                        {filaMarcada && (
                          <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded bg-yellow-200 text-yellow-900 text-[10px] font-semibold uppercase tracking-wide">
                            Adjuntó a respuesta
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-gray-600">
                        {ap.nombre} {ap.apellido}
                        <span className="text-gray-400 ml-1">· DNI {ap.dni}</span>
                      </p>
                    </div>
                    <div className="flex gap-1.5 flex-shrink-0">
                      <DniBadge
                        docs={ap.dniFrente}
                        lado="Frente"
                        onClick={() => openModal(ap, 'frente')}
                        isMarked={frenteMarcado}
                      />
                      <DniBadge
                        docs={ap.dniDorso}
                        lado="Dorso"
                        onClick={() => openModal(ap, 'dorso')}
                        isMarked={dorsoMarcado}
                      />
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal previsualizador */}
      <Modal
        isOpen={modal.isOpen}
        onClose={closeModal}
        title={`DNI ${modal.side === 'frente' ? 'Frente' : 'Dorso'} — ${modal.apoderado?.nombre} ${modal.apoderado?.apellido}`}
        size="xl"
      >
        <div className="space-y-4">
          <div className="bg-gray-50 rounded-lg px-4 py-3">
            <div className="flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900">{labelPersona ?? 'APODERADO FIRMANTE'}</p>
                <p className="text-xs text-gray-500 mt-0.5">DNI {modal.apoderado?.dni}</p>
              </div>
              {currentDoc && (
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full flex-shrink-0 ${
                  currentEstado === 'APROBADO' ? 'bg-green-100 text-green-700' :
                  currentEstado === 'RECHAZADO' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                }`}>
                  {estadoDniLabel(currentEstado)}
                </span>
              )}
            </div>
            {currentDoc?.descripcion && (
              <p className="text-xs text-gray-500 mt-2 border-t border-gray-200 pt-2">{currentDoc.descripcion}</p>
            )}
            {/* El motivo se muestra en cualquiera de los tres estados: un DNI
                verificado o reabierto también puede tener uno escrito. Va en su
                propio renglón y no dentro de la pastilla de estado: ahí entran
                hasta 200 000 caracteres y deformaban el encabezado del modal. */}
            {currentDoc?.observacion && (
              <div className="mt-2 border-t border-gray-200 pt-2">
                <MotivoDoc doc={{ estadoRevision: currentEstado ?? 'PENDIENTE', observacion: currentDoc.observacion }} />
              </div>
            )}
          </div>

          <div className="flex border-b border-gray-200">
            {(['frente', 'dorso'] as const).map(s => (
              <button key={s} type="button" onClick={() => switchSide(s)}
                className={`px-5 py-2 text-sm font-medium border-b-2 transition-colors ${
                  modal.side === s ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}>
                {s === 'frente' ? 'Frente' : 'Dorso'}
              </button>
            ))}
          </div>

          {/* Selector de versión si hay múltiples docs para este lado */}
          {(() => {
            const docs = modal.side === 'frente' ? modal.apoderado?.dniFrente : modal.apoderado?.dniDorso;
            if (!docs || docs.length <= 1) return null;
            const labels: Record<string, string> = { APROBADO: 'Aprobado', RECHAZADO: 'Rechazado', PENDIENTE: 'Pendiente' };
            return (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-gray-500 font-medium">Versiones:</span>
                {docs.map((doc, idx) => (
                  <button
                    key={doc.id}
                    type="button"
                    onClick={() => switchDocIdx(idx)}
                    className={`px-2.5 py-1 text-xs rounded-lg border font-medium transition-colors ${
                      modal.docIdx === idx
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    #{idx + 1} · {labels[doc.estadoRevision] ?? doc.estadoRevision}
                  </button>
                ))}
              </div>
            );
          })()}

          <div className="bg-gray-100 rounded-lg overflow-hidden" style={{ height: '420px' }}>
            {modal.loadingUrl ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
              </div>
            ) : !currentDoc ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-400">
                <svg className="w-12 h-12 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="text-sm font-medium">Documento no cargado</p>
                <p className="text-xs mt-1">El {modal.side === 'frente' ? 'frente' : 'dorso'} del DNI aún no fue subido</p>
              </div>
            ) : modal.previewUrl ? (
              isImagePreview ? (
                <ZoomableImage src={modal.previewUrl} alt={`DNI ${modal.side}`} />
              ) : (
                <iframe src={modal.previewUrl} className="w-full h-full border-0" title={`DNI ${modal.side}`} />
              )
            ) : (
              <div className="flex items-center justify-center h-full text-gray-400">
                <p className="text-sm">No se pudo cargar la vista previa</p>
              </div>
            )}
          </div>

          {motivoAccion && (
            <MotivoModalCampo
              accion={motivoAccion}
              texto={motivoTexto}
              setTexto={setMotivoTexto}
              onConfirmar={() => aplicarEstado(motivoAccion)}
              onCancelar={() => { setMotivoAccion(null); setMotivoTexto(''); }}
            />
          )}

          <div className="flex items-center justify-between pt-2 border-t border-gray-100">
            <div className="flex gap-2">
              {currentDoc && (
                <button type="button" onClick={() => documentoService.download(currentDoc.id)}
                  className="px-3 py-1.5 text-xs border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-600">
                  Descargar
                </button>
              )}
            </div>
            {canEdit && currentDoc && (
              motivoAccion ? (
                <div className="flex gap-2">
                  <button type="button" onClick={() => { setMotivoAccion(null); setMotivoTexto(''); }}
                    className="px-4 py-1.5 text-xs border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-600">
                    Cancelar
                  </button>
                  <button type="button" onClick={() => aplicarEstado(motivoAccion)}
                    disabled={MOTIVO_ACCION[motivoAccion].obligatorio && !motivoTexto.trim()}
                    className={`px-4 py-1.5 text-xs text-white rounded-lg font-medium disabled:opacity-40 disabled:cursor-not-allowed ${MOTIVO_ACCION[motivoAccion].botonClase}`}>
                    {MOTIVO_ACCION[motivoAccion].botonConfirmar}
                  </button>
                </div>
              ) : currentEstado === 'PENDIENTE' ? (
                <div className="flex gap-2">
                  <button type="button" onClick={handleRechazar}
                    className="px-4 py-1.5 text-xs bg-red-50 text-red-600 border border-red-300 rounded-lg font-medium hover:bg-red-100">
                    Rechazar
                  </button>
                  <button type="button" onClick={handleVerificar}
                    className="px-4 py-1.5 text-xs bg-green-600 text-white rounded-lg font-medium hover:bg-green-700">
                    Verificar
                  </button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <button type="button" onClick={handleMarcarPendienteAp}
                    className="px-4 py-1.5 text-xs border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-600">
                    Marcar pendiente
                  </button>
                  {currentEstado === 'APROBADO' ? (
                    <button type="button" onClick={handleRechazar}
                      className="px-4 py-1.5 text-xs bg-red-50 text-red-600 border border-red-300 rounded-lg font-medium hover:bg-red-100">
                      Rechazar
                    </button>
                  ) : (
                    <button type="button" onClick={handleVerificar}
                      className="px-4 py-1.5 text-xs bg-green-600 text-white rounded-lg font-medium hover:bg-green-700">
                      Verificar
                    </button>
                  )}
                </div>
              )
            )}
          </div>
        </div>
      </Modal>
    </>
  );
}

// =============================================================================
// Página principal
// =============================================================================

const ROLES_INTERNOS = ['ADMINISTRADOR', 'SUPERVISOR', 'AUTORIDAD_FIRMANTE', 'ANALISTA', 'MESA_ENTRADAS', 'AUDITOR'];
const ROLES_PUEDEN_REVISAR = ['ADMINISTRADOR', 'SUPERVISOR', 'AUTORIDAD_FIRMANTE', 'ANALISTA', 'MESA_ENTRADAS'];

export default function DocumentosPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const isExterno = !ROLES_INTERNOS.includes(user?.rol || '');
  const canRevisarPorRol = ROLES_PUEDEN_REVISAR.includes(user?.rol || '');

  const [partidoFilter, setPartidoFilter] = useState(searchParams.get('partidoId') || '');
  // Vista por pestañas (solo internos): "resumen" muestra la tabla de todos los
  // partidos; "detalle" la vista de documentos del partido/expediente elegido.
  // Si se entra con un partido/expediente en la URL, arranca en "detalle".
  const [vista, setVista] = useState<'resumen' | 'detalle'>(
    (searchParams.get('partidoId') || searchParams.get('expedienteId') || searchParams.get('revisarObs'))
      ? 'detalle' : 'resumen',
  );
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  // Filtro de expediente: arranca desde el query param y puede cambiarse por dropdown
  const [expedienteFilter, setExpedienteFilter] = useState(searchParams.get('expedienteId') || '');

  // Modo "revisar respuesta a observación": cuando el supervisor entra desde el
  // botón "Revisar en Documentos" del expediente, viene con ?revisarObs=obsId
  // y se activa: banner con conteos, highlight amarillo y auto-scroll al siguiente
  // doc pendiente después de cada acción.
  const revisarObsId = searchParams.get('revisarObs') || '';

  // Llegada desde el tablero de documentación del expediente: ?abrirTipo=<TIPO>
  // abre el acordeón de ese tipo y scrollea hasta él. La barra de retorno vuelve
  // a la MISMA fila del tablero (ancla #doc-<TIPO>).
  const abrirTipo = searchParams.get('abrirTipo') || '';
  const revisarObsExpedienteId = searchParams.get('expedienteId') || '';

  const [showUploadModal, setShowUploadModal] = useState(false);
  // RECONOCIMIENTO: cuando se sube desde una fila obligatoria, el tipo queda fijo.
  const [tipoBloqueado, setTipoBloqueado] = useState(false);
  const [uploadData, setUploadData] = useState({
    tipo: '',
    titulo: '',
    descripcion: '',
    partidoId: user?.partidoId || '',
    expedienteId: searchParams.get('expedienteId') || '',
    dniPersona: '',
  });

  // Panel de observación unificada
  const [showObsPanel, setShowObsPanel] = useState(false);
  const [obsText, setObsText] = useState('');
  const [obsCopied, setObsCopied] = useState(false);

  const [dniCheckLoading, setDniCheckLoading] = useState(false);
  const [dniCheckResult, setDniCheckResult] = useState<{
    frente: { path: string; titulo: string; url: string } | null;
    dorso: { path: string; titulo: string; url: string } | null;
    persona: { nombre: string; apellido: string } | null;
  } | null>(null);
  const [dniReemplazar, setDniReemplazar] = useState(false);

  // Query documentos
  const { data, isLoading } = useQuery({
    queryKey: ['documentos', partidoFilter, fechaDesde, fechaHasta, user?.partidoId, expedienteFilter],
    queryFn: async () => {
      const params: Record<string, any> = { pageSize: 500 };
      if (isExterno && user?.partidoId) params.partidoId = user.partidoId;
      else if (partidoFilter) params.partidoId = partidoFilter;
      if (expedienteFilter) params.expedienteId = expedienteFilter;
      if (fechaDesde) params.fechaDesde = fechaDesde;
      if (fechaHasta) params.fechaHasta = fechaHasta;
      const response = await documentoService.list(params);
      return response.data.data as Documento[];
    },
  });

  // Lista de expedientes del partido activo (para el dropdown del filtro)
  const partidoIdParaExpedientes = isExterno ? user?.partidoId : partidoFilter;
  const { data: expedientesList } = useQuery({
    queryKey: ['expedientes-para-filtro-docs', partidoIdParaExpedientes],
    queryFn: async () => {
      const response = await expedienteService.list({
        partidoId: partidoIdParaExpedientes,
        pageSize: 200,
      });
      return (response.data?.data?.items ?? response.data?.data ?? []) as Array<{
        id: string;
        numero: string;
        tipo: string;
      }>;
    },
    enabled: !!partidoIdParaExpedientes,
  });

  // Si el partido tiene un único expediente, seleccionarlo automáticamente
  // (evita que un reconocimiento con un solo expediente muestre el checklist de
  // reempadronamiento por defecto al estar en "Todos los expedientes").
  useEffect(() => {
    if (!expedienteFilter && expedientesList && expedientesList.length === 1) {
      setExpedienteFilter(expedientesList[0].id);
    }
  }, [expedientesList, expedienteFilter]);

  const partidoActivoId = isExterno ? user?.partidoId : partidoFilter;

  // Si hay filtro por expediente, cargo el expediente para conocer su tipo/estado
  // (y, si es de rendición, qué tipo de PDF firmado corresponde).
  const { data: expedienteActivo } = useQuery({
    queryKey: ['expediente-filtro-docs', expedienteFilter],
    queryFn: async () => {
      const response = await expedienteService.getById(expedienteFilter);
      return (response.data?.data ?? response.data) as {
        id: string;
        tipo: string;
        estado?: string;
        // Agrupación DUEÑA del expediente. En una alianza no es el partido del
        // usuario: el apoderado de un integrante tiene su propio partido en la
        // sesión, y sin esto la pantalla se anclaba en el partido equivocado.
        partidoId?: string | null;
        datosFormulario?: {
          rendicionAnualId?: string;
          rendicionCampanaId?: string;
          // RECONOCIMIENTO: fase del trámite (atributos / documentación / etc.)
          fase?: string;
          // RECONOCIMIENTO: archivos del wizard externo (solo visores)
          iniciador?: { dniFrentePath?: string; dniDorsoPath?: string } | null;
          atributos?: { colores?: string[]; simbolosPaths?: string[]; descripcionSimbolo?: string } | null;
        } | null;
      };
    },
    enabled: !!expedienteFilter,
  });

  // Revisar exige DOS cosas: el rol, y que el trámite del expediente elegido ya haya
  // sido INICIADO. Un expediente en BORRADOR o PRESENTADO no tiene etapa abierta, así
  // que ni sus documentos cambian de estado ni sus secciones se aprueban — el backend
  // devuelve 409 (services/tramiteIniciado.ts) y acá se apagan los controles para no
  // ofrecer una acción que va a fallar.
  //
  // Sin expediente elegido no se bloquea nada: la pantalla se abre por partido y ahí
  // no hay trámite que iniciar.
  const tramiteEstaIniciado = tramiteIniciado(expedienteActivo?.estado);
  // En candidaturas Mesa de Entradas es de solo lectura: ve todo, no verifica nada
  // (el backend le devuelve 403). En los demás trámites conserva el permiso.
  const soloLecturaEnCandidatura = user?.rol === 'MESA_ENTRADAS' && expedienteActivo?.tipo === 'CANDIDATURA';
  const canRevisar = canRevisarPorRol && tramiteEstaIniciado && !soloLecturaEnCandidatura;

  // Agrupación en la que se anclan las secciones cuando hay un expediente elegido:
  // la DUEÑA del expediente, no el partido de la sesión.
  //
  // En una alianza los dos no coinciden. El apoderado de un partido INTEGRANTE tiene
  // su propio partido en la sesión, así que la pantalla le mostraba las autoridades
  // y los apoderados de SU partido mientras el expediente abierto era el de la
  // alianza — y del lado interno, que entra con `?partidoId=` de la alianza, se veían
  // los de la alianza. Misma pantalla, dos contenidos distintos.
  //
  // Sin expediente elegido no hay nada de dónde derivarlo y se conserva el partido
  // de siempre.
  const agrupacionActivaId = expedienteActivo?.partidoId || partidoActivoId;
  // `useMemo` para no rearmar el array en cada render: es el valor de un contexto y
  // una referencia nueva vuelve a renderizar a todos sus consumidores.
  const agrupacionesConAcceso = useMemo(() => [agrupacionActivaId], [agrupacionActivaId]);

  // Query del partido activo para obtener su tipo
  const { data: partidoActivo } = useQuery({
    queryKey: ['partido-detail', partidoActivoId],
    queryFn: async () => {
      const response = await partidoService.getById(partidoActivoId!);
      return response.data.data ?? response.data;
    },
    enabled: !!partidoActivoId,
  });

  // Query agrupaciones (solo internos).
  // `clase: 'TODAS'` porque un documento puede pertenecer a un partido, a una
  // confederación o a una alianza, y el listado excluye las dos últimas por defecto
  // (son filas de `partidos`, ver ClaseAgrupacion). Sin esto no había forma de
  // asociar un documento a una alianza desde acá.
  const { data: partidos } = useQuery({
    queryKey: ['partidos-select-todas'],
    queryFn: async () => {
      const response = await partidoService.list({ pageSize: 500, clase: 'TODAS' });
      return response.data.data;
    },
    enabled: !isExterno,
  });

  // Queries DNI para observación unificada — comparten queryKey con las secciones.
  // Van por el expediente ELEGIDO y la agrupación dueña, no por el query param y el
  // partido de la sesión: con dos claves distintas la página y sus secciones pedían
  // dos conjuntos de archivos y los contadores de la observación no coincidían con
  // lo que se veía abajo.
  const expedienteIdFromUrl = searchParams.get('expedienteId') || '';
  const { data: autoridadesDniData } = useQuery<any[]>({
    queryKey: expedienteFilter
      ? ['autoridades-dni', 'exp', expedienteFilter]
      : ['autoridades-dni', 'partido', agrupacionActivaId],
    queryFn: async () => {
      if (expedienteFilter) {
        const response = await expedienteService.getAutoridadesDni(expedienteFilter);
        return response.data.data;
      }
      const response = await partidoService.getAutoridadesDni(agrupacionActivaId!);
      return response.data.data;
    },
    enabled: !!(expedienteFilter || agrupacionActivaId),
  });

  const { data: apoderadosDniData } = useQuery<any[]>({
    queryKey: ['apoderados-dni', agrupacionActivaId, expedienteFilter || null],
    queryFn: async () => {
      const response = await partidoService.getApoderadosDni(agrupacionActivaId!, expedienteFilter || undefined);
      return response.data.data;
    },
    enabled: !!agrupacionActivaId,
  });

  const partidoParaExpedientes = isExterno
    ? user?.partidoId
    : (uploadData.partidoId || partidoFilter);

  const { data: expedientes } = useQuery({
    queryKey: ['expedientes-modal', partidoParaExpedientes],
    queryFn: async () => {
      const response = await expedienteService.list({ partidoId: partidoParaExpedientes, pageSize: 100 });
      return response.data.data;
    },
    enabled: !!partidoParaExpedientes,
    staleTime: 60_000,
  });

  // Mutation estado + observacion
  const expedienteId = searchParams.get('expedienteId') || '';

  const estadoMutation = useMutation({
    mutationFn: ({ id, estado, observacion, etapa }: { id: string; estado: EstadoRevision; observacion?: string | null; etapa: 'ADMISIBILIDAD' | 'ANALISIS' }) =>
      documentoService.updateEstadoRevision(id, estado, observacion, etapa),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documentos'] });
      queryClient.invalidateQueries({ queryKey: ['autoridades-dni'] });
      queryClient.invalidateQueries({ queryKey: ['apoderados-dni'] });
      queryClient.invalidateQueries({ queryKey: ['iniciador-dni'] });
      // Al revisar un adjunto cambia el estado del expediente (p. ej. quedan o no
      // adjuntos pendientes). Invalidamos su query para que "Volver al expediente"
      // muestre la info actualizada en vez de la cache vieja.
      if (expedienteId) queryClient.invalidateQueries({ queryKey: ['expediente', expedienteId] });
      // También la bandeja de gestión, así sus contadores ("respuestas sin revisar")
      // se actualizan al volver al listado sin tener que recargar a mano.
      queryClient.invalidateQueries({ queryKey: ['gestion-expedientes'] });
    },
    onError: () => {
      toast.error('Error al actualizar el estado');
    },
  });

  // El listado de adherentes (Excel) NO se verifica a mano: la decisión la da el
  // control de adherentes. Cualquier acción de verificación sobre ese tipo dispara
  // el control (carga el Excel + evalúa el mínimo legal) y setea el estado del doc.
  const esDocAdherentesExcel = (docId: string) =>
    (data ?? []).some((d) => d.id === docId && d.tipo === 'LISTADO_ADHERENTES_EXCEL');

  const verificarAdherentesMutation = useMutation({
    mutationFn: (docId: string) => documentoService.verificarAdherentes(docId),
    onSuccess: (resp: any) => {
      const { alcanzaMinimo, resumen, partidoId } = resp.data ?? {};
      queryClient.invalidateQueries({ queryKey: ['documentos'] });
      if (expedienteId) queryClient.invalidateQueries({ queryKey: ['expediente', expedienteId] });
      queryClient.invalidateQueries({ queryKey: ['gestion-expedientes'] });
      if (alcanzaMinimo) {
        toast.success(`Listado verificado: ${resumen?.adherentesValidos} adherentes válidos (mínimo ${resumen?.minimoRequerido}).`);
      } else {
        toast.error(`No alcanza el mínimo de adherentes (${resumen?.adherentesValidos}/${resumen?.minimoRequerido}). El documento quedó rechazado.`);
        if (partidoId) navigate(`/adherentes?partido=${partidoId}&obs=1`);
      }
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || 'Error al verificar contra el control de adherentes');
    },
  });

  const handleSetEstadoDoc = (docId: string, estado: EstadoRevision, observacion?: string | null) => {
    // Mientras corre el control de adherentes no se acepta ninguna otra acción: el
    // overlay ya tapa los clics, esto cubre los atajos de teclado del cursor.
    if (verificarAdherentesMutation.isPending) return;
    if (esDocAdherentesExcel(docId)) { verificarAdherentesMutation.mutate(docId); return; }
    estadoMutation.mutate({ id: docId, estado, observacion, etapa: dimensionRevision }, {
      onSuccess: () => avanzarCursorTrasAccion(docId),
    });
  };

  const handleSetEstadoTabla = (docId: string, estado: EstadoRevision, observacion?: string | null) => {
    if (verificarAdherentesMutation.isPending) return;
    if (esDocAdherentesExcel(docId)) { verificarAdherentesMutation.mutate(docId); return; }
    estadoMutation.mutate({ id: docId, estado, observacion, etapa: dimensionRevision }, {
      onSuccess: () => avanzarCursorTrasAccion(docId),
    });
  };

  // Invalida todas las queries afectadas por un upload/vincular de documento.
  // Los DNIs (autoridades/apoderados) viven en queries separadas del listado general.
  const invalidateDocQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['documentos'] });
    queryClient.invalidateQueries({ queryKey: ['autoridades-dni'] });
    queryClient.invalidateQueries({ queryKey: ['apoderados-dni'] });
    queryClient.invalidateQueries({ queryKey: ['iniciador-dni'] });
  };

  // Mutation upload
  const uploadMutation = useMutation({
    mutationFn: (formData: FormData) => documentoService.upload(formData),
    onSuccess: () => {
      toast.success('Documento subido exitosamente');
      invalidateDocQueries();
      setShowUploadModal(false);
      resetUploadForm();
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error?.message || 'Error al subir documento');
    },
  });

  // Mutation vincular existente
  const vincularMutation = useMutation({
    mutationFn: (body: object) => api.post('/documentos/vincular-existente', body),
    onSuccess: () => {
      toast.success('Documento vinculado exitosamente');
      invalidateDocQueries();
      setShowUploadModal(false);
      resetUploadForm();
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error?.message || 'Error al vincular documento');
    },
  });

  const resetUploadForm = () => {
    setUploadData({ tipo: '', titulo: '', descripcion: '', partidoId: user?.partidoId || '', expedienteId: '', dniPersona: '' });
    setDniCheckResult(null);
    setDniReemplazar(false);
    setTipoBloqueado(false);
  };

  // Abre el modal de subida con el tipo fijo (desde una fila de obligatorio).

  const buildAutoTitle = (tipo: string, nombre: string, apellido: string, dni: string) => {
    const labelMap: Record<string, string> = {
      DNI_FRENTE_AUTORIDAD: 'DNI Frente',
      DNI_DORSO_AUTORIDAD: 'DNI Dorso',
      DNI_FRENTE_APODERADO: 'DNI Frente',
      DNI_DORSO_APODERADO: 'DNI Dorso',
    };
    const label = labelMap[tipo] || tipoDocLabels[tipo] || tipo;
    const nombreCompleto = `${nombre} ${apellido}`.toUpperCase();
    return `${label} - ${nombreCompleto} (${dni})`;
  };

  const checkDniPersona = async (dni: string, tipoActual?: string) => {
    const clean = dni.replace(/\D/g, '');
    if (clean.length < 5) { setDniCheckResult(null); return; }
    setDniCheckLoading(true);
    try {
      const resp = await api.get(`/documentos/check-dni-persona/${clean}`);
      const data = resp.data.data;
      setDniCheckResult(data);
      // Auto-generar título si hay persona
      if (data?.persona) {
        const tipo = tipoActual ?? uploadData.tipo;
        setUploadData(prev => ({
          ...prev,
          titulo: buildAutoTitle(tipo, data.persona.nombre, data.persona.apellido, clean),
        }));
      }
    } catch {
      setDniCheckResult(null);
    } finally {
      setDniCheckLoading(false);
    }
  };

  const handleUpload = async (files: File[]) => {
    if (files.length === 0) return;
    const file = files[0];
    if (!uploadData.tipo) {
      toast.error('Seleccioná un tipo de documento');
      return;
    }
    // Externo: el documento debe asociarse a un expediente (salvo que el partido
    // no tenga ninguno, caso atípico).
    if (isExterno && !uploadData.expedienteId && (expedientes?.length ?? 0) > 0) {
      toast.error('Elegí el expediente al que corresponde este documento');
      return;
    }
    const cleanDni = uploadData.dniPersona.replace(/\D/g, '');
    if (TIPOS_DNI_PERSONA.includes(uploadData.tipo) && !cleanDni) {
      toast.error('Ingresá el DNI de la persona antes de subir el archivo');
      return;
    }
    if (isExterno && uploadData.expedienteId && obsPendientesPorExpediente[uploadData.expedienteId]?.length) {
      toast.error('Respondé la observación pendiente del expediente antes de subir documentos');
      return;
    }
    const formData = new FormData();
    formData.append('archivo', file);
    formData.append('tipo', uploadData.tipo);
    formData.append('titulo', uploadData.titulo || file.name);
    if (uploadData.descripcion) formData.append('descripcion', uploadData.descripcion);
    if (uploadData.partidoId) formData.append('partidoId', uploadData.partidoId);
    if (uploadData.expedienteId) formData.append('expedienteId', uploadData.expedienteId);
    if (cleanDni) formData.append('dniPersona', cleanDni);
    uploadMutation.mutate(formData);
  };


  // --- Secciones por tipo (alianzas) ---------------------------------------
  // `expediente_tipos_documento`: el estado de cada TIPO, que es el validado
  // definitivo de la sección.
  //
  // La query ya NO se limita a las alianzas. La aprobación por tipo dejó de ser un
  // piloto: también la usan reempadronamiento y reconocimiento. Un expediente sin
  // filas sembradas devuelve [] y la pantalla se comporta como antes, derivando el
  // estado del tipo de sus archivos — así que no hace falta preguntar por el trámite
  // para saber si corresponde pedirlas.
  const esExpedienteAlianza = expedienteActivo?.tipo === 'CONSTITUCION_ALIANZA';

  const { data: seccionesTipo } = useQuery({
    queryKey: ['secciones-tipo-docs', expedienteFilter],
    queryFn: () => seccionesDocumentoService.listar(expedienteFilter),
    enabled: !!expedienteFilter,
  });

  const aprobarTipoMutation = useMutation({
    mutationFn: ({ tipo, estado, nota, dimension }:
      { tipo: string; estado: string; nota?: string; dimension: 'ADMISIBILIDAD' | 'ANALISIS' }) =>
      seccionesDocumentoService.aprobar(expedienteFilter, tipo, estado, nota, dimension),
    onSuccess: () => {
      toast.success('Sección actualizada');
      queryClient.invalidateQueries({ queryKey: ['secciones-tipo-docs', expedienteFilter] });
      queryClient.invalidateQueries({ queryKey: ['alianza-expediente', expedienteFilter] });
    },
    onError: (e: any) => toast.error(e.response?.data?.error?.message || 'No se pudo actualizar la sección'),
  });

  // Filas de una sección de DNI (frente + dorso) y su nota. `seccionesTipo` viene
  // plana, una fila por tipo; el grupo se arma acá para que el check sea uno solo.
  const filasDeGrupo = (grupo: string) => {
    const tipos = GRUPOS_TIPOS_DOCUMENTO[grupo] ?? [];
    return (seccionesTipo ?? []).filter((x: any) => tipos.includes(x.tipo));
  };
  const notaDeGrupo = (grupo: string) => filasDeGrupo(grupo).find((f: any) => f.nota)?.nota ?? null;

  const guardarNotaTipoMutation = useMutation({
    mutationFn: ({ tipo, nota }: { tipo: string; nota: string }) =>
      seccionesDocumentoService.nota(expedienteFilter, tipo, nota),
    onSuccess: () => {
      toast.success('Nota guardada');
      queryClient.invalidateQueries({ queryKey: ['secciones-tipo-docs', expedienteFilter] });
      queryClient.invalidateQueries({ queryKey: ['alianza-expediente', expedienteFilter] });
    },
    onError: (e: any) => toast.error(e.response?.data?.error?.message || 'No se pudo guardar la nota'),
  });

  // "Aprobar todo Admisibilidad": cierra la dimensión formal completa —archivos y
  // checks de tipo— en una sola operación. La admisibilidad es un control sobre el
  // CONJUNTO ("está toda la documentación y es la que dice ser"), no un juicio de
  // fondo archivo por archivo; ése es el análisis. En alianzas además es la única
  // forma de cerrarla, porque el trámite pasa directo a análisis al iniciarse y
  // ningún avance de etapa la declara.
  const aprobarTodoAdmisibilidadMutation = useMutation({
    mutationFn: () => seccionesDocumentoService.aprobarTodoAdmisibilidad(expedienteFilter),
    onSuccess: (r) => {
      toast.success(`Admisibilidad aprobada: ${r.documentos} archivo(s) y ${r.secciones} sección(es).`);
      queryClient.invalidateQueries({ queryKey: ['documentos'] });
      queryClient.invalidateQueries({ queryKey: ['secciones-tipo-docs', expedienteFilter] });
      queryClient.invalidateQueries({ queryKey: ['alianza-expediente', expedienteFilter] });
      // Los DNI se dibujan desde sus propias queries (por persona), no desde
      // `documentos`: sin esto las casillas de autoridades y apoderados quedaban
      // sin actualizar hasta recargar.
      queryClient.invalidateQueries({ queryKey: ['autoridades-dni'] });
      queryClient.invalidateQueries({ queryKey: ['apoderados-dni'] });
      queryClient.invalidateQueries({ queryKey: ['iniciador-dni'] });
      if (expedienteFilter) queryClient.invalidateQueries({ queryKey: ['expediente', expedienteFilter] });
    },
    onError: (e: any) => toast.error(e.response?.data?.error?.message || 'No se pudo aprobar la admisibilidad'),
  });

  // "Descargar todo": un ZIP con todos los documentos visibles del expediente,
  // ordenados en las MISMAS secciones que se ven en esta pantalla. El backend lo
  // manda por stream mientras lo comprime —no hay tope de tamaño—, pero en
  // expedientes grandes son varios segundos igual, así que el botón queda
  // deshabilitado: un segundo click dispara una segunda descarga completa.
  const descargarZipMutation = useMutation({
    mutationFn: () => documentoService.descargarZipExpediente(expedienteFilter),
    // El servicio ya desenvuelve el mensaje del backend del Blob de error.
    onError: (e: any) => toast.error(e?.message || 'No se pudo generar la descarga'),
  });

  const eximirTipoMutation = useMutation({
    mutationFn: ({ tipo, obligatorio }: { tipo: string; obligatorio: boolean }) =>
      seccionesDocumentoService.eximir(expedienteFilter, tipo, obligatorio),
    onSuccess: () => {
      toast.success('Requisito actualizado');
      queryClient.invalidateQueries({ queryKey: ['secciones-tipo-docs', expedienteFilter] });
      queryClient.invalidateQueries({ queryKey: ['alianza-expediente', expedienteFilter] });
    },
    onError: (e: any) => toast.error(e.response?.data?.error?.message || 'No se pudo cambiar el requisito'),
  });

  // --- Etapa de verificación activa (admisibilidad / análisis) -------------
  // Clasificación del estado del expediente en categorías de etapa.
  const estadoExp = expedienteActivo?.estado;
  // En RECONOCIMIENTO la etapa se deriva de la FASE (el circuito corre por fase,
  // no por estado). Las fases de atributos se muestran en la vista de admisibilidad;
  // las de documentación en análisis; las finales de documentación son posteriores.
  const esRecon = expedienteActivo?.tipo === 'RECONOCIMIENTO';
  const faseRecon = esRecon ? getFaseActual(expedienteActivo?.datosFormulario) : null;
  // La regla de qué etapa alcanzó el expediente vive en utils/etapaVerificacion:
  // la comparte la tab Documentación del expediente, que antes tenía su propia
  // versión (peor) y mostraba admisibilidad en trámites ya pasados a análisis.
  const etapaPorEstado: EtapaVerificacion = etapaAlcanzada({
    tipo: expedienteActivo?.tipo,
    estado: estadoExp,
    fase: faseRecon,
  });

  // RECONOCIMIENTO no cambia de estado durante el trámite: su etapa se deriva de
  // la fase (datosFormulario.fase). Atributos → admisibilidad; documentación
  // (de ATRIBUTOS_APROBADOS en adelante) → análisis.
  const esExpedienteRecon = expedienteActivo?.tipo === 'RECONOCIMIENTO';
  const esExpedienteCandidatura = expedienteActivo?.tipo === 'CANDIDATURA';
  // Etapa "real" en la que está el expediente (independiente del toggle de vista):
  // fija el default y decide qué pestaña se habilita.
  const etapaActualExp: 'ADMISIBILIDAD' | 'ANALISIS' | 'POSTERIOR' | null = etapaPorEstado;

  // Toggle de etapa que mira el supervisor. Default inteligente según la etapa
  // real del expediente; respeta la elección manual una vez que el usuario lo cambia.
  const [etapaVista, setEtapaVista] = useState<'ADMISIBILIDAD' | 'ANALISIS'>('ADMISIBILIDAD');
  // Verificación en RECONOCIMIENTO: es ÚNICA (una sola revisión por documento),
  // no las dos dimensiones de reempadronamiento. Se usa siempre la dimensión
  // 'ADMISIBILIDAD', así lo verificado en atributos sigue verificado en análisis
  // (análisis solo agrega documentos nuevos, no re-verifica lo anterior).
  const dimensionRevision: 'ADMISIBILIDAD' | 'ANALISIS' = esExpedienteRecon ? 'ADMISIBILIDAD' : etapaVista;
  // El apoderado (externo) ve la MISMA vista que el lado interno: mismo selector de
  // etapa y mismo corte de documentos por etapa, en reconocimiento y en
  // reempadronamiento. Solo cambian las acciones (el partido no verifica).
  const etapaTouchedRef = useRef(false);
  useEffect(() => {
    if (etapaTouchedRef.current) return;
    // Default inteligente. En RECONOCIMIENTO, una vez pasada la etapa de atributos
    // (cualquier fase de documentación, incluidas las POSTERIOR como EN_TEP/RECHAZADA/
    // APROBADA), la verificación se abre en "En análisis (documentación)". Solo abre
    // Admisibilidad si el expediente aún está en atributos.
    // En el resto de los trámites vale la misma regla: se abre en Análisis en
    // cuanto el expediente pasó la admisibilidad, incluidas las etapas POSTERIOR
    // (EN_SECRETARIO / APROBADO / RECHAZADO / ARCHIVADO). Solo se abre en
    // Admisibilidad si todavía está ahí, o si aún no llegó (etapa null:
    // BORRADOR / PRESENTADO), donde análisis no tiene nada que mostrar.
    // CANDIDATURA no tiene admisibilidad (pasa de PRESENTADO a EN_ANALISIS): abre
    // siempre en Análisis, que además es la única pestaña que se le muestra.
    setEtapaVista(
      esExpedienteCandidatura || etapaActualExp === 'ANALISIS' || etapaActualExp === 'POSTERIOR' ? 'ANALISIS' : 'ADMISIBILIDAD'
    );
  }, [etapaActualExp, esExpedienteCandidatura]);
  const cambiarEtapaVista = (e: 'ADMISIBILIDAD' | 'ANALISIS') => {
    etapaTouchedRef.current = true;
    setEtapaVista(e);
  };

  // Estado/motivo "efectivos" según la etapa que se está viendo: se proyectan
  // sobre estadoRevision/observacion/revisado* para que toda la UI existente
  // (badges, listas, checklist) siga funcionando sin cambios.
  const proyectarEtapa = (d: Documento): Documento => {
    // En reconocimiento la verificación es única (dimensión admisibilidad).
    const enAnalisis = dimensionRevision === 'ANALISIS';
    return {
      ...d,
      estadoRevision: (enAnalisis ? d.estadoRevisionAnalisis : d.estadoRevisionAdmisibilidad) ?? d.estadoRevision ?? 'PENDIENTE',
      observacion: enAnalisis ? (d.observacionAnalisis ?? undefined) : (d.observacionAdmisibilidad ?? undefined),
      revisadoPorNombre: enAnalisis ? (d.analisisRevisadoPor ?? undefined) : (d.admisibilidadRevisadoPor ?? undefined),
      revisadoEn: enAnalisis ? (d.analisisRevisadoEn ?? undefined) : (d.admisibilidadRevisadoEn ?? undefined),
    };
  };

  // Qué documentos se ven. La regla es UNA SOLA y vive en utils/etapaVerificacion
  // (`pesaEnDimension`): la comparten esta pantalla, el panel de secciones del
  // expediente y el backend que cuenta los pendientes. Lo que se oculta acá NO se
  // cuenta allá — leer el comentario de esa función antes de tocar esto.
  //
  // Lo que se saca en análisis es el archivo ya rechazado en admisibilidad: es el
  // original que el partido reemplazó, no trabajo pendiente de esta etapa.
  //
  // NO VUELVE el corte de POSTERIOR que había acá ("sólo los aprobados en
  // admisibilidad"): ése ocultaba también los PENDIENTE, que son justamente los que
  // hay que poder revisar, y es el que dejaba secciones trabadas sin salida.
  const esVisible = (d: Documento): boolean => {
    // Los virtuales son los "faltantes" del checklist: no son archivos.
    if (d.virtual) return true;
    // Revisando la respuesta a una observación se ven todos sus adjuntos, sin
    // importar en qué dimensión estén: son el objeto de esa revisión.
    if (revisarObsId) return true;
    // RECONOCIMIENTO tiene verificación ÚNICA (dimensión admisibilidad): no hay una
    // segunda etapa de la que sacar nada.
    if (esExpedienteRecon) return true;
    return pesaEnDimension(d, etapaVista);
  };

  const docsRaw = (data || []).filter(esVisible).map(proyectarEtapa);
  const showChecklist = !!partidoActivoId;

  // ------------------------------------------------------------------
  // Observaciones pendientes del externo (banner + bloqueo de upload)
  // ------------------------------------------------------------------

  interface ObsPendiente {
    id: string;
    tipo: string;
    estado: string;
    texto: string;
    creadoEn: string;
    fechaLimite: string | null;
    plazoDias: number | null;
    expediente: { id: string; numero: string; tipo: string };
  }

  const { data: obsPendientesExterno = [] } = useQuery<ObsPendiente[]>({
    queryKey: ['obs-pendientes', user?.partidoId],
    queryFn: async () => {
      const r = await api.get(`/partidos/${user?.partidoId}/observaciones-pendientes`);
      return r.data.data;
    },
    enabled: isExterno && !!user?.partidoId,
  });

  // Map: expedienteId -> array de obs pendientes (para bloqueo en upload)
  const obsPendientesPorExpediente: Record<string, ObsPendiente[]> = {};
  obsPendientesExterno.forEach(o => {
    if (!obsPendientesPorExpediente[o.expediente.id]) obsPendientesPorExpediente[o.expediente.id] = [];
    obsPendientesPorExpediente[o.expediente.id].push(o);
  });

  const expFiltroTieneObsPendiente = !!(expedienteFilter && obsPendientesPorExpediente[expedienteFilter]?.length);
  const expModalTieneObsPendiente = !!(uploadData.expedienteId && obsPendientesPorExpediente[uploadData.expedienteId]?.length);

  // Tipo de documento según el expediente elegido DENTRO del modal (no el filtro externo).
  const expedienteModalSel = (expedientes as Array<{ id: string; tipo?: string }> | undefined)?.find(e => e.id === uploadData.expedienteId);
  const esReconModal = expedienteModalSel?.tipo === 'RECONOCIMIENTO';
  // Hay expedientes para elegir pero todavía no se eligió → primero el expediente, luego el tipo.
  const faltaElegirExpedienteModal = (expedientes?.length ?? 0) > 0 && !uploadData.expedienteId;

  // ------------------------------------------------------------------
  // Modo "revisar respuesta a observación"
  // ------------------------------------------------------------------

  // Query metadata de la obs (tipo, fecha) para el banner
  const { data: obsRevisar } = useQuery({
    queryKey: ['observacion-revisar', revisarObsExpedienteId, revisarObsId],
    queryFn: async () => {
      const r = await api.get(`/expedientes/${revisarObsExpedienteId}/observaciones/${revisarObsId}`);
      return r.data.data as {
        id: string;
        tipo: string;
        creadoEn: string;
        respuestaFecha: string | null;
        expediente: { id: string; numero: string; partidoId?: string; tipo?: string };
      };
    },
    enabled: !!(revisarObsId && revisarObsExpedienteId),
  });

  // Pre-cargar el filtro de partido al entrar en modo "revisar respuesta", para que
  // se muestre el checklist completo (obligatorios + DNIs Apoderados) en lugar
  // de la rama "sin partido seleccionado". Sólo si el supervisor no eligió otro
  // filtro manualmente.
  useEffect(() => {
    if (!revisarObsId) return;
    const pid = obsRevisar?.expediente?.partidoId;
    if (pid && !partidoFilter) setPartidoFilter(pid);
  }, [revisarObsId, obsRevisar?.expediente?.partidoId, partidoFilter]);

  // Al entrar a "revisar respuesta", la etapa activa la define el tipo de la obs:
  // intimación -> admisibilidad, observación -> análisis.
  useEffect(() => {
    if (!obsRevisar?.tipo) return;
    etapaTouchedRef.current = true;
    setEtapaVista(obsRevisar.tipo === 'INTIMACION' && !esExpedienteCandidatura ? 'ADMISIBILIDAD' : 'ANALISIS');
  }, [obsRevisar?.tipo, esExpedienteCandidatura]);

  // Docs adjuntos a esta respuesta (intimación -> intimacionRespuestaId,
  // observación -> observacionRespuestaId).
  const markedDocs = revisarObsId
    ? docsRaw.filter(d => obsRevisar?.tipo === 'INTIMACION'
        ? d.intimacionRespuestaId === revisarObsId
        : d.observacionRespuestaId === revisarObsId)
    : [];
  const markedDocIds = new Set(markedDocs.map(d => d.id));
  const markedPendientes = markedDocs.filter(d => d.estadoRevision === 'PENDIENTE');
  const markedAprobados = markedDocs.filter(d => d.estadoRevision === 'APROBADO').length;
  const markedRechazados = markedDocs.filter(d => d.estadoRevision === 'RECHAZADO').length;

  // Cursor: id del próximo doc a scrollear. Se setea al cargar y después de cada acción.
  const [scrollTargetId, setScrollTargetId] = useState<string | null>(null);
  const initialScrollDoneRef = useRef(false);

  // Al cargar por primera vez en modo revisar, apuntar al primer pendiente.
  useEffect(() => {
    if (!revisarObsId || initialScrollDoneRef.current) return;
    if (markedPendientes.length === 0) return;
    setScrollTargetId(markedPendientes[0].id);
    initialScrollDoneRef.current = true;
  }, [revisarObsId, markedPendientes]);

  // Cuando scrollTargetId cambia, intentar scrollear al doc (con un pequeño delay
  // para que los acordeones tengan tiempo de auto-abrirse).
  useEffect(() => {
    if (!scrollTargetId) return;
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-doc-id="${scrollTargetId}"]`);
      if (el && 'scrollIntoView' in el) {
        (el as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 200);
    return () => clearTimeout(t);
  }, [scrollTargetId]);

  // Scroll al tipo pedido por el tablero del expediente. El delay le da tiempo a
  // los acordeones a abrirse (arrancan abiertos por prop, pero después de la query).
  const scrollTipoHechoRef = useRef(false);
  useEffect(() => {
    if (!abrirTipo || scrollTipoHechoRef.current || isLoading) return;
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-tipo-doc="${abrirTipo}"]`);
      if (el) {
        (el as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
        scrollTipoHechoRef.current = true;
      }
    }, 300);
    return () => clearTimeout(t);
  }, [abrirTipo, isLoading]);

  const avanzarCursorTrasAccion = (docIdActuado: string) => {
    if (!revisarObsId) return;
    // Próximo pendiente que NO sea el que acabamos de aprobar/rechazar
    const restantes = markedDocs.filter(d => d.id !== docIdActuado && d.estadoRevision === 'PENDIENTE');
    if (restantes.length > 0) {
      setScrollTargetId(restantes[0].id);
    } else {
      setScrollTargetId(null);
    }
  };

  const esExpedienteRendicionAnual = expedienteActivo?.tipo === 'RENDICION_ANUAL';
  const esExpedienteRendicionCampana = expedienteActivo?.tipo === 'RENDICION_CAMPANA';
  const rendicionVinculadaId = esExpedienteRendicionAnual
    ? expedienteActivo?.datosFormulario?.rendicionAnualId
    : esExpedienteRendicionCampana
    ? expedienteActivo?.datosFormulario?.rendicionCampanaId
    : undefined;

  const { data: rendicionVinculada } = useQuery({
    queryKey: ['rendicion-filtro-docs', expedienteActivo?.tipo, rendicionVinculadaId],
    queryFn: async () => {
      if (!rendicionVinculadaId) return null;
      const svc = esExpedienteRendicionAnual ? rendicionAnualService : rendicionCampanaService;
      const response = await svc.getById(rendicionVinculadaId);
      return (response.data?.data ?? response.data) as { tipoPresentacion: 'COMPLETA' | 'COPIA_DISTRITO' };
    },
    enabled: !!rendicionVinculadaId,
  });

  const esFiltroDeRendicion = esExpedienteRendicionAnual || esExpedienteRendicionCampana;

  // Tipos obligatorios:
  //  - Si filtré por un expediente de rendición → único obligatorio es el PDF firmado
  //    (CERTIFICACION_CPCE para COMPLETA, COPIA_SPECA para COPIA_DISTRITO)
  //  - Si no → checklist de partido (reempadronamiento + extras según tipo de partido)
  const tiposObligatorios = (() => {
    if (esFiltroDeRendicion && rendicionVinculada) {
      return rendicionVinculada.tipoPresentacion === 'COPIA_DISTRITO'
        ? ['COPIA_SPECA']
        : ['CERTIFICACION_CPCE'];
    }
    if (esFiltroDeRendicion && !rendicionVinculada) {
      // Mientras carga la rendición, todavía no sé qué tipo es → vacío
      return [];
    }
    // RECONOCIMIENTO: obligatorios del Art. 18/19 (no los de reempadronamiento).
    // Los de cada etapa posterior se suman como labels nuevos del mismo checklist
    // cuando el trámite ALCANZA la etapa, y no vuelven a salir: el padrón del 4‰ y
    // las actas de las internas son la prueba del definitivo, y el Secretario y el
    // TEP los revisan justamente cuando la etapa ya terminó.
    //
    // Antes se preguntaba por la etapa EN CURSO (esEtapaAfiliados / esEtapaAutoridades)
    // y al avanzar a DEFINITIVO_* esos tipos se soltaban del checklist y aparecían
    // como "Otros documentos", o sea como documentación opcional.
    if (expedienteActivo?.tipo === 'RECONOCIMIENTO') {
      return [
        ...DOCS_OBLIGATORIOS_RECONOCIMIENTO,
        ...(alcanzoEtapaAfiliados(faseRecon) ? DOCS_OBLIGATORIOS_RECONOCIMIENTO_AFILIADOS : []),
        ...(alcanzoEtapaAutoridades(faseRecon) ? DOCS_OBLIGATORIOS_RECONOCIMIENTO_AUTORIDADES : []),
      ];
    }
    // CANDIDATURA (Gaiman): los 8 tipos propios del candidato (DNI, foto,
    // comprobante de residencia, aceptación, antecedentes penales, plataforma
    // electoral, acta de proclamación) — NO los del reempadronamiento del partido.
    if (expedienteActivo?.tipo === 'CANDIDATURA') {
      return TIPOS_CANDIDATURA;
    }
    // CONSTITUCIÓN DE ALIANZA: los 8 tipos del Art. 27 y complementarios, NO los del
    // reempadronamiento. Sin esta rama caía en `obligatoriosPorPartido`, que devuelve
    // la lista del partido (carta orgánica, padrón de afiliados, cuenta bancaria…) y
    // dejaba los 8 documentos de la alianza en "Otros documentos".
    if (expedienteActivo?.tipo === 'CONSTITUCION_ALIANZA') {
      // Los tipos que YA tienen sección propia quedan afuera del checklist: SIMBOLO
      // en "Emblemas y símbolos" y los cuatro de DNI en "Documentación de
      // Autoridades" y "de Apoderados". Si entraran acá, el mismo tipo se listaría
      // dos veces en la pantalla —y encima el DNI se vería como una fila suelta,
      // sin la persona, que es lo único con lo que se puede revisar.
      return DOCS_ALIANZA_TODOS.filter((t) => !TIPOS_CON_SECCION_PROPIA.includes(t));
    }
    return showChecklist
      ? obligatoriosPorPartido(partidoActivo?.tipo, (partidoActivo as any)?.clase)
      : [];
  })();
  const tiposObligatoriosSet = new Set(tiposObligatorios);

  // Docs para el checklist (obligatorios)
  const docsChecklist = docsRaw.filter(d => tiposObligatoriosSet.has(d.tipo));

  // Docs para las otras secciones (excluyendo obligatorios).
  // ACTA_DESIGNACION_APODERADOS ahora es obligatorio → cae en docsChecklist,
  // por lo que se excluye automáticamente de las secciones de abajo.
  const docsOtros = docsRaw.filter(d => !tiposObligatoriosSet.has(d.tipo));

  const docsConVirtual = docsOtros;

  // Secciones cuando hay checklist activo
  const seccionAutoridades = docsConVirtual.filter(d => TIPOS_AUTORIDADES.includes(d.tipo));
  const seccionApoderados = docsConVirtual.filter(d => TIPOS_APODERADOS.includes(d.tipo));
  // Resoluciones: informes de elevación + resoluciones. Tiene su propia
  // sección y NO debe aparecer en "Otros documentos". Orden cronológico ASCENDENTE
  // (más viejo primero) para que la numeración #1, #2, #3 sea del más viejo al más
  // nuevo, en línea con el N°/versión del título del documento.
  const ordenarCronologico = (arr: Documento[]) =>
    [...arr].sort((a, b) => new Date(a.creadoEn).getTime() - new Date(b.creadoEn).getTime());
  const seccionResoluciones = ordenarCronologico(docsConVirtual.filter(d => TIPOS_RESOLUCIONES.includes(d.tipo)));
  const seccionOtrosTabla = docsConVirtual.filter(d =>
    // SIMBOLO tiene su propia sección "Emblemas y símbolos", no va en "Otros".
    // El DNI del iniciador es un atributo: tiene su sección en admisibilidad y
    // NO debe aparecer en "Otros documentos" (ni en análisis).
    !TIPOS_AUTORIDADES.includes(d.tipo) && !TIPOS_APODERADOS.includes(d.tipo) && d.tipo !== 'SIMBOLO'
    && d.tipo !== 'DNI_FRENTE_INICIADOR' && d.tipo !== 'DNI_DORSO_INICIADOR'
    && !TIPOS_RESOLUCIONES.includes(d.tipo)
  );

  // Secciones cuando NO hay checklist (interno sin partido seleccionado)
  const virtualPartido = showChecklist ? [] : (() => {
    const base = [...DOCS_OBLIGATORIOS_BASE];
    const tipos = docsRaw.map(d => d.tipo);
    return base.filter(t => !tipos.includes(t)).map(tipo => ({
      id: `virtual-${tipo}`,
      tipo,
      titulo: 'No presentado',
      nombreOriginal: '',
      mimeType: '',
      tamanioBytes: 0,
      version: 0,
      estadoRevision: 'PENDIENTE' as EstadoRevision,
      creadoEn: '',
      virtual: true,
    }));
  })();

  const docsConVirtualSinChecklist = [...docsRaw, ...virtualPartido];
  const seccionPartidoSinChecklist = docsConVirtualSinChecklist.filter(d =>
    TIPOS_PARTIDO.includes(d.tipo) ||
    (tiposObligatorios.length === 0 && DOCS_OBLIGATORIOS_BASE.includes(d.tipo) && !TIPOS_APODERADOS.includes(d.tipo))
  );
  const seccionAutoridadesSinChecklist = docsConVirtualSinChecklist.filter(d => TIPOS_AUTORIDADES.includes(d.tipo));
  const seccionApoderadosSinChecklist = docsConVirtualSinChecklist.filter(d => TIPOS_APODERADOS.includes(d.tipo));
  const seccionResolucionesSinChecklist = ordenarCronologico(docsConVirtualSinChecklist.filter(d => TIPOS_RESOLUCIONES.includes(d.tipo)));
  const seccionOtrosSinChecklist = docsConVirtualSinChecklist.filter(d =>
    !TIPOS_PARTIDO.includes(d.tipo) && !TIPOS_AUTORIDADES.includes(d.tipo) && !TIPOS_APODERADOS.includes(d.tipo)
    && !TIPOS_RESOLUCIONES.includes(d.tipo)
  );

  // Sección "Resoluciones" reutilizable (mismos documentos en toda etapa).
  // Se coloca: en admisibilidad ARRIBA de "Otros documentos"; en análisis DEBAJO
  // de "Documentos obligatorios". Usa SeccionLista (una fila por documento, con
  // numeración de repetidos) para que CADA publicación/informe/resolución quede
  // visible — no agrupada/colapsada por tipo.
  const renderSeccionResoluciones = (docs: typeof seccionResoluciones) => docs.length > 0 ? (
    <SeccionLista
      etapa={dimensionRevision}
      titulo="Resoluciones"
      docs={docs}
      canEdit={canRevisar}
      isInterno={!isExterno}
      onSetEstado={handleSetEstadoTabla}
      markedDocIds={markedDocIds}
      mostrarTitulo
    />
  ) : null;

  // ---------------------------------------------------------------------------
  // Observación unificada: obligatorios + DNIs autoridades/apoderados
  // ---------------------------------------------------------------------------
  // ¿La sección de este tipo tiene su CHECK en la dimensión que se está viendo?
  //
  // Es EL corte de la observación automática. La regla anterior era "si algún archivo
  // del tipo está aprobado, no se lista nada más", y eso se rompe justamente donde el
  // tipo tiene N archivos: en una alianza, la constancia de voluntad del integrante A
  // aprobada silenciaba el rechazo de la del integrante B, y el partido nunca se
  // enteraba de que le faltaba corregir esa. Contar archivos no puede saber cuántos
  // hacen falta; el check de la sección sí, porque lo puso una persona.
  //
  // `null` = el expediente no tiene secciones sembradas (todavía). Ahí se conserva la
  // regla vieja, para que nada cambie antes de la siembra.
  const seccionAprobada = (tipo: string): boolean | null => {
    const filas = (seccionesTipo ?? []).filter((x: any) => {
      const grupo = Object.entries(GRUPOS_TIPOS_DOCUMENTO).find(([, t]) => t.includes(tipo))?.[0];
      return grupo ? GRUPOS_TIPOS_DOCUMENTO[grupo].includes(x.tipo) : x.tipo === tipo;
    });
    if (filas.length === 0) return null;
    // Una sección de DNI son DOS filas: aprobada sólo si lo están las dos.
    return filas.every((f: any) =>
      (dimensionRevision === 'ADMISIBILIDAD' ? f.admisibilidad : f.analisis).estado === 'APROBADO');
  };

  /**
   * Archivos RECHAZADOS de un tipo, con su motivo, cuando la sección no tiene check.
   *
   * Devuelve TODOS los rechazados, no sólo el último: con varios archivos por tipo
   * cada rechazo es de un objeto distinto (un integrante, una persona, un órgano) y
   * quedarse con el último dejaba a los demás sin avisar. Se descarta el rechazo que
   * ya fue superado por una carga POSTERIOR del mismo título, que sí es ruido.
   */
  const rechazadosDeTipo = (files: Array<any>): Array<{ titulo: string; motivo?: string }> => {
    const porTitulo = new Map<string, any[]>();
    for (const f of files) {
      const clave = (f.titulo || f.nombreOriginal || '').trim() || f.id;
      porTitulo.set(clave, [...(porTitulo.get(clave) ?? []), f]);
    }
    const salida: Array<{ titulo: string; motivo?: string }> = [];
    for (const [clave, grupo] of porTitulo) {
      const ordenado = [...grupo].sort((a, b) => new Date(a.creadoEn).getTime() - new Date(b.creadoEn).getTime());
      const ultimo = ordenado[ordenado.length - 1];
      if (ultimo.estadoRevision === 'RECHAZADO') {
        salida.push({ titulo: clave, motivo: ultimo.observacion || undefined });
      }
    }
    return salida;
  };

  const obligatoriosProblemas: Array<{ tipo: string; label: string; estado: 'FALTANTE' | 'RECHAZADO'; motivo?: string }> = [];
  if (showChecklist) {
    tiposObligatorios.forEach(tipo => {
      const files = docsChecklist
        .filter(a => a.tipo === tipo)
        .sort((a, b) => new Date(a.creadoEn).getTime() - new Date(b.creadoEn).getTime());
      const label = tipoDocLabels[tipo] || tipo;
      if (files.length === 0) {
        obligatoriosProblemas.push({ tipo, label, estado: 'FALTANTE' });
        return;
      }
      const check = seccionAprobada(tipo);
      // Con la sección tildada no hay nada que observar, tenga los archivos que tenga.
      if (check === true) return;
      if (check === null) {
        // Sin secciones sembradas: regla anterior (un aprobado alcanza).
        if (files.some(f => f.estadoRevision === 'APROBADO')) return;
        const lastReviewed = [...files].reverse().find(f => f.estadoRevision !== 'PENDIENTE');
        if (lastReviewed?.estadoRevision === 'RECHAZADO') {
          obligatoriosProblemas.push({ tipo, label, estado: 'RECHAZADO', motivo: lastReviewed.observacion || undefined });
        }
        return;
      }
      // Sección sin tildar: se listan TODOS los rechazados del tipo, uno por objeto.
      for (const r of rechazadosDeTipo(files)) {
        obligatoriosProblemas.push({
          tipo,
          label: r.titulo && r.titulo !== label ? `${label} — ${r.titulo}` : label,
          estado: 'RECHAZADO',
          motivo: r.motivo,
        });
      }
    });
  }

  const evaluarLadoDni = (arr: any[] | undefined): { estado: 'OK' | 'PENDIENTE' | 'FALTANTE' | 'RECHAZADO'; motivo?: string } => {
    if (!arr || arr.length === 0) return { estado: 'FALTANTE' };
    const mejor = arr.find((d: any) => d.estadoRevision === 'APROBADO')
      ?? arr.find((d: any) => d.estadoRevision === 'PENDIENTE')
      ?? arr[0];
    if (mejor.estadoRevision === 'APROBADO') return { estado: 'OK' };
    if (mejor.estadoRevision === 'PENDIENTE') return { estado: 'PENDIENTE' };
    return { estado: 'RECHAZADO', motivo: mejor.observacion || undefined };
  };

  // Proyecta los DNIs a la etapa activa antes de evaluarlos: sin esto el estado
  // efectivo queda undefined y evaluarLadoDni lo trata como RECHAZADO.
  const enAnalisisDni = etapaVista === 'ANALISIS';
  const autoridadesDniProj = (autoridadesDniData || []).map((o: any) => ({
    ...o,
    autoridades: (o.autoridades || []).map((a: any) => ({
      ...a,
      dniFrente: (a.dniFrente || []).map((d: any) => proyectarDniEtapa(d, enAnalisisDni)),
      dniDorso: (a.dniDorso || []).map((d: any) => proyectarDniEtapa(d, enAnalisisDni)),
    })),
  }));
  const apoderadosDniProj = (apoderadosDniData || []).map((ap: any) => ({
    ...ap,
    dniFrente: (ap.dniFrente || []).map((d: any) => proyectarDniEtapa(d, enAnalisisDni)),
    dniDorso: (ap.dniDorso || []).map((d: any) => proyectarDniEtapa(d, enAnalisisDni)),
  }));

  const dniProblemas: Array<{ label: string; estado: 'FALTANTE' | 'RECHAZADO'; motivo?: string }> = [];
  // Con la sección de DNI tildada no se observa nada de ese grupo, igual que con
  // cualquier otro tipo: el check es la declaración de que están todos.
  //
  // Adentro, en cambio, cada persona y cada lado siguen evaluándose por separado. Ahí
  // "hay uno aprobado, está bien" NO es el bug de cardinalidad: el frente de una
  // persona es un solo objeto, así que si el rechazado se reemplazó por uno aprobado
  // esa persona está en orden y listar el rechazo viejo sería pedirle corregir algo
  // que ya corrigió.
  const checkAutoridades = seccionAprobada('DNI_FRENTE_AUTORIDAD');
  const checkApoderados = seccionAprobada('DNI_FRENTE_APODERADO');
  if (showChecklist) {
    (checkAutoridades === true ? [] : autoridadesDniProj).forEach((organo: any) => {
      const nombreOrgano = organo.denominacionPersonalizada || organo.denominacion || organo.tipoOrgano || 'Órgano';
      (organo.autoridades || []).forEach((aut: any) => {
        const cargo = aut.mandato?.cargo || 'Autoridad';
        const nombreCompleto = `${cargo} ${aut.nombre || ''} ${aut.apellido || ''}`.trim();
        const personaLabel = `${nombreOrgano} — ${nombreCompleto}${aut.dni ? ` (DNI ${aut.dni})` : ''}`;
        const fr = evaluarLadoDni(aut.dniFrente);
        if (fr.estado === 'FALTANTE' || fr.estado === 'RECHAZADO') {
          dniProblemas.push({ label: `DNI FRENTE - ${personaLabel}`, estado: fr.estado, motivo: fr.motivo });
        }
        const dr = evaluarLadoDni(aut.dniDorso);
        if (dr.estado === 'FALTANTE' || dr.estado === 'RECHAZADO') {
          dniProblemas.push({ label: `DNI DORSO - ${personaLabel}`, estado: dr.estado, motivo: dr.motivo });
        }
      });
    });
    (checkApoderados === true ? [] : apoderadosDniProj)
      .filter((ap: any) => ap.activo !== false)
      .forEach((ap: any) => {
        const nombreCompleto = `${ap.nombre || ''} ${ap.apellido || ''}`.trim();
        const personaLabel = `Apoderado ${nombreCompleto}${ap.dni ? ` (DNI ${ap.dni})` : ''}`;
        const fr = evaluarLadoDni(ap.dniFrente);
        if (fr.estado === 'FALTANTE' || fr.estado === 'RECHAZADO') {
          dniProblemas.push({ label: `DNI FRENTE - ${personaLabel}`, estado: fr.estado, motivo: fr.motivo });
        }
        const dr = evaluarLadoDni(ap.dniDorso);
        if (dr.estado === 'FALTANTE' || dr.estado === 'RECHAZADO') {
          dniProblemas.push({ label: `DNI DORSO - ${personaLabel}`, estado: dr.estado, motivo: dr.motivo });
        }
      });
  }

  // Otros documentos: son cargas opcionales, así que sólo es problema un RECHAZO
  // (no aplica FALTANTE). Se agrupa por tipo igual que los obligatorios: si algún
  // archivo de ese tipo fue APROBADO, no es problema; si no, se toma el último
  // revisado y se observa solo si quedó RECHAZADO (ignora rechazos viejos
  // superados por una carga posterior). Se excluyen Notas del sistema y virtuales.
  const otrosProblemas: Array<{ tipo: string; label: string; estado: 'RECHAZADO'; motivo?: string }> = [];
  if (showChecklist) {
    // Los SÍMBOLOS entran acá aunque tengan sección propia: no están en el checklist
    // de obligatorios ni en "Otros documentos", así que sin esto un emblema rechazado
    // no aparecía en la observación automática y el partido no se enteraba.
    const simbolosReales = docsRaw.filter(d => d.tipo === 'SIMBOLO' && !d.virtual).map(proyectarEtapa);
    const otrosReales = [...seccionOtrosTabla, ...simbolosReales].filter(d => !d.virtual && d.tipo !== 'NOTA');
    const tiposOtros = Array.from(new Set(otrosReales.map(d => d.tipo)));
    tiposOtros.forEach(tipo => {
      const files = otrosReales
        .filter(d => d.tipo === tipo)
        .sort((a, b) => new Date(a.creadoEn).getTime() - new Date(b.creadoEn).getTime());
      const check = seccionAprobada(tipo);
      if (check === true) return;
      if (check === null) {
        if (files.some(f => f.estadoRevision === 'APROBADO')) return;
        const lastReviewed = [...files].reverse().find(f => f.estadoRevision !== 'PENDIENTE');
        if (lastReviewed?.estadoRevision === 'RECHAZADO') {
          const label = lastReviewed.titulo || tipoDocLabels[tipo] || tipo;
          otrosProblemas.push({ tipo, label, estado: 'RECHAZADO', motivo: lastReviewed.observacion || undefined });
        }
        return;
      }
      for (const r of rechazadosDeTipo(files)) {
        otrosProblemas.push({
          tipo,
          label: r.titulo || tipoDocLabels[tipo] || tipo,
          estado: 'RECHAZADO',
          motivo: r.motivo,
        });
      }
    });
  }

  const totalProblemas = obligatoriosProblemas.length + dniProblemas.length + otrosProblemas.length;
  const textoObsUnificada = (() => {
    const lineasObl = obligatoriosProblemas.map((p, i) => {
      if (p.estado === 'FALTANTE') {
        return `${i + 1}. ${p.label.toUpperCase()}: NO PRESENTADO — Se requiere la carga de este documento obligatorio.`;
      }
      const motivo = p.motivo || 'Sin observación registrada';
      return `${i + 1}. ${p.label.toUpperCase()}: RECHAZADO — ${motivo}`;
    });
    const lineasDni = dniProblemas.map((p, i) => {
      const motivo = p.motivo ? ` — ${p.motivo}` : '';
      return `${obligatoriosProblemas.length + i + 1}. ${p.label}: ${p.estado}${motivo}`;
    });
    const lineasOtros = otrosProblemas.map((p, i) => {
      const motivo = p.motivo || 'Sin observación registrada';
      return `${obligatoriosProblemas.length + dniProblemas.length + i + 1}. ${p.label.toUpperCase()}: RECHAZADO — ${motivo}`;
    });
    const cierre = '\nSe intima al partido a subsanar las observaciones precedentes dentro del plazo establecido, bajo apercibimiento de lo dispuesto por el artículo 18 de la Ley XII N° 23.';
    return [...lineasObl, ...lineasDni, ...lineasOtros].join('\n') + (totalProblemas > 0 ? cierre : '');
  })();

  // Observación automática para RECONOCIMIENTO en admisibilidad (atributos):
  // se arma con el DNI del iniciador y los símbolos rechazados. Misma lógica de
  // dedup que reempadronamiento: se agrupa por tipo y NO se observa un rechazo si
  // ya hay al menos un documento APROBADO de ese tipo (p.ej. el partido reenvió y
  // se verificó una versión nueva); si no hay ninguno verificado, se toma el
  // último revisado y sólo es problema si quedó RECHAZADO (ignora rechazos viejos
  // superados por una carga posterior).
  const esReconAdmis = esExpedienteRecon && etapaVista === 'ADMISIBILIDAD';
  const atributosProblemas = esReconAdmis
    ? (() => {
        const tiposAtributos = ['DNI_FRENTE_INICIADOR', 'DNI_DORSO_INICIADOR', 'SIMBOLO'];
        const problemas: Documento[] = [];
        tiposAtributos.forEach(tipo => {
          const files = docsRaw
            .filter(d => !d.virtual && d.tipo === tipo)
            .sort((a, b) => new Date(a.creadoEn).getTime() - new Date(b.creadoEn).getTime());
          if (files.length === 0) return;
          if (files.some(f => f.estadoRevision === 'APROBADO')) return;
          const lastReviewed = [...files].reverse().find(f => f.estadoRevision !== 'PENDIENTE');
          // No se lista el rechazo en bloque por resolución del TEP (no es una
          // objeción al símbolo puntual): ensuciaría la intimación automática.
          if (lastReviewed?.estadoRevision === 'RECHAZADO' && lastReviewed.observacion !== MOTIVO_RECHAZO_TEP_SIMBOLO) {
            problemas.push(lastReviewed);
          }
        });
        return problemas;
      })()
    : [];
  const textoObsAtributos = atributosProblemas.length === 0 ? '' : (() => {
    const label = (t: string) => t === 'DNI_FRENTE_INICIADOR' ? 'DNI del iniciador (frente)'
      : t === 'DNI_DORSO_INICIADOR' ? 'DNI del iniciador (dorso)'
      : 'Símbolo/emblema';
    const lineas = atributosProblemas.map((d, i) => {
      const motivo = d.observacion ? ` — ${d.observacion}` : ' — Sin observación registrada';
      return `${i + 1}. ${label(d.tipo).toUpperCase()}: RECHAZADO${motivo}`;
    });
    return lineas.join('\n') + '\nSe intima al partido a subsanar las observaciones precedentes dentro del plazo establecido, bajo apercibimiento de lo dispuesto por el artículo 18 de la Ley XII N° 23.';
  })();
  const obsProblemasCount = esReconAdmis ? atributosProblemas.length : totalProblemas;
  const obsTextoGenerado = esReconAdmis ? textoObsAtributos : textoObsUnificada;

  return (
    /* La agrupación dueña del expediente abierto también es "propia" para el botón de
       renombrar: en una alianza los documentos llevan su `partidoId`, no el del
       partido del apoderado integrante. Ver AgrupacionesConAccesoContext. */
    <AgrupacionesConAccesoContext.Provider value={agrupacionesConAcceso}>
    <div className="space-y-6">
      {/* Barra de retorno: sólo cuando se llegó desde el tablero del expediente.
          Vuelve a la MISMA fila del tablero, no al principio de la página. */}
      {abrirTipo && expedienteIdFromUrl && (
        <div className="flex flex-wrap items-center justify-between gap-3 -mt-2 px-4 py-2.5 bg-primary-50 border border-primary-200 rounded-lg">
          <Link
            to={`/expedientes/${expedienteIdFromUrl}?tab=docs#doc-${abrirTipo}`}
            className="text-sm font-medium text-primary-700 hover:text-primary-800 hover:underline inline-flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 16 16" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10 3.5 5.5 8l4.5 4.5" />
            </svg>
            Volver al tablero del expediente
          </Link>
          <span className="text-xs text-primary-700">
            Abierto: {tipoDocLabels[abrirTipo] || abrirTipo}
          </span>
        </div>
      )}

      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Documentos</h1>
          <p className="mt-1 text-sm text-gray-500">
            {isExterno ? 'Documentos de su partido' : 'Gestión de documentos del sistema'}
          </p>
        </div>
        {user?.rol !== 'MESA_ENTRADAS' && !(esExpedienteRecon && isExterno) && (
        <button
          onClick={() => {
            if (!isExterno && partidoFilter) {
              setUploadData(prev => ({ ...prev, partidoId: partidoFilter }));
            }
            // Externo: si el filtro apunta a un expediente con obs pendiente, precargarlo
            // en el modal para que se vea bloqueado el upload
            if (isExterno && expedienteFilter) {
              setUploadData(prev => ({ ...prev, expedienteId: expedienteFilter }));
            }
            setShowUploadModal(true);
          }}
          disabled={isExterno && expFiltroTieneObsPendiente}
          title={
            isExterno && expFiltroTieneObsPendiente
              ? 'El expediente seleccionado tiene observaciones pendientes — respondelas antes de subir documentos'
              : undefined
          }
          className={`mt-4 sm:mt-0 px-4 py-2 rounded-lg text-sm font-medium ${
            isExterno && expFiltroTieneObsPendiente
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-blue-600 hover:bg-blue-700 text-white'
          }`}
        >
          + Subir Documento
        </button>
        )}
      </div>

      {/* Pestañas (solo internos): Resumen de todos los partidos / Documentos del partido */}
      {!isExterno && (
        <div className="border-b border-gray-200">
          <nav className="flex gap-6">
            <button
              onClick={() => setVista('resumen')}
              className={`py-3 text-sm font-medium border-b-2 transition ${vista === 'resumen' ? 'text-blue-600 border-blue-600 font-semibold' : 'text-gray-500 border-transparent hover:text-gray-700'}`}
            >
              Resumen de Documentación
            </button>
            <button
              onClick={() => setVista('detalle')}
              className={`py-3 text-sm font-medium border-b-2 transition ${vista === 'detalle' ? 'text-blue-600 border-blue-600 font-semibold' : 'text-gray-500 border-transparent hover:text-gray-700'}`}
            >
              Documentos
            </button>
          </nav>
        </div>
      )}

      {!isExterno && vista === 'resumen' && (
        <ResumenDocumentacionTab
          onSelect={(expedienteId, partidoId) => {
            setPartidoFilter(partidoId);
            setExpedienteFilter(expedienteId);
            setVista('detalle');
          }}
        />
      )}

      {(isExterno || vista === 'detalle') && (
      <>
      {/* Filtros */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-wrap gap-3 items-end">
          {!isExterno && (
            <div>
              <label className="block text-xs text-gray-500 mb-1">Partido</label>
              <select
                value={partidoFilter}
                onChange={(e) => {
                  setPartidoFilter(e.target.value);
                  setExpedienteFilter(''); // si cambia el partido, limpio el filtro de expediente
                }}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm min-w-[200px]"
              >
                <option value="">Todos los partidos</option>
                {partidos?.map((p: any) => (
                  <option key={p.id} value={p.id}>
                    {etiquetaPartidoFiltro(p)}
                  </option>
                ))}
              </select>
            </div>
          )}
          {!!partidoIdParaExpedientes && (
            <div>
              <label className="block text-xs text-gray-500 mb-1">Expediente</label>
              <select
                value={expedienteFilter}
                onChange={(e) => setExpedienteFilter(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm min-w-[340px]"
              >
                <option value="">Todos los expedientes</option>
                {expedientesList?.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.numero} — {tipoTramiteLabel(e.tipo)}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="block text-xs text-gray-500 mb-1">Desde</label>
            <input
              type="date"
              value={fechaDesde}
              onChange={(e) => setFechaDesde(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Hasta</label>
            <input
              type="date"
              value={fechaHasta}
              onChange={(e) => setFechaHasta(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>
          {(partidoFilter || expedienteFilter || fechaDesde || fechaHasta) && (
            <button
              onClick={() => { setPartidoFilter(''); setExpedienteFilter(''); setFechaDesde(''); setFechaHasta(''); }}
              className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 underline"
            >
              Limpiar filtros
            </button>
          )}
        </div>
        {expedienteFilter && expedientesList && (
          <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded text-xs text-blue-900">
            <span>
              Filtrando por expediente{' '}
              {(() => {
                const exp = expedientesList.find((e) => e.id === expedienteFilter);
                return <strong>{exp ? `${exp.numero} — ${tipoTramiteLabel(exp.tipo)}` : expedienteFilter}</strong>;
              })()}
            </span>
            <button
              onClick={() => setExpedienteFilter('')}
              className="text-blue-700 hover:text-blue-900 font-bold"
              title="Quitar filtro"
            >
              ✕
            </button>
          </div>
        )}
      </div>

      {/* Banner: observaciones pendientes del partido (externo) */}
      {isExterno && obsPendientesExterno.length > 0 && (
        <div className="mb-4 bg-orange-50 border-2 border-orange-300 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div className="flex-1 min-w-0">
              <h2 className="text-sm font-semibold text-orange-900">
                Tenés {obsPendientesExterno.length} comunicaci{obsPendientesExterno.length === 1 ? 'ón' : 'ones'} pendiente{obsPendientesExterno.length === 1 ? '' : 's'} de respuesta
              </h2>
              <div className="mt-2 space-y-1.5">
                {obsPendientesExterno.map(obs => (
                  <div key={obs.id} className="flex items-center gap-2 text-xs">
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${
                      obs.tipo === 'INTIMACION' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {obs.tipo === 'INTIMACION' ? 'Intimación' : 'Observación'}
                    </span>
                    <span className="text-gray-700">
                      Expediente <strong>{obs.expediente.numero} — {tipoTramiteLabel(obs.expediente.tipo)}</strong>
                      {obs.fechaLimite && (
                        <> · vence {new Date(obs.fechaLimite).toLocaleDateString('es-AR')}</>
                      )}
                    </span>
                    <Link
                      to={`/expedientes/${obs.expediente.id}?tab=obs&focus=primera`}
                      className="inline-flex items-center gap-1 text-base text-orange-700 hover:text-orange-900 underline font-semibold"
                    >
                      Responder
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Banner: revisión de respuesta a observación (supervisor desde expediente) */}
      {revisarObsId && obsRevisar && (
        <div className="mb-4 bg-orange-50 border-2 border-orange-300 rounded-lg p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <svg className="w-5 h-5 text-orange-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                <h2 className="text-sm font-semibold text-orange-900">
                  Revisando adjuntos a la respuesta de {obsRevisar.tipo === 'INTIMACION' ? 'la intimación' : 'la observación'}
                </h2>
              </div>
              <p className="text-xs text-orange-800 ml-7">
                Expediente <strong>{obsRevisar.expediente?.numero}{obsRevisar.expediente?.tipo ? ` — ${tipoTramiteLabel(obsRevisar.expediente.tipo)}` : ''}</strong>
                {obsRevisar.respuestaFecha && (
                  <> · Respuesta del partido el <strong>{new Date(obsRevisar.respuestaFecha).toLocaleDateString('es-AR')}</strong></>
                )}
              </p>
              <div className="mt-2 ml-7 flex flex-wrap gap-3 text-xs">
                <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-white border border-green-200 rounded">
                  <span className="w-2 h-2 rounded-full bg-green-500" />
                  <strong className="text-green-700">{markedAprobados}</strong> aprobado{markedAprobados === 1 ? '' : 's'}
                </span>
                <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-white border border-amber-200 rounded">
                  <span className="w-2 h-2 rounded-full bg-amber-500" />
                  <strong className="text-amber-700">{markedPendientes.length}</strong> pendiente{markedPendientes.length === 1 ? '' : 's'}
                </span>
                <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-white border border-red-200 rounded">
                  <span className="w-2 h-2 rounded-full bg-red-500" />
                  <strong className="text-red-700">{markedRechazados}</strong> rechazado{markedRechazados === 1 ? '' : 's'}
                </span>
              </div>
              {markedDocs.length === 0 && (
                <p className="mt-2 ml-7 text-xs text-orange-700 italic">
                  No se encontraron documentos adjuntos a esta respuesta. ¿Quizá fueron eliminados?
                </p>
              )}
            </div>
            <Link
              to={`/expedientes/${obsRevisar.expediente?.id}?tab=obs`}
              className="inline-flex items-center gap-1 px-3 py-1.5 bg-white border border-orange-300 text-orange-800 hover:bg-orange-100 text-xs font-medium rounded-md flex-shrink-0"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Volver al expediente
            </Link>
          </div>
        </div>
      )}

      {/* Secciones */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      ) : revisarObsId ? (
        /* Modo "revisar respuesta": solo los documentos que adjuntó el apoderado. */
        <SeccionLista
          etapa={dimensionRevision}
          titulo="Documentos adjuntados a la respuesta"
          docs={markedDocs}
          canEdit={canRevisar}
          isInterno={!isExterno}
          onSetEstado={handleSetEstadoTabla}
        />
      ) : showChecklist ? (
        <div className="space-y-3">
          {/* Trámite todavía en la bandeja: los checks vienen apagados (canRevisar es
              false) y sin este cartel el revisor no tiene forma de saber por qué. */}
          {canRevisarPorRol && !tramiteEstaIniciado && (
            <div className="border border-amber-200 rounded-lg bg-amber-50 px-4 py-3">
              <p className="text-sm text-amber-800">{AVISO_TRAMITE_SIN_INICIAR}</p>
            </div>
          )}
          {/* Selector de etapa de verificación: misma vista para internos y para el
              partido (el partido no verifica, pero ve el mismo corte por etapa). */}
          <div className="bg-white border border-gray-200 rounded-lg px-4 py-3 flex flex-wrap items-center gap-3">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Etapa de verificación</span>
            <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden">
              {/* Candidaturas no tiene etapa de admisibilidad: pasa directo de
                  PRESENTADO a EN_ANALISIS, así que esta pestaña no aplica. */}
              {!esExpedienteCandidatura && (
              <button
                type="button"
                onClick={() => cambiarEtapaVista('ADMISIBILIDAD')}
                className={`px-3 py-1.5 text-sm font-medium ${etapaVista === 'ADMISIBILIDAD' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
              >
                {esExpedienteRecon ? 'Admisibilidad (atributos)' : 'Admisibilidad'}
              </button>
              )}
              <button
                type="button"
                onClick={() => cambiarEtapaVista('ANALISIS')}
                disabled={etapaActualExp === 'ADMISIBILIDAD'}
                title={etapaActualExp === 'ADMISIBILIDAD' ? 'El expediente aún está en la etapa de admisibilidad' : undefined}
                className={`px-3 py-1.5 text-sm font-medium ${etapaVista === 'ANALISIS' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'} disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white`}
              >
                {esExpedienteRecon ? 'En análisis (documentación)' : 'Análisis'}
              </button>
            </div>

            {/* Al costado derecho del selector: es la acción de LA ETAPA que se está
                viendo, no de un tipo de documento puntual, así que va con el
                selector y no dentro del checklist.
                Sólo en Admisibilidad. Análisis no tiene un equivalente en bloque a
                propósito: ahí se decide el fondo de cada documento, y aprobar todo
                de un saque sería firmar un juicio que nadie hizo. */}
            {canRevisar && etapaVista === 'ADMISIBILIDAD' && !!expedienteFilter && (
              <button
                type="button"
                onClick={() => {
                  if (!window.confirm(
                    'Se van a aprobar en ADMISIBILIDAD todos los archivos y todos los checks de tipo de documento que estén sin revisar.\n\n'
                    + 'No se toca lo que ya está aprobado ni lo rechazado, no se toca la etapa de análisis y no cambia la etapa del expediente.\n\n'
                    + '¿Continuar?',
                  )) return;
                  aprobarTodoAdmisibilidadMutation.mutate();
                }}
                disabled={aprobarTodoAdmisibilidadMutation.isPending}
                title="Aprueba archivos y checks de tipo en la dimensión de admisibilidad. No cambia la etapa del expediente."
                className="px-3 py-1.5 text-sm font-medium rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {aprobarTodoAdmisibilidadMutation.isPending ? 'Aprobando…' : 'Aprobar todo Admisibilidad'}
              </button>
            )}
            {/* Descarga masiva del expediente. No es una acción de revisión —no toca
                ningún estado— pero vive en esta barra porque es del EXPEDIENTE
                entero, igual que el selector de etapa, y no de una sección. */}
            {!!expedienteFilter && (
              <button
                type="button"
                onClick={() => descargarZipMutation.mutate()}
                disabled={descargarZipMutation.isPending}
                title="Descarga un ZIP con todos los documentos del expediente, ordenados en carpetas por sección."
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" />
                </svg>
                {descargarZipMutation.isPending ? 'Preparando ZIP…' : 'Descargar todo'}
              </button>
            )}
            {etapaActualExp === 'ADMISIBILIDAD' && (
              <span className="text-xs text-gray-500">
                {esExpedienteRecon
                  ? 'El trámite está en atributos (admisibilidad); la documentación (análisis) se habilita al aprobarse los atributos.'
                  : 'El expediente está en admisibilidad: la etapa de análisis aún no está disponible.'}
              </span>
            )}
            {esExpedienteRecon && etapaActualExp !== 'ADMISIBILIDAD' && (
              <span className="text-xs text-gray-500">Admisibilidad (atributos) muestra los atributos; En análisis (documentación) muestra los documentos del Art. 18.</span>
            )}
        
          </div>
          {/* Resumen de documentos de la ETAPA que se está viendo (no del total) */}
          {(() => {
            const TIPOS_ATRIBUTOS_RECON = ['DNI_FRENTE_INICIADOR', 'DNI_DORSO_INICIADOR', 'SIMBOLO'];
            // Documentos que corresponden a la etapa actual:
            //  - RECONOCIMIENTO admisibilidad (atributos): DNI del iniciador + símbolos.
            //  - RECONOCIMIENTO análisis (documentación): el resto (obligatorios, DNIs, otros).
            //  - No reconocimiento: docsRaw ya viene filtrado por dimensión (esVisible).
            const docsEtapa = !esExpedienteRecon
              ? docsRaw
              : etapaVista === 'ADMISIBILIDAD'
                ? docsRaw.filter(d => TIPOS_ATRIBUTOS_RECON.includes(d.tipo))
                : docsRaw.filter(d => !TIPOS_ATRIBUTOS_RECON.includes(d.tipo));
            // "Faltantes": documentación obligatoria que todavía no se presentó.
            //
            // En ANÁLISIS se muestra SIEMPRE, para cualquier trámite. Antes en los que
            // no son reconocimiento sólo aparecía en admisibilidad, y quien analiza
            // —que es quien decide si el expediente se eleva— no tenía a la vista qué
            // faltaba: había que deducirlo recorriendo las secciones.
            //
            // En ADMISIBILIDAD se mantiene como estaba: en reconocimiento esa etapa
            // mira los atributos (símbolos, DNI del iniciador) y los obligatorios del
            // Art. 18 recién se presentan en la etapa de documentación, así que
            // contarlos ahí daría un número alarmante y falso.
            const mostrarFaltantes = etapaVista === 'ANALISIS' || !esExpedienteRecon;
            // En alianza la lista de tipos mezcla los del Art. 27 con los
            // complementarios (Art. 24), que NO bloquean la elevación. Contar un
            // complementario como faltante convertiría el número en algo que no se
            // puede usar para decidir.
            const tiposQueFaltan = tiposObligatorios.filter(t =>
              !docsRaw.some(d => d.tipo === t)
              && !(esExpedienteAlianza && esComplementarioAlianza(t)),
            );
            const resumenCards = [
              { label: 'Documentos', value: docsEtapa.filter(d => !d.virtual).length, color: 'text-gray-900' },
              { label: 'Verificados', value: docsEtapa.filter(d => d.estadoRevision === 'APROBADO').length, color: 'text-green-700' },
              { label: 'Pendientes', value: docsEtapa.filter(d => d.estadoRevision === 'PENDIENTE' && !d.virtual).length, color: 'text-amber-700' },
              { label: 'Rechazados', value: docsEtapa.filter(d => d.estadoRevision === 'RECHAZADO').length, color: 'text-red-700' },
              ...(mostrarFaltantes
                ? [{ label: 'Faltantes', value: tiposQueFaltan.length, color: 'text-orange-700' }]
                : []),
            ];
            return (
              <div className={`grid grid-cols-2 ${resumenCards.length === 5 ? 'sm:grid-cols-5' : 'sm:grid-cols-4'} gap-3`}>
                {resumenCards.map(({ label, value, color }) => (
                  <div key={label} className="bg-white border border-gray-200 rounded-lg px-4 py-3">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</p>
                    <p className={`text-2xl font-bold mt-1 ${color}`}>{value}</p>
                  </div>
                ))}
              </div>
            );
          })()}
          {/* Observación automática — según la etapa que se verifica:
              - RECONOCIMIENTO / admisibilidad (atributos): DNI del iniciador + símbolos rechazados.
              - Análisis (documentación) y no-reconocimiento: documentos obligatorios / DNI. */}
          {canRevisar && (
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Observación automática</h2>
                  {obsProblemasCount === 0 ? (
                    <p className="text-xs text-green-600 font-medium mt-1">{esReconAdmis ? 'Atributos sin observaciones.' : 'Documentación completa — no hay problemas.'}</p>
                  ) : esReconAdmis ? (
                    <p className="text-xs text-amber-600 mt-1">
                      {atributosProblemas.length} atributo{atributosProblemas.length === 1 ? '' : 's'} (DNI / símbolos) con problemas
                    </p>
                  ) : (
                    <p className="text-xs text-amber-600 mt-1">
                      {obligatoriosProblemas.length} obligatorio{obligatoriosProblemas.length === 1 ? '' : 's'} · {dniProblemas.length} DNI{dniProblemas.length === 1 ? '' : 's'} · {otrosProblemas.length} otro{otrosProblemas.length === 1 ? '' : 's'} con problemas
                    </p>
                  )}
                </div>
                <button
                  onClick={() => { setObsText(obsTextoGenerado); setShowObsPanel(true); }}
                  disabled={obsProblemasCount === 0}
                  className="text-sm px-4 py-2 rounded-lg font-medium bg-orange-600 hover:bg-orange-700 text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Generar observación {obsProblemasCount > 0 && `(${obsProblemasCount})`}
                </button>
              </div>
              {showObsPanel && (
                <div className="mt-4 border-t border-gray-200 pt-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-800">Observación generada automáticamente</h3>
                      <p className="text-xs text-gray-500 mt-0.5">Podés editar el texto antes de copiar. Incluye documentación obligatoria y DNIs.</p>
                    </div>
                    <button onClick={() => setShowObsPanel(false)} className="text-gray-400 hover:text-gray-600 text-lg leading-none ml-4">✕</button>
                  </div>
                  <textarea
                    value={obsText}
                    onChange={e => setObsText(sanitizarTextoLive(e.target.value))}
                    className="w-full h-56 border border-gray-200 rounded-lg px-3 py-2.5 text-sm resize-y focus:outline-none focus:border-orange-400 font-mono"
                  />
                  <div className="flex flex-wrap justify-end gap-2 mt-2">
                    <button
                      onClick={() => setShowObsPanel(false)}
                      className="text-sm px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50"
                    >
                      Cerrar
                    </button>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(obsText).then(() => {
                          setObsCopied(true);
                          setTimeout(() => setObsCopied(false), 2000);
                        });
                      }}
                      className="text-sm px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-medium transition-colors"
                    >
                      {obsCopied ? '✓ Copiado' : 'Copiar texto'}
                    </button>
                    {expedienteIdFromUrl && (
                      <Link
                        to={`/expedientes/${expedienteIdFromUrl}?tab=obs`}
                        className="text-sm px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
                      >
                        Ir al expediente →
                      </Link>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* RECONOCIMIENTO: Admisibilidad (atributos) muestra solo los atributos;
              En análisis (documentación) muestra los documentos del Art. 18. */}
          {/* RECONOCIMIENTO admisibilidad (atributos): mismo layout que reempadronamiento
              — apoderados promotores (filas nombre+DNI) + emblemas/símbolos + colores. */}
          {esExpedienteRecon && etapaVista === 'ADMISIBILIDAD' && (
            <>
              {agrupacionActivaId && (
                <SeccionDNIApoderados
                  expedienteId={expedienteFilter || undefined}
                  partidoId={agrupacionActivaId}
                  canEdit={canRevisar}
                  onSetEstado={handleSetEstadoTabla}
                  markedDocIds={markedDocIds}
                  etapaVista={dimensionRevision}
                  titulo="DNI del iniciador"
                  labelPersona="INICIADOR"
                  fuente="iniciador"
                  defaultOpen
                />
              )}
              <SeccionSimbolos
                etapa={dimensionRevision}
                seccion={(seccionesTipo ?? []).find((x: any) => x.tipo === 'SIMBOLO')}
                onAprobarTipo={(tipo, estado, nota) =>
                  aprobarTipoMutation.mutate({ tipo, estado, nota, dimension: dimensionRevision })}
                onGuardarNota={(tipo, nota) => guardarNotaTipoMutation.mutate({ tipo, nota })}
                expedienteId={expedienteFilter || undefined}
                simbolosDocs={docsRaw.filter(d => d.tipo === 'SIMBOLO' && !d.virtual)}
                colores={expedienteActivo?.datosFormulario?.atributos?.colores}
                descripcion={expedienteActivo?.datosFormulario?.atributos?.descripcionSimbolo}
                simbolosPaths={expedienteActivo?.datosFormulario?.atributos?.simbolosPaths}
                onSetEstado={handleSetEstadoDoc}
                canRevisar={canRevisar}
                defaultOpen
              />
              {/* RESOLUCIONES en la admisibilidad de reconocimiento: ya está en su
                  lugar, debajo del DNI del iniciador y arriba de "Otros documentos". */}
              {renderSeccionResoluciones(seccionResoluciones)}
              {/* Otros documentos (constancia del wizard, notas, etc.) también en
                  admisibilidad: en análisis se muestran más abajo. */}
              <SeccionAgrupadaPorTipo
                etapa={dimensionRevision}
                abrirTipo={abrirTipo}
                titulo="Otros Documentos"
                subtitulo="Documentación opcional agrupada por tipo"
                docs={seccionOtrosTabla}
                canEdit={canRevisar}
                isInterno={!isExterno}
                onSetEstado={handleSetEstadoTabla}
                markedDocIds={markedDocIds}
              />
            </>
          )}
          {(!esExpedienteRecon || etapaVista === 'ANALISIS') && (
            <SeccionObligatorios
              etapa={dimensionRevision}
              abrirTipo={abrirTipo}
              tiposObligatorios={tiposObligatorios}
              archivos={docsChecklist}
              canEdit={canRevisar}
              isInterno={!isExterno}
              onSetEstado={handleSetEstadoDoc}
              markedDocIds={markedDocIds}
              partidoId={agrupacionActivaId}
              /* ALIANZA: el estado de cada TIPO lo decide su check, no la cuenta de
                 archivos. Sin secciones estas props van vacías y no cambia nada.

                 La sección tiene LAS DOS dimensiones, igual que el documento, así
                 que el check aparece en las dos vistas y cada una escribe su propia
                 columna: la de Admisibilidad y la de Análisis son decisiones
                 distintas, no la misma repetida. */
              secciones={seccionesTipo}
              onAprobarTipo={(tipo, estado, nota) =>
                aprobarTipoMutation.mutate({ tipo, estado, nota, dimension: dimensionRevision })}
              onGuardarNota={(tipo, nota) => guardarNotaTipoMutation.mutate({ tipo, nota })}
              onEximirTipo={(tipo, obligatorio) => eximirTipoMutation.mutate({ tipo, obligatorio })}
              puedeEximir={['ADMINISTRADOR', 'SUPERVISOR', 'AUTORIDAD_FIRMANTE'].includes(user?.rol || '')}
              /* El partido NO sube obligatorios acá: solo por el wizard de
                 documentación o respondiendo una observación. Sin subida inline. */
            />
          )}
          {/* Emblemas y símbolos: JUSTO DEBAJO de los obligatorios. Es lo que la
              supervisora mira a continuación —son atributos de la agrupación, no
              documentación de terceros—, y estaba al final, después de los DNI. */}
          {!esExpedienteRecon && (
            <SeccionSimbolos
              etapa={dimensionRevision}
              seccion={(seccionesTipo ?? []).find((x: any) => x.tipo === 'SIMBOLO')}
              onAprobarTipo={(tipo, estado, nota) =>
                aprobarTipoMutation.mutate({ tipo, estado, nota, dimension: dimensionRevision })}
              onGuardarNota={(tipo, nota) => guardarNotaTipoMutation.mutate({ tipo, nota })}
              expedienteId={expedienteFilter || undefined}
              simbolosDocs={docsRaw.filter(d => d.tipo === 'SIMBOLO' && !d.virtual)}
              colores={expedienteActivo?.datosFormulario?.atributos?.colores}
              descripcion={expedienteActivo?.datosFormulario?.atributos?.descripcionSimbolo}
              simbolosPaths={expedienteActivo?.datosFormulario?.atributos?.simbolosPaths}
              onSetEstado={handleSetEstadoTabla}
              canRevisar={canRevisar}
              defaultOpen
            />
          )}
          {/* RECONOCIMIENTO análisis (documentación): obligatorios (arriba) +
              autoridades promotoras + apoderados promotores + otros (abajo).
              Los emblemas/símbolos y el DNI del iniciador son atributos: se
              revisan SOLO en admisibilidad, no acá. */}
          {esExpedienteRecon && etapaVista === 'ANALISIS' && (
            <>
              {agrupacionActivaId && (
                <SeccionDNIAutoridades
                  expedienteId={expedienteFilter || undefined}
                  partidoId={agrupacionActivaId || undefined}
                  canEdit={canRevisar}
                  onSetEstado={handleSetEstadoTabla}
                  markedDocIds={markedDocIds}
                  etapaVista={dimensionRevision}
                  titulo="Documentación de autoridades promotoras"
                  defaultOpen
                />
              )}
              {agrupacionActivaId && (
                <SeccionDNIApoderados
                  expedienteId={expedienteFilter || undefined}
                  partidoId={agrupacionActivaId}
                  canEdit={canRevisar}
                  onSetEstado={handleSetEstadoTabla}
                  markedDocIds={markedDocIds}
                  etapaVista={dimensionRevision}
                  titulo="Documentación de apoderados promotores"
                  labelPersona="APODERADO PROMOTOR"
                  defaultOpen
                />
              )}
            </>
          )}
          {/* Autoridades y apoderados (reempadronamiento y otros trámites; no en candidaturas, que no tienen ni autoridades ni apoderados propios que revisar acá). */}
          {!esExpedienteRecon && !esExpedienteCandidatura && (
            <>
              <SeccionDNIAutoridades
                expedienteId={expedienteFilter || undefined}
                partidoId={agrupacionActivaId || undefined}
                canEdit={canRevisar}
                onSetEstado={handleSetEstadoTabla}
                markedDocIds={markedDocIds}
                etapaVista={etapaVista}
                filasSeccion={filasDeGrupo('GRUPO_DNI_AUTORIDADES')}
                notaSeccion={notaDeGrupo('GRUPO_DNI_AUTORIDADES')}
                onAprobarSeccion={(estado) => aprobarTipoMutation.mutate({
                  tipo: 'GRUPO_DNI_AUTORIDADES', estado, dimension: dimensionRevision,
                })}
                onGuardarNotaSeccion={(nota) => guardarNotaTipoMutation.mutate({ tipo: 'GRUPO_DNI_AUTORIDADES', nota })}
              />
              {agrupacionActivaId ? (
                <SeccionDNIApoderados
                  expedienteId={expedienteFilter || undefined}
                  partidoId={agrupacionActivaId}
                  canEdit={canRevisar}
                  onSetEstado={handleSetEstadoTabla}
                  markedDocIds={markedDocIds}
                  etapaVista={etapaVista}
                  filasSeccion={filasDeGrupo('GRUPO_DNI_APODERADOS')}
                  notaSeccion={notaDeGrupo('GRUPO_DNI_APODERADOS')}
                  onAprobarSeccion={(estado) => aprobarTipoMutation.mutate({
                    tipo: 'GRUPO_DNI_APODERADOS', estado, dimension: dimensionRevision,
                  })}
                  onGuardarNotaSeccion={(nota) => guardarNotaTipoMutation.mutate({ tipo: 'GRUPO_DNI_APODERADOS', nota })}
                />
              ) : (
                <SeccionLista
                  etapa={dimensionRevision}
                  titulo="Documentación de Apoderados"
                  docs={seccionApoderados}
                  canEdit={canRevisar}
                  isInterno={!isExterno}
                  onSetEstado={handleSetEstadoTabla}
                  markedDocIds={markedDocIds}
                />
              )}
            </>
          )}
          {/* RESOLUCIONES, debajo de la documentación de apoderados y arriba de
              "Otros documentos": es el cierre del trámite y se lee al final, después
              de la documentación que se presentó.
              Antes iba arriba, pegada a los obligatorios, y con dos renders — uno
              para el análisis de reconocimiento y otro para el resto. Es el mismo
              contenido en los dos casos, así que va uno solo.
              Se llamaba "Documentación TEP": en la constitución de alianzas resuelve
              el Secretario Electoral y el Tribunal no interviene. */}
          {(!esExpedienteRecon || etapaVista === 'ANALISIS') && renderSeccionResoluciones(seccionResoluciones)}
          {(!esExpedienteRecon || etapaVista === 'ANALISIS') && (
            <SeccionAgrupadaPorTipo
              etapa={dimensionRevision}
              abrirTipo={abrirTipo}
              titulo="Otros Documentos"
              subtitulo="Documentación opcional agrupada por tipo"
              docs={seccionOtrosTabla}
              canEdit={canRevisar}
              isInterno={!isExterno}
              onSetEstado={handleSetEstadoTabla}
              markedDocIds={markedDocIds}
            />
          )}
        </div>
      ) : (
        <div className="space-y-3">
          <SeccionLista
            etapa={dimensionRevision}
            titulo="Documentación del Partido"
            docs={seccionPartidoSinChecklist}
            canEdit={canRevisar}
            isInterno={!isExterno}
            onSetEstado={handleSetEstadoTabla}
            markedDocIds={markedDocIds}
          />
          {(expedienteFilter || agrupacionActivaId) ? (
            <SeccionDNIAutoridades
              expedienteId={expedienteFilter || undefined}
              partidoId={agrupacionActivaId || undefined}
              canEdit={canRevisar}
              onSetEstado={handleSetEstadoTabla}
              markedDocIds={markedDocIds}
              etapaVista={etapaVista}
            />
          ) : (
            <SeccionLista
              etapa={dimensionRevision}
              titulo="Documentación de Autoridades"
              docs={seccionAutoridadesSinChecklist}
              canEdit={canRevisar}
              isInterno={!isExterno}
              onSetEstado={handleSetEstadoTabla}
              markedDocIds={markedDocIds}
            />
          )}
          {agrupacionActivaId ? (
            <SeccionDNIApoderados
              expedienteId={expedienteFilter || undefined}
              partidoId={agrupacionActivaId}
              canEdit={canRevisar}
              onSetEstado={handleSetEstadoTabla}
              markedDocIds={markedDocIds}
              etapaVista={etapaVista}
            />
          ) : (
            <SeccionLista
              etapa={dimensionRevision}
              titulo="Documentación de Apoderados"
              docs={seccionApoderadosSinChecklist}
              canEdit={canRevisar}
              isInterno={!isExterno}
              onSetEstado={handleSetEstadoTabla}
              markedDocIds={markedDocIds}
            />
          )}
          {/* RESOLUCIONES: debajo de apoderados, igual que en la vista con checklist. */}
          {renderSeccionResoluciones(seccionResolucionesSinChecklist)}
          <SeccionAgrupadaPorTipo
            etapa={dimensionRevision}
            abrirTipo={abrirTipo}
            titulo="Otros Documentos"
            subtitulo="Documentación opcional agrupada por tipo"
            docs={seccionOtrosSinChecklist}
            canEdit={canRevisar}
            isInterno={!isExterno}
            onSetEstado={handleSetEstadoTabla}
            markedDocIds={markedDocIds}
          />
        </div>
      )}

      {/* Modal de Upload */}
      <Modal
        isOpen={showUploadModal}
        onClose={() => { setShowUploadModal(false); resetUploadForm(); }}
        title="Subir Documento"
        size="lg"
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {!isExterno && (
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">Agrupación</label>
                {/* Buscable y por DENOMINACIÓN: el <select> nativo mostraba la sigla,
                    así que había que saberse las siglas de memoria. Incluye alianzas
                    y confederaciones. */}
                <AgrupacionSelect
                  value={uploadData.partidoId}
                  onChange={(id) => setUploadData({ ...uploadData, partidoId: id, expedienteId: '' })}
                  opciones={partidos ?? []}
                  labelVacio="Sin agrupación asociada"
                />
              </div>
            )}

            <div className="col-span-2">
              <label className="block text-sm font-medium mb-1">
                Expediente {isExterno && <span className="text-red-600">*</span>}
              </label>
              <select
                value={uploadData.expedienteId}
                onChange={(e) => setUploadData({ ...uploadData, expedienteId: e.target.value })}
                className={`w-full rounded-lg px-3 py-2 text-sm ${
                  isExterno && !uploadData.expedienteId && (expedientes?.length ?? 0) > 0
                    ? 'border-2 border-amber-400 bg-amber-50'
                    : 'border'
                }`}
                disabled={!partidoParaExpedientes}
              >
                {isExterno && (expedientes?.length ?? 0) > 0
                  ? <option value="" disabled>Elegí un expediente…</option>
                  : <option value="">Sin expediente asociado</option>}
                {expedientes?.map((exp: any) => (
                  <option key={exp.id} value={exp.id}>
                    {exp.numero} — {tipoTramiteLabel(exp.tipo)}
                    {isExterno && obsPendientesPorExpediente[exp.id]?.length ? ' ⚠ con observación pendiente' : ''}
                  </option>
                ))}
              </select>
              {!partidoParaExpedientes && (
                <p className="text-xs text-gray-400 mt-1">Seleccioná un partido primero</p>
              )}
              {isExterno && expModalTieneObsPendiente && (
                <div className="mt-2 p-2 bg-red-50 border border-red-300 rounded text-xs text-red-800">
                  <p className="font-semibold">⚠ Este expediente tiene una observación/intimación pendiente de respuesta.</p>
                  <p className="mt-1">Tenés que responderla desde el expediente antes de subir documentos.</p>
                  <Link
                    to={`/expedientes/${uploadData.expedienteId}?tab=obs&focus=primera`}
                    className="inline-flex items-center gap-1 mt-1 text-base text-red-700 hover:text-red-900 underline font-semibold"
                    onClick={() => setShowUploadModal(false)}
                  >
                    Ir a responder
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Tipo de Documento *</label>
              {tipoBloqueado ? (
                <div className="w-full border rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-700">
                  {tipoDocLabels[uploadData.tipo] || uploadData.tipo}
                </div>
              ) : faltaElegirExpedienteModal ? (
                <div className="w-full border rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-400">
                  Seleccioná primero un expediente
                </div>
              ) : (
                <TipoDocumentoSelect
                  value={uploadData.tipo}
                  tiposExtra={esReconModal ? DOCS_OBLIGATORIOS_RECONOCIMIENTO : undefined}
                  onChange={(nuevoTipo) => {
                    setUploadData({ ...uploadData, tipo: nuevoTipo, dniPersona: '' });
                    setDniCheckResult(null);
                    setDniReemplazar(false);
                  }}
                />
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                Título
                {TIPOS_DNI_PERSONA.includes(uploadData.tipo) && (
                  <span className="ml-1 text-xs text-gray-400 font-normal">(se genera automáticamente)</span>
                )}
              </label>
              <input
                type="text"
                value={uploadData.titulo}
                onChange={(e) => !TIPOS_DNI_PERSONA.includes(uploadData.tipo) && setUploadData({ ...uploadData, titulo: e.target.value })}
                readOnly={TIPOS_DNI_PERSONA.includes(uploadData.tipo)}
                placeholder={TIPOS_DNI_PERSONA.includes(uploadData.tipo) ? 'Se genera al ingresar el DNI' : 'Se usará el nombre del archivo si está vacío'}
                className={`w-full border rounded-lg px-3 py-2 text-sm ${TIPOS_DNI_PERSONA.includes(uploadData.tipo) ? 'bg-gray-50 text-gray-600 cursor-default' : ''}`}
              />
            </div>

            {TIPOS_DNI_PERSONA.includes(uploadData.tipo) && (
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">DNI de la persona *</label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={uploadData.dniPersona}
                    onChange={(e) => {
                      setUploadData({ ...uploadData, dniPersona: e.target.value, titulo: '' });
                      setDniCheckResult(null);
                      setDniReemplazar(false);
                    }}
                    onBlur={() => checkDniPersona(uploadData.dniPersona, uploadData.tipo)}
                    placeholder="Número de DNI"
                    className="border rounded-lg px-3 py-2 text-sm w-40"
                  />
                  {dniCheckLoading && <span className="text-xs text-gray-400">Buscando...</span>}
                </div>
              </div>
            )}

            <div className="col-span-2">
              <label className="block text-sm font-medium mb-1">Descripción</label>
              <textarea
                value={uploadData.descripcion}
                onChange={(e) => setUploadData({ ...uploadData, descripcion: e.target.value })}
                rows={2}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              />
            </div>
          </div>

          {(() => {
            const esTipoDni = TIPOS_DNI_PERSONA.includes(uploadData.tipo);
            if (esTipoDni) {
              const lado = uploadData.tipo.includes('FRENTE') ? 'frente' : 'dorso';
              const existente = dniCheckResult?.[lado];
              if (existente && !dniReemplazar) {
                const handleUsarExistente = () => {
                  const cleanDni = uploadData.dniPersona.replace(/\D/g, '');
                  vincularMutation.mutate({
                    tipo: uploadData.tipo,
                    titulo: uploadData.titulo || existente.titulo,
                    dniPersona: cleanDni,
                    partidoId: uploadData.partidoId || undefined,
                    expedienteId: uploadData.expedienteId || undefined,
                    rutaArchivo: existente.path,
                  });
                };
                return (
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-lg px-4 py-3">
                      <svg className="w-5 h-5 text-green-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-green-800">DNI aprobado encontrado</p>
                        <p className="text-xs text-green-600 truncate">{existente.titulo}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => window.open(existente.url, '_blank')}
                        className="text-xs text-blue-600 hover:underline flex-shrink-0"
                      >
                        Ver
                      </button>
                      <button
                        type="button"
                        onClick={() => setDniReemplazar(true)}
                        className="text-xs border border-gray-300 rounded px-2 py-1 hover:bg-gray-50 flex-shrink-0"
                      >
                        Reemplazar
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={handleUsarExistente}
                      disabled={vincularMutation.isPending}
                      className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium disabled:opacity-50"
                    >
                      {vincularMutation.isPending ? 'Vinculando...' : 'Usar este documento'}
                    </button>
                  </div>
                );
              }
            }
            const faltaExpediente = isExterno && !uploadData.expedienteId && (expedientes?.length ?? 0) > 0;
            const faltaTipo = !uploadData.tipo;
            const bloqueado = faltaTipo || faltaExpediente;
            const soloExcel = TIPOS_SOLO_EXCEL.includes(uploadData.tipo);
            return (
              <FileUpload
                onUpload={handleUpload}
                maxSize={25}
                multiple={false}
                disabled={bloqueado}
                accept={soloExcel ? ACCEPT_EXCEL_ODS : '.pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png'}
                label="Seleccionar archivo"
                description={
                  faltaExpediente
                    ? 'Elegí primero el expediente al que corresponde este documento.'
                    : faltaTipo
                    ? 'Elegí primero el tipo de documento.'
                    : soloExcel
                    ? 'Solo Excel (.xlsx/.xls) u ODS. Máximo 25MB.'
                    : 'PDF, Word, Excel o imágenes. Máximo 25MB.'
                }
              />
            );
          })()}

          {uploadMutation.isPending && (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto" />
              <p className="mt-2 text-sm text-gray-500">Subiendo documento...</p>
            </div>
          )}
        </div>
      </Modal>
      </>
      )}

      {/* Verificar el Listado de Adherentes dispara el control contra el padrón
          provincial (485k registros): tarda. Se tapa la pantalla con un velo muy
          suave para que se entienda que está procesando y que no se puede hacer
          otra cosa hasta que el backend responda. El velo intercepta los clics; los
          atajos de teclado se cortan en handleSetEstadoDoc/handleSetEstadoTabla. */}
      {verificarAdherentesMutation.isPending && (
        <div
          role="status"
          aria-live="polite"
          aria-busy="true"
          className="fixed inset-0 z-[100] flex items-center justify-center bg-gray-900/10 cursor-wait"
        >
          <div className="flex items-center gap-3 rounded-full border border-gray-200 bg-white px-5 py-3 shadow-lg">
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-200 border-t-blue-600" />
            <span className="text-sm font-medium text-gray-700">
              Verificando el listado de adherentes…
            </span>
          </div>
        </div>
      )}
    </div>
    </AgrupacionesConAccesoContext.Provider>
  );
}
